import { DatePipe } from '@angular/common';
import { Injectable, isDevMode } from '@angular/core';
import { AbstractControl, UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators, FormGroup, ValidatorFn, ValidationErrors } from '@angular/forms';
import { Params } from '@angular/router';
import { environment } from '@env/environment';
import { DisbursalEvent, FinanceStates, FullFinance, SettlementEvent, UserDisbursalEvent } from 'app/routes/apply/apply-models';
import { CommonUtilsService } from 'app/routes/credit-limit/common/common-utils.service';
import { CreditLimitStateType, MaxFinancingTenureFromEnum } from 'app/routes/credit-limit/credit-limit.model';
import { FinanceStateType, Notifications } from 'app/routes/dashboard/dashboard.models';
import { AuthService } from 'app/routes/sessions/login/auth.service';
import { FinanceTypeDetails, SellerOrBuyerOrFinanceInst, User } from 'app/routes/sessions/login/user.model';
import { getDateDiff, getUTCDate, setUTCHours } from 'app/shared/utils/utils';
import { Subject } from 'rxjs';
import { ConstantsService, getStatusTagTextClass } from './constants.service';
import { GoogleAnalyticalService } from './google-analytics.service';
import { CollectedAt, FinanceTypeInfoInterface, InterestRateConfigurableType, ProductConfigService } from './product-config.service';
import { UiService } from './ui.service';
declare let XLSX: any;

// Class used to add config params to grouped columns to handle multi-level grouping, if required.
export class RowGroup {
  level = 0;
  parent: RowGroup;
  expanded = true;
  totalCounts = 0;
}

// The exact set of finance-ocr-data prediction labels the invoice form actually prefills - see
// UtilsService.applyOcrFieldMapToInvoiceGroup below. finance-ocr-data returns many more fields
// than this (bank details, buyer/seller info, commodity, etc.) purely for the invoice preview
// panel's highlight overlay to scope itself to, so it doesn't draw boxes for fields nothing on
// the form reads - see InvoicePreviewPanelService.setHighlightsForFile.
export const OCR_PREFILLED_FIELD_LABELS: readonly string[] = [
  'currency',
  'invoice_number',
  'payment_due_date',
  'invoice_date',
  'invoice_amount',
  'net_d'
];

// Same purpose as OCR_PREFILLED_FIELD_LABELS above, for a finance-ocr-data call made with
// fileType: 'BILL_OF_LADING' instead - see UtilsService.applyOcrFieldMapToBillOfLadingGroup.
export const OCR_BOL_PREFILLED_FIELD_LABELS: readonly string[] = [
  'mbl_no',
  'container_no',
  'carrier',
  'booking_no',
  'shipment_reference',
  'hbl_number'
];

@Injectable({
  providedIn: 'root',
})

/**
 * basic utility functions. Don't put any dependencies in this file besides models.
 */
export class UtilsService {
  stateCodeList: any = {};
  isMobileDevice = Boolean(window.screen.width <= 600);
  filteredCountry: any = [];
  filteredState: any = [];
  filteredCity: any = [];
  countriesData: any = [];
  statesData: any = [];
  citiesData: any = [];
  financialInstitutionSubject: Subject<string> = new Subject();
  ocrAnalysisRawData: any[] = [];
  creditLimitBreadcrumSubject: Subject<string> = new Subject();
  fiAssessedState = 'Financial Institution Assessed';
  public autoSaveIdleTimer;
  maxFinancingTenureFromOptions = [
    {
      key: 'DISBURSAL_DATE',
      label: MaxFinancingTenureFromEnum.DISBURSAL_DATE
    },
    {
      key: 'INVOICE_DATE',
      label: MaxFinancingTenureFromEnum.INVOICE_DATE
    },
    {
      key: 'BL_DATE',
      label: MaxFinancingTenureFromEnum.BL_DATE
    }
  ];
  timedOutCloser;
  prevMouseTrigger;
  masterCreditLimit;
  masterCreditLimitControl;

  constructor(private constants: ConstantsService,
    private googleAnalytics: GoogleAnalyticalService,
    private ui: UiService,
    public datepipe: DatePipe,
    private prodConfig: ProductConfigService,
    private commonUtilsService: CommonUtilsService,
  ) {
  }

  applyIndex = -1;

  /**
   * Used by tables on several views.
   * @param state - finance state
   */
  getFinanceStatusColor(state) {
    // used by tables to display the state color of finances.
    switch (state) {
      case FinanceStates.BCO_FINANCE_SAVED:
        return '#CCCC00';
      case FinanceStates.FINANCE_CANCELLED:
      case FinanceStates.REJECTED:
      case FinanceStates.REJECTED_BY_APPLICANT:
      case FinanceStates.REJECTED_BY_FI:
        return 'red';
      case FinanceStateType.SETTLED:
      case FinanceStates.DETAILS_REQUIRED:
      case FinanceStates.PARTIALLY_SETTLED:
      case FinanceStates.WAIVER_REQUEST:
      case FinanceStates.WAIVER_REQUEST_REJECTED:
      case FinanceStateType.ACCEPTED:
        return 'blue';
      case FinanceStates.IMPORTER_APPROVAL_PENDING:
      case FinanceStateType.PROCESSING:
      case FinanceStates.REWORK_PENDING_BY_MAKER:
        return 'orange';
      default:
        return 'green';
    }
  }

  // status color based on state
  getTransactionMonitoringStateColor(state: string) {
    if (state === null) {
      return 'black';
    }
    else if (state === 'Approved') {
      return 'green';
    }
    else if (state === 'Highlighted') {
      return 'red';
    }
  }

  getStatusTagClass(status: string): string {
    return getStatusTagTextClass(status);
  }

  /**
 * Used by credit limit/facility view.
 * @param status - credit limit request status
 */
  getCreditFacilityStatusColor(status) {
    // used by table to display the state color of credit limit request.
    switch (status?.toLowerCase()) {
      case CreditLimitStateType.COMPLETED.toLowerCase():
        return 'green';
      case CreditLimitStateType.REJECTED_BY_FI.toLowerCase():
      case CreditLimitStateType.REJECTED_BY_APPLICANT.toLowerCase():
      case CreditLimitStateType.REJECTED_BY_COUNTER_PARTY.toLowerCase():
      case CreditLimitStateType.EXPIRED.toLowerCase():
      case CreditLimitStateType.ELIGIBILITY_REJECTED_BY_FI.toLowerCase():
      case CreditLimitStateType.COMPANY_REJECTED_BY_FI.toLowerCase():
      case CreditLimitStateType.REJECTED.toLowerCase():
        return 'red';
      case CreditLimitStateType.FI_ASSESSED.toLowerCase():
      case CreditLimitStateType.LENDING_FI_ASSESSED?.toLowerCase():
      case CreditLimitStateType.IMPORTER_ACCEPTANCE_PENDING.toLowerCase():
      case CreditLimitStateType.DRAFT.toLowerCase():
      case CreditLimitStateType.SEND_REWORK.toLowerCase():
      case CreditLimitStateType.SIGNATURE_PENDING.toLowerCase():
      case CreditLimitStateType.REWORK_PENDING_BY_CREDIT_ANALYST.toLowerCase():
      case CreditLimitStateType.DOCUMENT_REWORK_PENDING_BY_CREDIT_ANALYST.toLowerCase():
        return 'orange';
      case CreditLimitStateType.UNDER_ASSESSMENT.toLowerCase():
      case CreditLimitStateType.DOCUMENT_PENDING.toLowerCase():
      case CreditLimitStateType.DETAILS_REQUIRED.toLowerCase():
        return 'blue';
      case undefined:
        return 'grey';
      default:
        return 'green';
    }
  }

  /**
   * Converts a Fina nceState to a user friendly string.
   * @param state -state of finance
   */
  getFinanceState(state, financeType) {
    // used by tables to display the state color of finances.
    switch (state) {
      case FinanceStateType.ACCEPTED:
        return 'Under Assessment';
      case FinanceStates.BCO_FINANCE_SAVED:
        return 'Draft';
      case FinanceStateType.SETTLED:
        return 'Application Settled';
      case FinanceStateType.APPROVED:
        return 'Terms Accepted';
      case FinanceStates.PARTIALLY_SETTLED:
        return 'Partially Settled';
      case FinanceStates.WAIVER_REQUEST:
        return 'Waiver Request';
      case FinanceStates.WAIVER_REQUEST_REJECTED:
        return 'Waiver Request Rejected';
      case FinanceStateType.PROCESSING:
        return 'Financial Institution Assessed';
      case FinanceStates.FINANCE_CANCELLED:
        return 'Cancelled';
      case FinanceStates.BANK_FUNDS_TRANSFERRED:
        return 'Funds Transferred';
      case FinanceStates.FUNDS_RETURNED_UPDATED_BY_MAKER:
        return 'Funds Returned Updated By Maker';
      case FinanceStates.FUNDS_RETURNED_UPDATED_BY_CHECKER:
        return 'Funds Returned Updated By Checker';
      case FinanceStates.FUNDS_RETURNED:
        return 'Funds Returned';
      case FinanceStates.ACCOUNT_SETTLED:
        return 'Payment Settled';
      case FinanceStates.DTF_FINANCE_CLOSED:
        return 'Application Closed';
      case FinanceStates.DETAILS_REQUIRED:
        return 'Details Required';
      case FinanceStates.IMPORTER_APPROVAL_PENDING:
        if (financeType === '1') {
          return 'Importer Approval Pending';
        } else {
          return 'Buyer Approval Pending';
        }
      case FinanceStates.APPROVED_BY_MAKER:
        return 'Approved By Maker';
      case FinanceStates.REJECT_IN_PROGRESS:
        return 'Reject In Progress';
      case FinanceStates.REWORK_PENDING_BY_MAKER:
        return 'Rework Pending By Maker';
      case FinanceStates.FINANCE_ASSESSMENT_REQUESTED_FOR_DEFAULTER:
        return 'Finance Assessment requested for defaulter';
      case FinanceStates.REJECTED_BY_APPLICANT:
        return 'Rejected By Applicant';
      case FinanceStates.REJECTED:
        return 'Rejected';
      case FinanceStates.OVERDUE_WAIVE_OFF_REQUESTED:
        return 'Overdue Waive Off Requested';
      case FinanceStates.REJECTED_BY_FI:
        return 'Rejected By Financial Institution';
      case FinanceStates.DUE_DATE_UPDATE_REQUESTED:
        return 'Due Date Update Requested';
      case FinanceStates.FUNDS_TRANSFER_UPDATED_BY_MAKER:
        return 'Funds Transfer Updated by Maker';
      case FinanceStates.FUNDS_TRANSFER_PENDING_REWORK:
        return 'Funds Transfer Pending Rework';
      case FinanceStates.PARTIAL_SETTLEMENT_PENDING_REWORK:
        return 'Partial Settlement Pending Rework';
      case FinanceStates.SETTLEMENT_UPDATED_BY_MAKER:
        return 'Settlement Updated by Maker';
      case FinanceStates.SETTLEMENT_PENDING_REWORK:
        return 'Settlement Pending Rework';
      case FinanceStates.BALANCE_SETTLEMENT_UPDATED_BY_MAKER:
        return 'Balance Settlement Updated by Maker';
      case FinanceStates.BALANCE_SETTLEMENT_PENDING_REWORK:
        return 'Balance Settlement Pending Rework';
      case FinanceStates.DTF_FINANCE_SETTLED:
        return 'DTF Finance Settled';
      case FinanceStates.INTEREST_SCHEDULE_UPDATE_REQUESTED:
        return 'Interest Schedule Updated by Maker';
      case FinanceStates.INTEREST_SCHEDULE_REWORK_PENDING:
        return 'Interest Schedule Rework pending with Maker';
      case FinanceStates.CM_REVIEW_PENDING_OM_CHECKER: return 'Credit Note Review Pending with Operations Checker';
      case FinanceStates.CM_REWORK_PENDING_OM_MAKER: return 'Credit Note Rework Pending with Operations Maker';
      case FinanceStates.CM_REVIEW_PENDING_FM: return 'Credit Note Review Pending with Finance Manager';
      case FinanceStates.CM_RESUBMISSION_PENDING_FM_CHECKER: return 'Credit Note Accounting Resubmission Pending with Finance Checker';
      case FinanceStates.CM_REWORK_PENDING_OM_CHECKER: return 'Credit Note Rework Pending with Operations Checker';
      case FinanceStates.CM_AWAIT_ACCOUNTING_APPROVE: return 'Credit Note Awaiting Accounting Approval';
      case FinanceStates.CM_REJECTED_BY_OM_CHECKER: return 'Credit Note Rejected by Operations Checker';
      case FinanceStates.CM_REJECTED_BY_FM: return 'Credit Note Rejected by Finance Manager';
      case FinanceStates.CM_ACCOUNTING_APPROVED: return 'Credit Note Accounting Approved';
      case FinanceStates.CONVERSION_REQUEST_PENDING_CLIENT_SUBMISSION:
        return 'Conversion Request Pending Client Submission';
      case FinanceStates.CONVERSION_REQUEST_PENDING_MAKER_REVIEW:
        return 'Conversion Request Pending Maker Review';
      case FinanceStates.CONVERSION_REQUEST_PENDING_CHECKER_REVIEW:
        return 'Conversion Request Pending Checker Review';
      case FinanceStates.CONVERSION_REQUEST_PENDING_CLIENT_RESUBMISSION:
        return 'Conversion Request Pending Client Resubmission';
      case FinanceStates.CONVERSION_REQUEST_PENDING_MAKER_RESUBMISSION:
        return 'Conversion Request Pending Maker Resubmission';
      case FinanceStates.CONVERSION_REQUEST_REJECTED_BY_MAKER:
        return 'Conversion Request Rejected by Maker';
      case FinanceStates.CONVERSION_REQUEST_REJECTED_BY_CHECKER:
        return 'Conversion Request Rejected by Checker';
      case FinanceStates.CONVERTED_TO_POST_SHIPMENT:
        return 'Converted To Post Shipment';
      case FinanceStates.POST_SHIPMENT_CONVERSION_IN_PROCESS:
        return 'Post Shipment Conversion In Process';
      default:
        return state;
    }
  }

  getBankBranchCode(country): string {
    const countryName = country && country.countryName ? country.countryName : country;
    if (countryName) {
      switch (countryName.toUpperCase()) {
        case 'UAE':
        case 'PAKISTAN':
        case 'UNITED KINGDOM':
          return 'IBAN';
        case 'UNITED ARAB EMIRATES':
          return 'IBAN';
        case 'USA':
          return 'Routing number/Fedwire';
        case 'UNITED STATES':
          return 'Routing number/Fedwire';
        case 'UNITED STATES OF AMERICA':
          return 'Routing number/Fedwire';
        case 'AUSTRALIA':
          return 'Branch Code';
        case 'INDIA':
          return 'IFSC Code';
        case 'CANADA':
          return 'Routing Transit Number';
        case undefined:
          return 'true'
        default:
          return 'true';
      }
    } else {
      return 'true';
    }
  }

  private readonly errorFieldMap: Record<string, string[]> = {
    [this.constants.cpBulkUploadErrorHandler.COUNTRY_MISSING]: ['country', 'state', 'city'],
    [this.constants.cpBulkUploadErrorHandler.COUNTRY_INVALID_COUNTRIES_OPERATE_IN]: ['countries_operate_in'],
    [this.constants.cpBulkUploadErrorHandler.COUNTRY_INVALID_PASSPORT_ISSUING_COUNTRY_CODE]: ['passportIssuingCountryCode'],
    [this.constants.cpBulkUploadErrorHandler.STATE_MISSING]: ['state', 'city'],
    [this.constants.cpBulkUploadErrorHandler.STATE_INVALID]: ['state', 'city'],
    [this.constants.cpBulkUploadErrorHandler.CITY_MISSING]: ['city'],
    [this.constants.cpBulkUploadErrorHandler.COMMODITY_INVALID]: ['commodities'],
    [this.constants.cpBulkUploadErrorHandler.CONTACT_NUMBER_INVALID]: ['contact_number'],
    [this.constants.cpBulkUploadErrorHandler.EMAIL_INVALID]: ['email'],
    [this.constants.cpBulkUploadErrorHandler.COUNTRY_CODE_INVALID]: ['country_code'],
    [this.constants.cpBulkUploadErrorHandler.INVALID_VALUE_COMPANY_BUSINESS_TYPE]: ['companyBusinessType'],
    [this.constants.cpBulkUploadErrorHandler.INVALID_VALUE_UAE_FREE_TRADE_ZONE]: ['uaeFreeTradeZoneRegistered'],
    [this.constants.cpBulkUploadErrorHandler.INVALID_VALUE_YEARS_BCO_DEALING]: ['years_bco_dealing_with_commodity'],
    [this.constants.cpBulkUploadErrorHandler.INVALID_VALUE_YEARS_OF_RELATIONSHIP]: ['years_of_relationship_with_bco'],
    [this.constants.cpBulkUploadErrorHandler.INVALID_VALUE_DEFAULT_IN_PAST_PAYMENTS]: ['default_in_past_payments'],
    [this.constants.cpBulkUploadErrorHandler.INVALID_VALUE_ANNUAL_TURNOVER]: ['annual_turnover'],
  };

  getBranchCodeMaxConstraint(country): string {
    const countryName = country && country.countryName ? country.countryName : country;
    if (countryName) {
      switch (countryName.toUpperCase()) {
        case 'UAE':
          return '34';
        case 'UNITED ARAB EMIRATES':
          return '34';
        case 'INDIA':
          return '11';
        default:
          return '20';
      }
    } else {
      return '20';
    }
  }

  // transaction state
  getTransactionMonitoringState(state: string) {
    if (state === null) {
      return 'Pending'
    }
    else {
      return state;
    }
  }

  getDueEvent(eventType: string): string {
    switch (eventType) {
      case 'balance_payment':
        return 'Settlement by FI: BALANCE_PAYMENT';
      case 'balance_payment_maker':
        return 'Settlement by FI: BALANCE_PAYMENT_MAKER';
      case 'account_settled':
        return 'Settlement: ACCOUNT_SETTLED';
      case 'account_settled_maker':
        return 'Settlement: ACCOUNT_SETTLED_MAKER';
      case 'partially_settled':
        return 'Settlement: PARTIALLY_SETTLED';
      case 'partially_settled_maker':
        return 'Settlement: PARTIALLY_SETTLED_MAKER';
      case 'settlement_adjustment_requested':
        return 'Settlement: WAIVER_REQUEST';
      case 'settlement_adjustment_approved':
        return 'Settlement: WAIVER_REQUEST_APPROVED';
      case 'settlement_adjustment_rejected':
        return 'Settlement: WAIVER_REQUEST_REJECTED';
      case 'financing_amount_payment':
        return 'Disbursal: FINANCING_AMOUNT_PAYMENT';
      case 'financing_amount_payment_maker':
        return 'Disbursal: FINANCING_AMOUNT_PAYMENT_MAKER';
      case 'disbursal_rejected_for_defaulter':
        return 'Disbursal: DISBURSAL_REJECTED_FOR_DEFAULTER';
      case 'disbursal_approved_for_defaulter':
        return 'Disbursal: DISBURSAL_APPROVED_FOR_DEFAULTER';
      default:
        return eventType;
    }
  }

  /**
   * Converts a FinanceState to a user friendly string.
   * @param state - show the tooltip of finance
   */
  getFinanceStateTooltip(state) {
    // used by tables to display the state color of finances.
    switch (state) {
      case FinanceStateType.ACCEPTED:
        return 'Application is under financial institution assessment';
      case FinanceStates.BCO_FINANCE_SAVED:
        return 'Application is saved in draft state and yet to be submitted for Financial Institution assessment';
      case FinanceStateType.SETTLED:
        return 'The application is settled';
      case FinanceStateType.APPROVED:
        return 'Terms are accepted by all parties';
      case FinanceStateType.PROCESSING:
        return 'Applicant has to accept terms set by Financial Institution for amount to be transferred';
      case FinanceStates.FINANCE_CANCELLED:
        return 'The application has been cancelled';
      case FinanceStates.BANK_FUNDS_TRANSFERRED:
        return 'The funds has been transferred';
      case FinanceStates.ACCOUNT_SETTLED:
        return 'The Importer has paid back';
      case FinanceStates.DTF_FINANCE_CLOSED:
        return 'Application has been successfully closed';
      case FinanceStates.DETAILS_REQUIRED:
        return 'Financial Institution has requested for more information';
      case FinanceStates.IMPORTER_APPROVAL_PENDING:
        return 'Importer/Buyer acceptance is pending';
      case FinanceStates.APPROVED_BY_MAKER:
        return 'Terms are accepted by maker';
      case FinanceStates.REWORK_PENDING_BY_MAKER:
        return 'Rework pending by maker';
      case FinanceStates.FINANCE_ASSESSMENT_REQUESTED_FOR_DEFAULTER:
        return 'Finance Assessment requested for defaulter';
      case FinanceStates.OVERDUE_WAIVE_OFF_REQUESTED:
        return 'Overdue Waive Off is Requested';
      case FinanceStates.REJECTED_BY_FI:
        return 'The application has been rejected by Financial Institution.';
      case FinanceStates.DUE_DATE_UPDATE_REQUESTED:
        return 'Credit team approval requested for update in due date.';
      case FinanceStates.FUNDS_TRANSFER_UPDATED_BY_MAKER:
        return 'Funds transfer updated by maker.';
      case FinanceStates.FUNDS_TRANSFER_PENDING_REWORK:
        return 'Funds transfer pending rework.';
      case FinanceStates.SETTLEMENT_UPDATED_BY_MAKER:
        return 'Settlement updated by maker.';
      case FinanceStates.PARTIAL_SETTLEMENT_UPDATED_BY_MAKER:
        return 'Partial settlement updated by maker.';
      case FinanceStates.PARTIAL_SETTLEMENT_PENDING_REWORK:
        return 'Partial settlement pending rework.';
      case FinanceStates.SETTLEMENT_PENDING_REWORK:
        return 'Settlement pending rework.';
      case FinanceStates.BALANCE_SETTLEMENT_UPDATED_BY_MAKER:
        return 'Balance settlement updated by maker.';
      case FinanceStates.BALANCE_SETTLEMENT_PENDING_REWORK:
        return 'Balance settlement pending rework.';
      case FinanceStates.DTF_FINANCE_SETTLED:
        return 'DTF Finance Settled';
      case FinanceStates.FUNDS_RETURNED:
        return 'Funds Returned';
      case FinanceStates.FUNDS_RETURNED_UPDATED_BY_CHECKER:
        return 'Funds Returned Updated By Checker';
      case FinanceStates.FUNDS_RETURNED_UPDATED_BY_MAKER:
        return 'Funds Returned Updated By Maker';
      case FinanceStates.REJECTED_BY_APPLICANT:
        return 'Rejected by Applicant';
      default:
        return this.getFinanceState(state, '');
    }
  }

  // Transaction monitoring status tooltip
  getTransactionMonitoringStateTooltip(state: string) {
    if (state === null) {
      return 'Transaction is yet to be reviewed';
    }
    else if (state === 'Approved') {
      return 'Transaction has been approved';
    }
    else if (state === 'Highlighted') {
      return 'Transaction is highlighted and needs review';
    }

  }

  /**
   * Used by applications tab.
   * @param data - processing data
   */
  processData(data, productType, isLendingOrg: boolean, isOperations?: boolean): Array<any> {
    const processedData = [];
    const financeMap = {};
    data.forEach(finance => {
      const product = this.prodConfig.getProductDisplayName(finance.product_category, finance.product_type);
      const record = {
        fi_name: finance.fi_name,
        finance_id: finance.finance_id,
        counter_party_name: finance.counter_party_name,
        applicant_name: finance.applicant_name,
        submitted_at: finance.submitted_at,
        finance_type: finance.finance_type,
        requested_amount: finance.requested_amount,
        state: finance.state,
        friendlyState: this.getFinanceState(finance.state, productType),
        action: finance.action,
        disbursementCurrency: finance.disbursementCurrency,
        product_type: this.prodConfig.getProductTypeFirstLetterCapCC(finance.product_type),
        invoicesDetails: finance.invoicesDetails,
        transaction_monitoring_state: finance.transaction_monitoring_status,
        receivable_amount: finance.receivable_amount,
        isOverdueChargeable: finance.isOverdueChargeable,
        financeInvoices: finance.financeInvoices,
        product_category: finance.product_category,
        product: product,
        applicant_id: finance.applicant_id,
        counter_party_id: finance.counter_party_id,
        active_workflow: finance.active_workflow,
        principalOutstanding: finance.principalOutstanding,
        financeInvoicesCategory: finance.financeInvoicesCategory,
        flags: finance.flags || [],
      };
      if (finance.pendingAuthorisedSignatoryDetails?.length) {
        record['authorisedSignApprovalPendingArr'] = finance.pendingAuthorisedSignatoryDetails.map((item) => item.emailId.toLowerCase());
        record['subStatus'] = finance.subStatus;
      }
      if (finance.authorisedSingatoryApprovalPending) {
        record['authorisedSingatoryApprovalPending'] = true;
      }
      if (isLendingOrg) {
        record['active_user'] = finance.active_user;
        record['platform'] = finance.platform;
        record['enable_limit_flag'] = isOperations && finance.is_exhausted && [FinanceStates.APPROVED_BY_MAKER, FinanceStates.DTF_FINANCE_ACCEPTED, FinanceStates.REWORK_PENDING_BY_MAKER, FinanceStates.CONVERSION_REQUEST_PENDING_MAKER_REVIEW, FinanceStates.CONVERSION_REQUEST_PENDING_CHECKER_REVIEW, FinanceStates.CONVERSION_REQUEST_PENDING_MAKER_RESUBMISSION].includes(finance.state);
        record['account_category'] = finance.account_category ? finance.account_category : '';
        record['fundsReturnedActionFlag'] = finance.fundsReturnedActionFlag ? finance.fundsReturnedActionFlag : false;
      }
      processedData.push(record);
      financeMap[finance.finance_id] = finance;
    });
    return [processedData, financeMap];
  }

  /**
   * Used by View and Apply tabs.
   * @param finance - finance name
   * @param locations - locations of bank
   * @param formBuilder -form
   */
  public createFormFromFinance(finance: FullFinance, locations, formBuilder, isLendingOrg: boolean, lendingBank?: boolean): UntypedFormGroup {
    // setup countries
    // dynamically assign defaults so changes to static data will not break testing tool.
    let toCountry;
    const toCountryName = '';

    let selectedBank = null;
    let fromCountry;
    let fromCountryName = '';

    const financeState = finance.state;
    const prodCategorySnakeUC = this.prodConfig.getCategoryAbbreviationSnakeUC(finance.product_category);
    const productTypeSnakeUC = this.prodConfig.getProductTypeAbbreviationSnakeUC(finance.product_type);
    const isCpToApplicant = this.prodConfig.isDisbursementToCpAndCollectedFromApplicant(prodCategorySnakeUC, productTypeSnakeUC);
    const isApplicantToCp = this.prodConfig.isDisbursementToApplicantAndCollectedFromCp(prodCategorySnakeUC, productTypeSnakeUC);
    const isAplicantToApplicant = this.prodConfig.isDisbursementToApplicantAndCollectedFromApplicant(prodCategorySnakeUC, productTypeSnakeUC);
    const isSponsorInvolved = this.prodConfig.isSponsorInvolved(prodCategorySnakeUC, productTypeSnakeUC);
    if (locations.length > 0) {
      locations.forEach(element => {
        if (element.country_name === finance.shipment_origin_country) {
          fromCountry = element;
          fromCountryName = fromCountry.country_name;
        }
        if (element.country === finance.shipment_destination_country) {
          toCountry = { name: element.name, country: element.country, type: element.type, assosiatePort: element.assosiatePort };
        }
      });
    }

    // this can be empty in logistics case
    if (finance.banksDetails.length > 0) {
      selectedBank = finance.banksDetails[0].bank_full_name;
    }

    // hack to deal with Save scenario not passing back currency
    if (!finance.requested_currency) {
      finance.requested_currency = 'USD';
    }
    let sellerOrBuyerBankId: any;
    let benificiaryBankId: any;
    if (finance.paymentTermsDetails && finance.paymentTermsDetails.length) {
      sellerOrBuyerBankId = finance.paymentTermsDetails[0].applicant_bank_id;
      if (isSponsorInvolved) {
        benificiaryBankId = finance.paymentTermsDetails[0].sponsor_bank_id;
      } else if (isCpToApplicant) {
        benificiaryBankId = finance.paymentTermsDetails[0].counter_party_bank_id;
      }
    }

    const bankIndex = finance.organisaitonBankDetails && finance.organisaitonBankDetails.length && sellerOrBuyerBankId
      ? finance.organisaitonBankDetails.findIndex((item) => item.id === sellerOrBuyerBankId) : -1;
    const sellerOrBuyerBankDetail = (bankIndex !== -1) ? finance.organisaitonBankDetails[bankIndex] : {};

    const formGroup: UntypedFormGroup = formBuilder.group({
      fromCountry: [''],
      fromCountryName: [''],
      toCountry: [''],
      toCountryName: [''],
      fromCity: [''],
      toCity: [''],
      fromPort: [''],
      toPort: [''],
      amount: [1, Validators.required],
      // not actually used, just here to help a control be part of form so can disable with form.
      correspondingPort: [null],

      contentDescription: [''], // [finance.cargoDetails[0].cargo_description, Validators.required], //turned off for now, until we support it.

      importer: [finance.applicant_id, Validators.required],
      applicantName: [finance.applicant_name],
      cargoType: [''],
      loading: [''],
      discharge: [''],
      proofType: [finance.proof_type],
      incoterms: [finance.inco_terms],
      totalAmount: [finance.invoice_amount], // this will be a formatted string, not the value we send to backend
      requestedAmount: [finance.requested_amount], // this will be a formatted string, not the value we send to backend
      totalAmountHolder: [''], // this will be the actual value sent to backend, since totalAmount can't be set
      requestedAmountHolder: [''], // this will be the actual value sent to backend, since requestedAmount can't be set
      endorseProofType: [''],
      endorseFile: [null,],

      dispersalTime: ['',],
      bank: [selectedBank, Validators.required],

      currency: [finance.requested_currency],
      processingFee: [''],
      amountReceivable: [''],
      transactionEvent: [''],
      file: [null],
      cargoFile: [null],  // turned off for now
      isUploadType: ['importer', Validators.required],
      remarks: [''],
      remarkDocument: [],
      created_at: [finance.created_at],
      disbursementCurrency: [finance.disbursementCurrency, Validators.required],
      invoiceCurrency: [finance.invoiceCurrency],
      tradeDocument: [],
      tradeDocumentsName: [],
      dueDate: [finance.due_date, Validators.required],
      sanctionedAmount: [finance.paymentTermsDetails[0]?.requested_sanction_amt_percentage, [Validators.required, this.minValValidator, Validators.max(Number(finance.creditLimitDetails[0]?.max_sanctioned_amount_limit) || 100)]],
      receivableAmount: [finance.requested_amount, [Validators.required, this.minValValidator]],
      processingFees: [finance.paymentTermsDetails[0]?.processing_fee, Validators.required],
      discountFeeAmount: [finance.paymentTermsDetails[0]?.requested_discount_fee_amt, Validators.required],
      processingFeeAmount: [finance.paymentTermsDetails[0]?.requested_processing_fee_amt, Validators.required],
      processingFeeChargeType: [finance.paymentTermsDetails[0]?.processing_fee_type, Validators.required],
      counterPartyInfo: [],
      discountRateType: [finance.paymentTermsDetails[0] ? finance.paymentTermsDetails[0]?.discount_rate_type : 'PER_MONTH'],
      taxAmount: [finance.taxDetails?.tax_amount ? Number(finance.taxDetails.tax_amount).toFixed(2) : ''],
      taxRateCode: [finance.taxDetails?.tax_rate_code ? finance.taxDetails.tax_rate_code : ''],
      taxRegimeCode: [finance.taxDetails?.tax_regime_code ? finance.taxDetails.tax_regime_code : ''],
      fixedInterestRate: [finance.paymentTermsDetails[0]?.overRideInterestRateValue ? finance.paymentTermsDetails[0].overRideInterestRateValue : ''],
      isCustomInterestAllowed: [finance.custom_interest_schedule],
      sponsorId: [finance['sponsor_id']],
      benchmark: [finance.paymentTermsDetails[0]?.finance_rate_source],
      allowMasterInvoiceSelection: [false],
    });

    if (this.prodConfig.validateProductControl(prodCategorySnakeUC, productTypeSnakeUC, 'APPLICANT_DETAILS', 'isVisible', 'MASTER_INVOICE_NO') && finance.masterInstrumentDetails) {
      formGroup.addControl('masterPoAmount', new UntypedFormControl(finance.masterInstrumentDetails?.masterTotalAmount, Validators.required));
      formGroup.addControl('mpoNumber', new UntypedFormControl(finance.masterInstrumentDetails?.instrumentNumber, Validators.required));
      formGroup.addControl('masterFundingPercentage', new UntypedFormControl({ value: finance.masterInstrumentDetails?.masterFundingPercentage, disabled: true }));
      formGroup.addControl('masterFundingLimit', new UntypedFormControl({ value: finance.masterInstrumentDetails?.masterFundingAmount, disabled: true }));
      formGroup.addControl('masterUtilisedLimit', new UntypedFormControl({ value: finance.masterInstrumentDetails?.masterUtilisedAmount, disabled: true }));
      formGroup.addControl('masterAvailableLimit', new UntypedFormControl({ value: finance.masterInstrumentDetails?.masterAvailableLimit, disabled: true }));
    }

    if (this.prodConfig.validateProductControl(prodCategorySnakeUC, productTypeSnakeUC, 'APPLICANT_DETAILS', 'isVisible', 'APPLICATION_NUMBER') && finance.preshipmentApplicationDetails) {
      const preshipmentDetails = finance.preshipmentApplicationDetails;
      formGroup.addControl('masterApplicationNumber', new UntypedFormControl({ value: preshipmentDetails?.appId, disabled: true }));
      formGroup.addControl('masterConversionDate', new UntypedFormControl({ value: preshipmentDetails?.conversionDate, disabled: true }));
      formGroup.addControl('masterCarriedPrincipal', new UntypedFormControl({ value: preshipmentDetails?.carriedPrincipal || 0, disabled: true }));
      formGroup.addControl('masterCarriedTax', new UntypedFormControl({ value: preshipmentDetails?.carriedTaxAmount || 0, disabled: true }));
      formGroup.addControl('masterCarriedInterest', new UntypedFormControl({ value: preshipmentDetails?.carriedInterest || 0, disabled: true }));
      formGroup.addControl('masterCarriedTransactionFee', new UntypedFormControl(preshipmentDetails?.carriedTransactionFee || 0));
      formGroup.addControl('masterCarriedOverdueFee', new UntypedFormControl(preshipmentDetails?.carriedOverdueFee || 0));
      formGroup.addControl('masterCarriedFees', new UntypedFormControl({ value: (this.getPreShipmentCarriedFeeValue(preshipmentDetails)), disabled: true }));
    }

    if (this.prodConfig.validateProductControl(prodCategorySnakeUC, productTypeSnakeUC, 'APPLICANT_DETAILS', 'isVisible', 'ALLOW_MASTER_INVOICE_SELECTION') && finance.masterInstrumentDetails && !finance.preshipmentApplicationDetails && this.prodConfig.checkPoCompatibleProduct(productTypeSnakeUC)) {
      formGroup.get('allowMasterInvoiceSelection').setValue(true);
    }

    if (this.prodConfig.isfactoringFeeApplicable(prodCategorySnakeUC, productTypeSnakeUC)) {
      formGroup.addControl('factoringFee', new UntypedFormControl(finance.paymentTermsDetails[0]?.factoringFee, Validators.required));
      formGroup.addControl('factoringFeeAmount', new UntypedFormControl((finance.paymentTermsDetails[0]?.approvedFactoringFeeAmt || finance.paymentTermsDetails[0]?.requestedFactoringFeeAmt), Validators.required));
      formGroup.addControl('factoringFeeChargeType', new UntypedFormControl(finance.paymentTermsDetails[0]?.factoringFeeType, Validators.required));
    }
    if (finance?.trackingInformation?.length) {
      const mblFormData = formBuilder.array([]);
      const trackingInfo = finance.trackingInformation;
      for (let mblDetail of trackingInfo) {
        const containerNumbersArray = formBuilder.array([]);
        if (mblDetail?.container_number) {
          for (let i = 0; i < mblDetail.container_number.length; i++) {
            const container = mblDetail.container_number[i];
            containerNumbersArray.push(new UntypedFormControl(container));
          }
        }
        const mblFormObj = formBuilder.group(
          {
            mblNumber: [mblDetail?.mbl_number],
            containerNumbers: containerNumbersArray,
            carrierName: [mblDetail?.carrier_name],
            shipmentReference: [mblDetail?.shipment_reference],
            bookingNumber: [mblDetail?.booking_number],
            id: [mblDetail?.id?.toString()],
          });
        if (finance.creditLimitDetails[0].shipment_address_required) {
          mblFormObj.addControl('incoTerm', new UntypedFormControl(mblDetail?.inco_term));
          mblFormObj.addControl('transportModality', new UntypedFormControl(mblDetail?.transport_modality));
          if (mblDetail.destinationAddress) {
            const destinationAddress = mblDetail.destinationAddress;
            const destinationAddressForm = formBuilder.group({
              country: [destinationAddress.country, Validators.required],
              province: [destinationAddress.province, Validators.required],
              city: [destinationAddress.city, Validators.required],
              address1: [destinationAddress.address, Validators.required],
              address2: [destinationAddress.address2, Validators.required],
              pinCode: [destinationAddress.pin_code, Validators.required],
            });
            mblFormObj.addControl('destinationAddress', destinationAddressForm);
          }
          if (mblDetail.originAddress) {
            const originAddress = mblDetail.originAddress;
            const originAddressForm = formBuilder.group({
              country: [originAddress.country, Validators.required],
              province: [originAddress.province, Validators.required],
              city: [originAddress.city, Validators.required],
              address1: [originAddress.address, Validators.required],
              address2: [originAddress.address2, Validators.required],
              pinCode: [originAddress.pin_code, Validators.required],
            });
            mblFormObj.addControl('originAddress', originAddressForm);
          }
        }
        mblFormData.push(mblFormObj);
      }
      formGroup.addControl('mblsDetails', mblFormData);
    } else {
      if (!formGroup.get('mblsDetails')) {
        formGroup.addControl('mblsDetails', this.createMBLDetailsFormArray(formBuilder));
      }
      const trackingInfoValidators = [Validators.pattern(this.constants.inputPatternWithAlphaNumericAndSpace)];
      if (this.prodConfig.validateProductControl(prodCategorySnakeUC, productTypeSnakeUC, 'TRACKING_INFORMATION', 'isMandatory')) {
        if (this.prodConfig.validateProductControl(prodCategorySnakeUC, productTypeSnakeUC, 'TRACKING_INFORMATION', 'isVisible', 'HBL_NO')) {
          const mblDetails = formGroup.get('mblsDetails') as UntypedFormArray;
          for (let index = 0; index < mblDetails.controls?.length; index++) {
            const hblNumber = finance.ehblDetails[index]?.hbl_number ? finance.ehblDetails[index].hbl_number : '';
            formGroup.get('mblsDetails')['controls'][index].addControl('hblNumber', new UntypedFormControl(hblNumber));
          }
        }
        trackingInfoValidators.push(Validators.required);
      }
    }
    let benificiaryBankDetail;
    if (isSponsorInvolved || isCpToApplicant) {
      const bankIndex = finance.organisaitonBankDetails && finance.organisaitonBankDetails.length && benificiaryBankId
        ? finance.organisaitonBankDetails.findIndex((item) => item.id === benificiaryBankId) : -1;
      benificiaryBankDetail = (bankIndex !== -1) ? finance.organisaitonBankDetails[bankIndex] : undefined;
    }
    formGroup.addControl('sellerBankDetails', this.createBankFormData(formBuilder, sellerOrBuyerBankDetail));
    if (benificiaryBankDetail) {
      formGroup.addControl('buyerBankDetails', this.createBankFormData(formBuilder, benificiaryBankDetail, isSponsorInvolved ? 'sponsor' : 'cp'));
    }

    const trackingInfoValidators = [Validators.pattern(this.constants.inputPatternWithAlphaNumericAndSpace)];
    if (this.prodConfig.validateProductControl(prodCategorySnakeUC, productTypeSnakeUC, 'TRACKING_INFORMATION', 'isMandatory')) {
      if (this.prodConfig.validateProductControl(prodCategorySnakeUC, productTypeSnakeUC, 'TRACKING_INFORMATION', 'isVisible', 'HBL_NO')) {
        const mblDetails = formGroup.get('mblsDetails') as UntypedFormArray;
        for (let index = 0; index < mblDetails.controls?.length; index++) {
          const hblNumber = finance.ehblDetails?.[index]?.hbl_number ? finance.ehblDetails[index].hbl_number : '';
          formGroup.get('mblsDetails')['controls'][index].addControl('hblNumber', new UntypedFormControl(hblNumber));
        }
      }
      trackingInfoValidators.push(Validators.required);
    }
    if (isAplicantToApplicant && finance.counterPartyDetails && finance.counterPartyDetails.length > 0) {
      formGroup.addControl('counter_party', new UntypedFormControl(finance.counterPartyDetails[0].name));
      formGroup.addControl('counterPartyName', new UntypedFormControl(finance.counterPartyDetails[0].name));
    } else if (isApplicantToCp) {
      formGroup.addControl('counter_party', new UntypedFormControl(finance.creditLimitDetails[0].counter_party_info ? finance.creditLimitDetails[0].counter_party_id : finance.counter_party_id));
      formGroup.addControl('counterPartyName', new UntypedFormControl(finance.creditLimitDetails[0].counter_party_info ? finance.creditLimitDetails[0].counter_party_org_name : finance.counter_party_name));
    } else if (isCpToApplicant) {
      formGroup.addControl('counterPartyName', new UntypedFormControl(finance.counter_party_name));
      formGroup.addControl('counter_party', new UntypedFormControl(finance.counter_party_name));
    } else {
      formGroup.addControl('counterPartyName', new UntypedFormControl(finance.counter_party_name));
      formGroup.addControl('counter_party', new UntypedFormControl(finance.counter_party_name));
    }

    // TODO: do i still need this?
    if (finance.userDisbursalEventsDetails) {
      // this might not exist in a save scenario
      const dispersal = finance.userDisbursalEventsDetails[0];
      if (dispersal) {
        formGroup.addControl('discountRate', new UntypedFormControl(finance.userDisbursalEventsDetails[0].discount_rate));
      }
    }

    formGroup.addControl('requestedAmount', new UntypedFormControl(finance.requested_amount));
    formGroup.addControl('disbursementCurrency', new UntypedFormControl(finance.disbursementCurrency));
    formGroup.addControl('invoiceCurrency', new UntypedFormControl(finance.invoiceCurrency));
    if (finance.paymentTermsDetails.length) {
      formGroup.addControl('paymentDate', new UntypedFormControl(getUTCDate(finance.paymentTermsDetails[0].payment_date, false), Validators.required));
      formGroup.addControl('blDate', new UntypedFormControl(getUTCDate(finance.paymentTermsDetails[0].bl_date, false)));
      if (finance.paymentTermsDetails[0].approved_finance_tenure) {
        formGroup.addControl('financingTenure', new UntypedFormControl(finance.paymentTermsDetails[0].approved_finance_tenure, Validators.required));
      } else {
        formGroup.addControl('financingTenure', new UntypedFormControl((finance.paymentTermsDetails[0].requested_finance_tenure
          ? finance.paymentTermsDetails[0].requested_finance_tenure
          : ''), Validators.required));
      }
      // Exporter bank details missing.
      formGroup.patchValue({
        dueDate: getUTCDate(new Date(finance.due_date), false),
        sanctionedAmount: finance.paymentTermsDetails[0]?.requested_sanction_amt_percentage,
        receivableAmount: finance.requested_amount,
        processingFees: finance.paymentTermsDetails[0]?.processing_fee,
        discountFeeAmount: finance.paymentTermsDetails[0]?.requested_discount_fee_amt,
        processingFeeAmount: finance.paymentTermsDetails[0]?.requested_processing_fee_amt
      });
      if (this.prodConfig.isfactoringFeeApplicable(prodCategorySnakeUC, productTypeSnakeUC)) {
        formGroup.patchValue({
          factoringFee: finance.paymentTermsDetails[0]?.factoringFee,
          factoringFeeAmount: finance.paymentTermsDetails[0]?.approvedFactoringFeeAmt || finance.paymentTermsDetails[0]?.requestedFactoringFeeAmt,
        });
      }
    } else {
      formGroup.addControl('paymentDate', new UntypedFormControl('', Validators.required));
      formGroup.addControl('financingTenure', new UntypedFormControl(0, Validators.required));
    }
    if (finance.paymentTermsDetails.length && finance.paymentTermsDetails[0]?.finance_rate_source) {
      const paymentTermDetails: any = finance.paymentTermsDetails[0];
      const discountRate = finance.paymentTermsDetails[0]?.discount_rate > 0
        ? Number(finance.paymentTermsDetails[0].discount_rate)
        : (paymentTermDetails.approved_finance_rate_type
          ? paymentTermDetails.approved_finance_rate_type + ' + ' + paymentTermDetails.approved_margin
          : paymentTermDetails.requested_finance_rate_type + ' + ' + paymentTermDetails.requested_margin);
      formGroup.addControl('discountRate', new UntypedFormControl(discountRate ? discountRate : 0, Validators.required));
    } else {
      const discountRate = finance.paymentTermsDetails[0]?.discount_rate;
      formGroup.addControl('discountRate', new UntypedFormControl(discountRate ? Number(discountRate).toFixed(2) : 0, Validators.required))
    }

    if (this.prodConfig.validateProductControl(prodCategorySnakeUC, productTypeSnakeUC, 'APPLICANT_DETAILS', 'isVisible', 'APPLICATION_AMOUNT')) {
      formGroup.addControl('applicationAmount', new UntypedFormControl((finance.invoice_amount ? finance.invoice_amount : 0), [Validators.required, Validators.pattern(this.constants.inputPattern)]));
    } else {
      formGroup.addControl('invoiceDetails', formBuilder.array([]));
      const invoiceDetails: UntypedFormArray = formGroup.get('invoiceDetails') as UntypedFormArray;

      if (finance.invoicesDetails && finance.invoicesDetails.length > 0) {
        finance.invoicesDetails.filter((event, i) => {
          invoiceDetails.push(this.createinvoiceDetails(event, formBuilder, i, lendingBank ? finance.lendingDocuments : finance.documentsDetails, prodCategorySnakeUC, productTypeSnakeUC));
        });
      } else {
        const limit = formGroup.get('masterAvailableLimit')?.value;
        const invoiceDetail = new UntypedFormGroup({
          // counter_party_id: new UntypedFormControl(0, Validators.required),
          invoiceId: new UntypedFormControl('', Validators.required),
          invoiceDueDate: new UntypedFormControl('', Validators.required),
          invoiceDate: new UntypedFormControl('', Validators.required),
          invoiceAmount: new UntypedFormControl(0, [Validators.required, Validators.pattern(this.constants.inputPattern), ...(formGroup.get('mpoNumber')?.value && limit != null ? [Validators.max(limit)] : [])]),
          invoiceAmountInCurrency: new UntypedFormControl(''),
          invoiceFile: new UntypedFormControl('', Validators.required),
          invoiceCommodities: new UntypedFormControl([]),
        });
        if (this.prodConfig.validateProductControl(prodCategorySnakeUC, productTypeSnakeUC, 'APPLICANT_DETAILS', 'isVisible', 'INVOICE_DETAILS', 'INVOICE_COMMODITIES')) {
          invoiceDetail.get('invoiceCommodities').setValidators(Validators.required);
        }
        invoiceDetails.push(invoiceDetail);
      }
    }
    // can be null in logistics case
    if (finance.paymentTermsDetails) {

      formGroup.addControl('eventDetails', formBuilder.array([]));
      const events: UntypedFormArray = formGroup.get('eventDetails') as UntypedFormArray;

      // existing submitted finance
      if (finance.paymentTermsDetails.length > 0 && finance.paymentTermsDetails[0].disbursalEvents && finance.paymentTermsDetails[0].disbursalEvents.length > 0) {
        finance.paymentTermsDetails[0].disbursalEvents.filter((event) => {
          events.push(this.createEventsDetails(event, formBuilder, financeState));
        });
        // loading from a saved finance
      } else {
        const eventDetails = new UntypedFormGroup({
          paymentEvent: new UntypedFormControl(''),
          daysAfterDisbursalEvent: new UntypedFormControl(''),
          paymentPercentage: new UntypedFormControl(''),
          interestBearer: new UntypedFormControl('Exporter'),
          recourse: new UntypedFormControl(''),
          settlementBy: new UntypedFormControl('')
        });
        events.push(eventDetails);
      }

      // this can be confusing
      formGroup.addControl('settlementEventDetails', formBuilder.array([]));
      const settlementEvents: UntypedFormArray = formGroup.get('settlementEventDetails') as UntypedFormArray;

      if (finance.paymentTermsDetails.length === 0 || !finance.paymentTermsDetails[0].settlementEvents || finance.paymentTermsDetails[0].settlementEvents.length === 0) {
        const settlementEventDetails = new UntypedFormGroup({
          settlementEvent: new UntypedFormControl(''),
          daysAfterSettlementEvent: new UntypedFormControl(''),
          settlementPercentage: new UntypedFormControl(''),
        });
        settlementEvents.push(settlementEventDetails);
      } else {
        finance.paymentTermsDetails[0].settlementEvents.forEach((settlementEvent) => {
          settlementEvents.push(this.createSettlementEventsDetails(settlementEvent, formBuilder));
        });
      }
    }
    if (isLendingOrg) {
      formGroup.addControl('internalComments', new UntypedFormControl(''))
      formGroup.addControl('internalCommentDocs', new UntypedFormControl(''));
      formGroup.addControl('lendingEntityStatus', new UntypedFormControl({
        key: 'APPROVED',
        desc: 'Approve'
      }, Validators.required));
    }
    return formGroup;
  }

  createMBLDetailsFormArray(formBuilder: UntypedFormBuilder): UntypedFormArray {
    const tempArray = formBuilder.array([]);
    tempArray.push(formBuilder.group({
      mblNumber: '',
      carrierName: '',
      containerNumbers: formBuilder.array([new UntypedFormControl()]),
      bookingNumber: '',
      shipmentReference: '',
    }))
    return tempArray;
  }

  createEnrichDataFormGroup(formBuilder: UntypedFormBuilder, bank, finance, editable = true, user: User): any {
    const paymentTermDetail = finance.paymentTermsDetails[0];
    const defaultDisbursalDate: Date = new Date(paymentTermDetail?.payment_date);
    defaultDisbursalDate.setDate(defaultDisbursalDate.getDate() + paymentTermDetail?.requested_finance_tenure);
    const bankDetailsArr = finance.organisaitonBankDetails?.length > 0 ?
      finance.organisaitonBankDetails.filter((item) => item?.id === finance.paymentTermsDetails[0].fi_bank_id)
      : [];
    const bankDetailObj = bankDetailsArr.length > 0 ? bankDetailsArr[0] : {
      account_number: '',
      bank_name: '',
      bank_branch_address: '',
      swift_code: '',
      bank_branch_code: '',
      address: '',
      city: '',
      province: '',
      country: '',
      pin_code: '',
    };
    let receivableApprovedAmount = (finance.requested_amount ? finance.requested_amount : '0.00');
    let sanctionApprovedAmount = paymentTermDetail?.requested_sanction_amt_percentage;
    const usedDepts = ['MAKER', 'CHECKER']
    if (usedDepts.includes(user.user_department) && user.is_lending_organisation) {
      if (finance.receivable_amount) {
        receivableApprovedAmount = finance.receivable_amount;
      }
      if (finance.paymentTermsDetails?.length && finance.paymentTermsDetails[0]?.sanction_amt_percentage) {
        sanctionApprovedAmount = finance.paymentTermsDetails[0].sanction_amt_percentage
      }
    }
    const prodCategorySnakeUC = this.prodConfig.getCategoryAbbreviationSnakeUC(finance.product_category);
    const productTypeSnakeUC = this.prodConfig.getProductTypeAbbreviationSnakeUC(finance.product_type);
    const isDisbursementAndCollectionValid = this.prodConfig.isDisbursementToApplicantAndCollectedFromCp(prodCategorySnakeUC, productTypeSnakeUC);
    const enrichDataFormGroup = formBuilder.group({
      bankApproved: [{ value: bank, disabled: true }, Validators.required],
      requestedAmountApproved: [(receivableApprovedAmount), [Validators.required, Validators.min(0), this.minValValidator]],
      disbursementCurrencyApproved: [{ value: finance.disbursementCurrency, disabled: true }, Validators.required],
      totalAmountApproved: [{ value: finance.invoice_amount, disabled: true }, Validators.required],
      invoiceAmount: [finance.invoice_amount, Validators.pattern(this.constants.inputPattern)],
      paymentDate: [getUTCDate(paymentTermDetail?.payment_date, false)],
      sanctionAmountApproved: [sanctionApprovedAmount],
      maxSanctionedAmountLimit: [ finance.preshipmentApplicationDetails?.conversionDate ? '100' : finance.creditLimitDetails[0].max_advance_rate],
      invoiceDetails: formBuilder.array([]),
      actualDisbursalDate: [paymentTermDetail?.payment_date_by_bank ? getUTCDate(paymentTermDetail.payment_date_by_bank, false) : (paymentTermDetail?.payment_date ? getUTCDate(paymentTermDetail?.payment_date, false) : ''), Validators.required],
      invoiceApproveDueDate: [{
        value: (isDisbursementAndCollectionValid
          ? getUTCDate(new Date(finance.due_date), false)
          : getUTCDate(defaultDisbursalDate, false)),
        disabled: true
      }],
      fiBankAccountNumber: [bankDetailObj.account_number, Validators.required], //to be removed
      fiBankName: [bankDetailObj.bank_name, Validators.required], // to be removed
      fiBankBranchAddress: [bankDetailObj.bank_branch_address, Validators.required],
      fiBankSwiftCode: [bankDetailObj.swift_code, Validators.required],
      fiBankCodeAccordingCountry: [bankDetailObj?.bank_branch_code?.length > 0 ? bankDetailObj.bank_branch_code : ''],
      fiBankBeneficiaryStreetAddress: [bankDetailObj.address, Validators.required], //Address + City + State/Province + Country + Pincode/PO box number
      fiBankBeneficiaryCity: [bankDetailObj.city, Validators.required],
      fiBankBeneficiaryState: [bankDetailObj.province, Validators.required],
      fiBankBeneficiaryCountry: [bankDetailObj.country, Validators.required],
      fiBankBeneficiaryPinCode: [bankDetailObj.pin_code, Validators.required],
      discountFeeAmountApproved: ['0.00', [Validators.required, Validators.min(0)]],
      annualProcessingFeeCollected: ['No'],
      facilityEnhancementFeeCollected: ['No'],
      facilityRenewalFeeCollected: ['No'],
      processingFeeAmountApproved: ['0.00', [Validators.required, Validators.min(0)]], // on basis of feeChargeType value or percentage, if Value then set default value as per Form transaction fee
      overdueFeeAmt: ['0.00', [Validators.min(0)]],
      financeCommissionAmount: ['0.00', [Validators.required, Validators.min(0)]],
      transactionFeeAmount: ['0.00', [Validators.required, Validators.min(0)]],
      fiBankReenterAccountNumber: [bankDetailObj.account_number, Validators.required],
      fixedInterestRate: [finance.paymentTermsDetails[0]?.overRideInterestRateValue ? finance.paymentTermsDetails[0].overRideInterestRateValue : (finance.paymentTermsDetails[0]?.finance_rate_source === "CUSTOM" ? finance.paymentTermsDetails[0]?.discount_rate : '')],
      benchmark: [finance.paymentTermsDetails[0]?.finance_rate_source, Validators.required],
      margin: [finance.paymentTermsDetails[0]?.approved_margin || finance.paymentTermsDetails[0]?.requested_margin, Validators.required]
    });
    if (this.prodConfig.validateProductControl(prodCategorySnakeUC, productTypeSnakeUC, 'DISBURSEMENT_TERMS_APPROVED', 'isVisible', 'ALLOW_CUSTOM_INTEREST_RATE')) {
      enrichDataFormGroup.addControl('isCustomInterestAllowed', new UntypedFormControl(finance.custom_interest_schedule));
    }
    if (this.prodConfig.isDisbursementToApplicantAndCollectedFromApplicant(prodCategorySnakeUC, productTypeSnakeUC)) {
      enrichDataFormGroup.addControl('factoringFeeAmountApproved', new UntypedFormControl('0.00', [Validators.required, Validators.min(0)]));
    }
    if (bankDetailObj.id) {
      enrichDataFormGroup.addControl('organisationBankId', new UntypedFormControl(bankDetailObj.id));
    }
    enrichDataFormGroup.get('sanctionAmountApproved').setValidators([Validators.required, Validators.max(paymentTermDetail?.requested_sanction_amt_percentage), this.maxSancAmountLinkValidator, this.minValValidator]);
    if (isDisbursementAndCollectionValid) {
      enrichDataFormGroup.addControl('transactionConfirmationStatus', new UntypedFormControl(false, [Validators.required]));
    }
    if (this.prodConfig.validateProductControl(prodCategorySnakeUC, productTypeSnakeUC, 'APPLICANT_DETAILS', 'isVisible', 'APPLICATION_AMOUNT')) {
      enrichDataFormGroup.addControl('applicationAmount', new UntypedFormControl((finance.invoice_amount ? finance.invoice_amount : 0), [Validators.required, Validators.pattern(this.constants.inputPattern)]));
      if (!editable) {
        enrichDataFormGroup.get('applicationAmount').disable();
      }
      enrichDataFormGroup.removeControl('invoiceDetails');
    } else {
      const invoiceDetails: UntypedFormArray = enrichDataFormGroup.get('invoiceDetails') as UntypedFormArray;
      finance.invoicesDetails.forEach(event => {
        invoiceDetails.push(this.createinvoiceDetails(event, formBuilder, 0, user.is_lending_organisation ? finance.lendingDocuments : finance.documentsDetails, prodCategorySnakeUC, productTypeSnakeUC));
      });
      if (!editable) {
        invoiceDetails.disable();
      }
    }
    enrichDataFormGroup.addControl(
      'flags',
      new UntypedFormControl(
        (finance.flags || []).map((f: any) => ({ key: f.flagTypeId, value: f.name, selected: true }))
      )
    );
    return enrichDataFormGroup;
  }

  maxSancAmountLinkValidator(control: AbstractControl): { [key: string]: boolean } | null {
    if (control.value && control.parent.get('maxSanctionedAmountLimit').value && parseFloat(control.value) > parseFloat(control.parent.get('maxSanctionedAmountLimit').value)) {
      control.markAllAsTouched();
      return { 'bigger': true };
    }
    return null;
  }

  minValValidator(control: AbstractControl): { [key: string]: boolean } | null {
    if (control.value === 0 || control.value === '0' || control.value === '0.00') {
      control.markAllAsTouched();
      return { 'zero': true };
    }
    return null;
  }

  createEventsDetails(event, formBuilder, financeState) {
    let details: any;
    if (financeState === 'BCO_FINANCE_SAVED' || financeState === 'CONVERSION_REQUEST_PENDING_CLIENT_SUBMISSION') {
      details = formBuilder.group({
        paymentEvent: [event.disburse_on_event, Validators.required],
        daysAfterDisbursalEvent: [event.payment_deadline_in_days, Validators.required],
        paymentPercentage: [event.payment_percentage, [Validators.required, Validators.max(100)]],
        interestBearer: [event.interest_bearer, Validators.required],
        recourse: [event.recourse, Validators.required],
        settlementBy: [event.settlement_by, Validators.required],
      });
    } else {
      details = formBuilder.group({
        paymentEvent: [event.disburse_on_event, Validators.required],
        daysAfterDisbursalEvent: [event.payment_deadline_in_days, Validators.required],
        paymentPercentage: [event.payment_percentage, [Validators.required, Validators.max(100)]],
        interestBearer: [event.interest_bearer, Validators.required],
        recourse: [event.recourse, Validators.required],
        settlementBy: [event.settlement_by, Validators.required],
        interestRate: [event.interest_rate, Validators.required],
      });
    }
    return details;
  }

  createinvoiceDetails(detail: UserDisbursalEvent | DisbursalEvent | any, formBuilder: any, i?: any, documentDetails?: any, prodCategorySnakeUC?: string, productTypeSnakeUC?: string) {
    let invoiceDetails: any;
    const ind = documentDetails?.findIndex(doc => doc.id === detail.invoice_document_id);
    invoiceDetails = formBuilder.group({
      invoiceId: [detail.invoice_id, Validators.required],
      invoiceDate: [getUTCDate(detail.invoice_date, false), Validators.required],
      invoiceDueDate: [getUTCDate(detail.invoice_due_date, false), Validators.required],
      invoiceAmount: [detail.invoice_amount, [Validators.required, Validators.pattern(this.constants.inputPattern)]],
      invoiceAmountInCurrency: [detail.invoiceAmountInCurrency],
      invoiceFile: [documentDetails?.[ind]?.document_name, Validators.required],
      invoiceDocumentId: [documentDetails?.[ind]?.id],
      invoiceTerms: [detail.invoice_terms, Validators.required],
      invoiceCommodities: [detail.invoiceCommodities ? detail.invoiceCommodities : []],
      id: [detail.id],
    });
    if (prodCategorySnakeUC && productTypeSnakeUC && this.prodConfig.validateProductControl(prodCategorySnakeUC, productTypeSnakeUC, 'APPLICANT_DETAILS', 'isVisible', 'INVOICE_DETAILS', 'INVOICE_COMMODITIES')) {
      invoiceDetails.get('invoiceCommodities').setValidators(Validators.required);
    }
    if (i >= 1) {
      invoiceDetails.get('invoiceDueDate').disable();
      invoiceDetails.get('invoiceTerms').disable();
    }
    return invoiceDetails;
  }

  /**
   * shown in Accept Terms for Trade Finance.
   * @param event - event of settlement event
   * @param formBuilder - form
   */
  createSettlementEventsDetails(event: SettlementEvent, formBuilder) {
    const terms = formBuilder.group({
      settlementEvent: [event.settle_on_event, Validators.required],
      daysAfterSettlementEvent: [event.settlement_deadline_in_days, Validators.required],
      settlementPercentage: [event.settlement_percentage, [Validators.required, Validators.max(100)]],

    });

    return terms;
  }

  /**
   * our azure pipeline isn't setup to deploy a staging build to staging, it deploys prod build
   * there instead, so we can't use a bool in environment file to detect staging right now. So
   * we are using this work around.
   */
  isStaging() {
    return (window.location.host === 'staging.revma.dpworld.com' || window.location.host === 'staging-finance.dpworld.com');
  }

  isQA() {
    return (window.location.host === 'qa.revma.dpworld.com' || window.location.host === 'qa-finance.dpworld.com');
  }

  isDevlopment() {
    return (window.location.host === 'dev.revma.dpworld.com' || window.location.host === 'qa-tradefinance.dpworld.com');
  }

  isUat(): boolean {
    return (window.location.host === 'uat.revma.dpworld.com' || window.location.host === 'uat-tradefinance.dpworld.com');
  }

  isProd() {
    return !this.isStaging && !isDevMode() && !this.isQA() && !this.isDevlopment() && !this.isUat();
  }

  logisticsRequestedAmount(form: UntypedFormGroup) {
    let amount = 0;

    // sum of %s * invoiceAmount
    const invoiceDetailsFormGroup = (form.get('invoiceDetails') as UntypedFormArray).controls;
    invoiceDetailsFormGroup.forEach((eventDetails: UntypedFormGroup) => {

      const percentage = eventDetails.get(`invoiceAmount`);
      if (percentage) {
        const paymentPercentage = percentage.value;
        amount += parseFloat(paymentPercentage) || 0;
      }
    });

    return amount.toFixed(2);
  }

  isActualTransactionsVisible(finance): boolean {
    const transactionVisibleStates = [
      FinanceStates.DTF_FINANCE_CLOSED,
      FinanceStates.ACCOUNT_SETTLED,
      FinanceStates.PARTIALLY_SETTLED,
      FinanceStates.WAIVER_REQUEST,
      FinanceStates.WAIVER_REQUEST_REJECTED
    ]
    return transactionVisibleStates.includes(finance?.state);
  }

  createLogisticsTermsForm(finance: FullFinance, form: UntypedFormGroup, _formBuilder: UntypedFormBuilder, user: User, prodCategorySnakeUC: string, productTypeSnakeUC: string): UntypedFormGroup {
    const bankAssessmentFormGroup: UntypedFormGroup = _formBuilder.group({
      bankApproved: [form ? form.get('bank').value : ''],
      requestedAmountApproved: [{ value: finance ? finance.receivable_amount : '', disabled: true }, [Validators.required, this.minValValidator]],
      disbursementCurrencyApproved: [{ value: finance ? finance.disbursementCurrency : '', disabled: true }, Validators.required],
      totalAmountApproved: [{ value: finance ? finance.invoice_amount : '', disabled: true }, Validators.required],
      invoiceAmount: [{ value: finance ? finance.invoice_amount : '', disabled: true }, Validators.pattern(this.constants.inputPattern)],
      fixedInterestRate: [finance.paymentTermsDetails[0]?.overRideInterestRateValue ? finance.paymentTermsDetails[0].overRideInterestRateValue : '']
    });
    if (finance && this.isActualTransactionsVisible(finance)) {
      bankAssessmentFormGroup.addControl('finalDisbursalDate', new UntypedFormControl({ disabled: true }));
      bankAssessmentFormGroup.addControl('finalSettlementDate', new UntypedFormControl({ disabled: true }));
      bankAssessmentFormGroup.addControl('actualFinancingTenure', new UntypedFormControl({ disabled: true }));
      bankAssessmentFormGroup.addControl('otherChargesType', new UntypedFormControl({ disabled: true }));
      bankAssessmentFormGroup.addControl('otherChargesAmount', new UntypedFormControl({ value: '0.00', disabled: true }));
    }
    bankAssessmentFormGroup.addControl('financeCommissionAmount', new UntypedFormControl({ value: '0.00', disabled: true }));
    bankAssessmentFormGroup.addControl('miscFeesCommission', new UntypedFormControl({ value: '0.00', disabled: true }));
    if (finance && finance.transactionsDetails && finance.transactionsDetails.length) {
      finance.transactionsDetails.forEach(trx => {
        if (trx.transaction_event_type === 'Disbursal' && this.isActualTransactionsVisible(finance)) {
          bankAssessmentFormGroup.setControl('finalDisbursalDate', new UntypedFormControl({ value: getUTCDate(trx.transaction_event_date, false), disabled: true }));
        } else if (trx.transaction_event_type === 'Settlement') {
          if (this.isActualTransactionsVisible(finance)) {
            bankAssessmentFormGroup.setControl('finalSettlementDate', new UntypedFormControl({ value: getUTCDate(trx.transaction_event_date, false), disabled: true }));
            bankAssessmentFormGroup.get('otherChargesType').setValue(trx.other_charges_type ? trx.other_charges_type : '');
            bankAssessmentFormGroup.get('otherChargesAmount').setValue(trx.other_charges_amount ? trx.other_charges_amount : '');
          }
          bankAssessmentFormGroup.get('miscFeesCommission').setValue(trx.dpwfs_miscellaneous_fees_commission ? trx.dpwfs_miscellaneous_fees_commission : '');
          bankAssessmentFormGroup.get('financeCommissionAmount').setValue(trx.dpwfs_finance_commission ? trx.dpwfs_finance_commission : '');
        }
      })
      if (this.isActualTransactionsVisible(finance) && bankAssessmentFormGroup.get('finalDisbursalDate').value && bankAssessmentFormGroup.get('finalSettlementDate').value) {
        bankAssessmentFormGroup.setControl('actualFinancingTenure', new UntypedFormControl({ value: getDateDiff(bankAssessmentFormGroup.get('finalSettlementDate').value, bankAssessmentFormGroup.get('finalDisbursalDate').value), disabled: true }))
      }
    }
    if (this.prodConfig.validateProductControl(prodCategorySnakeUC, productTypeSnakeUC, 'DISBURSEMENT_TERMS_APPROVED', 'isVisible', 'ALLOW_CUSTOM_INTEREST_RATE')) {
      bankAssessmentFormGroup.addControl('isCustomInterestAllowed', new UntypedFormControl(finance.custom_interest_schedule));
    }

    if (finance && finance.paymentTermsDetails && finance.paymentTermsDetails.length) {
      bankAssessmentFormGroup.addControl('invoiceApproveDueDate', new UntypedFormControl({ value: getUTCDate(finance.approved_due_date, false), disabled: true }));
      bankAssessmentFormGroup.addControl('actualDisbursalDate', new UntypedFormControl({ value: getUTCDate(finance.paymentTermsDetails[0].payment_date_by_bank, false), disabled: true }));
      bankAssessmentFormGroup.addControl('updatedInvoiceApproveDueDate', new UntypedFormControl({ value: getUTCDate(finance.updated_approved_due_date, false), disabled: true }));
      bankAssessmentFormGroup.addControl('actualPaymentDate', new UntypedFormControl({ value: getUTCDate(finance.paymentTermsDetails[0].actual_payment_date, false), disabled: true }));
      bankAssessmentFormGroup.addControl('invoiceDiscountRate', new UntypedFormControl({ value: (finance.paymentTermsDetails[0].sanction_amt_percentage == null) ? '' : parseFloat(finance.paymentTermsDetails[0].discount_rate_per_month).toFixed(2), disabled: true }, Validators.required));

      const bankDetails = finance.organisaitonBankDetails && finance.organisaitonBankDetails.length > 0 ?
        finance.organisaitonBankDetails.filter((item) => item?.id === finance.paymentTermsDetails[0].fi_bank_id)
        : [];

      if (user.organisation_type === 'BANK' || user.organisation_type === 'DTF' || (user.organisation_type === 'ORG' && finance.applicant_id === user.organisation_id)) {
        bankAssessmentFormGroup.addControl('fiBankAccountNumber', new UntypedFormControl(bankDetails.length > 0 ? bankDetails[0].account_number : ''));
        bankAssessmentFormGroup.addControl('fiBankName', new UntypedFormControl(bankDetails.length > 0 ? bankDetails[0].bank_name : ''));
        bankAssessmentFormGroup.addControl('fiBankBranchAddress', new UntypedFormControl(bankDetails.length > 0 ? bankDetails[0].bank_branch_address : ''));
        bankAssessmentFormGroup.addControl('fiBankSwiftCode', new UntypedFormControl(bankDetails.length > 0 ? bankDetails[0].swift_code : ''));
        bankAssessmentFormGroup.addControl('fiBankCodeAccordingCountry', new UntypedFormControl(bankDetails.length && bankDetails[0].bank_branch_code?.length > 0 ? bankDetails[0].bank_branch_code : ''));
        bankAssessmentFormGroup.addControl('fiBankBeneficiaryStreetAddress', new UntypedFormControl(bankDetails.length > 0 ? bankDetails[0].address : ''));
        bankAssessmentFormGroup.addControl('fiBankBeneficiaryCity', new UntypedFormControl(bankDetails.length > 0 ? bankDetails[0].city : ''));
        bankAssessmentFormGroup.addControl('fiBankBeneficiaryState', new UntypedFormControl(bankDetails.length > 0 ? bankDetails[0].province : ''));
        bankAssessmentFormGroup.addControl('fiBankBeneficiaryCountry', new UntypedFormControl(bankDetails.length > 0 ? bankDetails[0].country : ''));
        bankAssessmentFormGroup.addControl('fiBankBeneficiaryPinCode', new UntypedFormControl(bankDetails.length > 0 ? bankDetails[0].pin_code : ''));
        bankAssessmentFormGroup.addControl('fiBankReenterAccountNumber', new UntypedFormControl(bankDetails.length > 0 ? bankDetails[0].account_number : ''));
        bankAssessmentFormGroup.addControl('discountFeeAmountApproved', new UntypedFormControl(finance.paymentTermsDetails[0].approved_discount_fee_amt));
        bankAssessmentFormGroup.addControl('processingFeeAmountApproved', new UntypedFormControl(finance.paymentTermsDetails[0].approved_processing_fee_amt));
        bankAssessmentFormGroup.addControl('overdueFeeAmt', new UntypedFormControl(finance.overdueFeeDetails?.overdueFeeAmt ? finance.overdueFeeDetails.overdueFeeAmt : '0.00'));
        bankAssessmentFormGroup.addControl('annualProcessingFeeCollected', new UntypedFormControl(finance.creditLimitDetails && finance.creditLimitDetails[0]?.annualProcessingFeeCollected ? 'Yes' : 'No'));
        bankAssessmentFormGroup.addControl('transactionFeeAmount', new UntypedFormControl(finance.paymentTermsDetails[0].transaction_fee_amt));
        bankAssessmentFormGroup.addControl('benchmark', new UntypedFormControl(finance.paymentTermsDetails[0]?.finance_rate_source));
        bankAssessmentFormGroup.addControl('margin', new UntypedFormControl(finance.paymentTermsDetails[0]?.approved_margin || finance.paymentTermsDetails[0]?.requested_margin, [Validators.required]));

        if (bankAssessmentFormGroup.get('financeCommissionAmount').value === '0.00') {
          bankAssessmentFormGroup.get('financeCommissionAmount').setValue(finance.paymentTermsDetails[0].finance_commission_amt);
        }
        if (this.prodConfig.isfactoringFeeApplicable(prodCategorySnakeUC, productTypeSnakeUC)) {
          bankAssessmentFormGroup.addControl('factoringFeeAmountApproved', new UntypedFormControl(finance.paymentTermsDetails[0].factoringFeeAmountApproved));
        }
      }
      if (user.is_lending_organisation) {
        bankAssessmentFormGroup.addControl('facilityEnhancementFeeCollected', new UntypedFormControl(finance.creditLimitDetails && finance.creditLimitDetails[0]?.facilityEnhancementFeeCollected ? 'Yes' : 'No'));
        bankAssessmentFormGroup.addControl('facilityRenewalFeeCollected', new UntypedFormControl(finance.creditLimitDetails && finance.creditLimitDetails[0]?.facilityRenewalFeeCollected ? 'Yes' : 'No'));
      }
      bankAssessmentFormGroup.addControl('sanctionAmountApproved', new UntypedFormControl({ value: (finance.paymentTermsDetails[0].sanction_amt_percentage == null) ? '' : finance.paymentTermsDetails[0].sanction_amt_percentage, disabled: true }, [Validators.required, this.minValValidator]));
      if (finance.paymentTermsDetails[0].fees_actual) {
        this.setActualFeesControls(bankAssessmentFormGroup, finance, prodCategorySnakeUC, productTypeSnakeUC);
      }
    } else {
      bankAssessmentFormGroup.addControl('invoiceApproveDueDate', new UntypedFormControl({ value: '', disabled: true }));
      bankAssessmentFormGroup.addControl('actualDisbursalDate', new UntypedFormControl({ value: '', disabled: true }));
      bankAssessmentFormGroup.addControl('invoiceDiscountRate', new UntypedFormControl({ value: '', disabled: true }, Validators.required));
      bankAssessmentFormGroup.addControl('sanctionAmountApproved', new UntypedFormControl({ value: '100.00', disabled: true }, [Validators.required, this.minValValidator]));
      bankAssessmentFormGroup.addControl('actualDiscountFeeAmount', new UntypedFormControl({ value: '', disabled: true }));
      bankAssessmentFormGroup.addControl('actualProcessingFeeAmount', new UntypedFormControl({ value: '', disabled: true }));
      bankAssessmentFormGroup.addControl('actualTaxAmount', new UntypedFormControl({ value: '', disabled: true }));
      bankAssessmentFormGroup.addControl('actualOverdueInterestAmount', new UntypedFormControl({ value: '', disabled: true }));
      if (this.prodConfig.isfactoringFeeApplicable(prodCategorySnakeUC, productTypeSnakeUC)) {
        bankAssessmentFormGroup.addControl('actualFactoringFeeAmt', new UntypedFormControl({ value: '', disabled: true }));
        bankAssessmentFormGroup.addControl('actualFactoringFeeTaxAmt', new UntypedFormControl({ value: '', disabled: true }));
      }
    }
    if (this.prodConfig.validateProductControl(prodCategorySnakeUC, productTypeSnakeUC, 'APPLICANT_DETAILS', 'isVisible', 'APPLICATION_AMOUNT')) {
      bankAssessmentFormGroup.addControl('applicationAmount', new UntypedFormControl((finance.invoice_amount ? finance.invoice_amount : 0), [Validators.required, Validators.pattern(this.constants.inputPattern)]));
    } else {
      bankAssessmentFormGroup.addControl('invoiceDetails', _formBuilder.array([]));
      const invoiceDetails: UntypedFormArray = bankAssessmentFormGroup.get('invoiceDetails') as UntypedFormArray;
      if (finance) {
        finance.invoicesDetails.forEach((invoice) => {
          invoiceDetails.push(this.createinvoiceDetails(invoice, _formBuilder, 0, user.is_lending_organisation ? finance.lendingDocuments : finance.documentsDetails, prodCategorySnakeUC, productTypeSnakeUC));
        });
      }
    }
    bankAssessmentFormGroup.addControl(
      'flags',
      new UntypedFormControl(
        (finance.flags || []).map((f: any) => ({ key: f.flagTypeId, value: f.name, selected: true }))
      )
    );
    bankAssessmentFormGroup.disable();
    return bankAssessmentFormGroup;
  }


  isAccountFullySetup(user: User, tenantCode: string) {
    if (!user.is_verified) {
      return false;
    }
    if (this.isCaymanOrgRequestPending(user, tenantCode)) {
      return true;
    } else if (!user.is_org_verified) {
      return false;
    }
    return true;
  }

  isCaymanOrgRequestPending(user: User, tenantCode: string): boolean {
    if (tenantCode === environment.serviceProviderCodeCayman && user) {
      const termsAcceptedStr = localStorage.getItem(environment.storage.termsAndConditionsAccepted);
      const termsAcceptedObj = termsAcceptedStr ? JSON.parse(termsAcceptedStr) : '';
      const isTermsAccepted = termsAcceptedObj && termsAcceptedObj[environment.serviceProviderCodeCayman]
        || (user.accepted_terms_version && user.accepted_terms_version[environment.serviceProviderCodeCayman].TermsAndCondition
          && user.accepted_terms_version[environment.serviceProviderCodeCayman].TermsAndCondition.length > 0);
      if (isTermsAccepted) {
        if (user.companyStatus && (user.companyStatus.name === 'Pending Approval'
          || user.companyStatus.name === 'Partially Approved' || user.companyStatus.name === 'On Hold')) {
          return true;
        }
        // else if () {
        // This condition to be used after Cayman Registration is submitted and user didn't logout.
        // }
      }
    }
    return false;
  }

  getApplyIndex() {
    return this.applyIndex;
  }

  setApplyIndex(index: number) {
    this.applyIndex = index;
  }

  // used to make form controls as red when we submit.
  validateAllFormFields(formGroup: UntypedFormGroup) {
    Object.keys(formGroup.controls).forEach(field => {
      const control = formGroup.get(field);
      if (control instanceof UntypedFormControl) {
        control.markAsTouched({ onlySelf: true });
      } else if (control instanceof UntypedFormGroup) {
        this.validateAllFormFields(control);
      } else if (control instanceof UntypedFormArray) {
        Object.keys(control.controls).forEach(arrayField => {
          const arrayControl = control.get(arrayField);
          if (arrayControl instanceof UntypedFormGroup) {
            this.validateAllFormFields(arrayControl);
          }
        });
      }
    });
  }

  // easy way to save the pagination for a view, so we can reload to the same page when we come back.
  getSavedPageState(keySavedTo: string, keySavedToForTime: string, millisecondsBeforeExpire = 3600000) {
    // try to find the last index we were on.
    const paginationSaveTime = window.localStorage.getItem(keySavedToForTime);
    if (paginationSaveTime) {
      const paginationSaveTimeNum = parseInt(paginationSaveTime);
      if (paginationSaveTimeNum + millisecondsBeforeExpire > new Date().getTime()) {
        return window.localStorage.getItem(keySavedTo);
      }
    }
  }

  getStoredFinanceType() {
    return JSON.parse(localStorage.getItem(environment.storage.productCombination));
  }

  updateFinanceTypeInfo(productCombination: FinanceTypeInfoInterface) {
    localStorage.setItem(environment.storage.productCombination, JSON.stringify(productCombination));
  }

  updateSellerBuyerOrFIDropDowns(dropDownType: string, productCategory: string, productType: string, allRaisedCreditFacilityData: any[], statusType: string, currencyType: string,
    applicantId?: number, fiId?: number, id?: number): {
      sellers: SellerOrBuyerOrFinanceInst[],
      //buyers: SellerOrBuyerOrFinanceInst[],
      financial_institutions: SellerOrBuyerOrFinanceInst[]
    } {
    const dropDownData = {
      sellers: [],
      //buyers: [],
      financial_institutions: []
    };
    allRaisedCreditFacilityData.forEach((element) => {
      this.filterAndUpdateDropDownData(element, currencyType, statusType, dropDownType, dropDownData, applicantId, fiId, id);
    });
    return dropDownData;
  }

  filterAndUpdateDropDownData(element, currencyType: string, statusType: string, dropDownType: string, dropDownData, applicantId?: number, fiId?: number, id?: number): void {
    const reqSellerCond = applicantId ? applicantId === element.applicant.id : true;
    const reqFiCond = fiId ? fiId === element.finiancialInstitutions.id : true;
    let reqCurrency = true;
    if (currencyType) {
      if (element.approved_currency) {
        reqCurrency = element.approved_currency === currencyType;
      } else if (element.requested_currency) {
        reqCurrency = element.requested_currency === currencyType;
      }
    }
    if (reqCurrency && reqSellerCond && reqFiCond) {
      if ((statusType === 'APPROVED' && element.credit_limit_status === 'Completed' && !this.isCreditLimitExpired(element))
        || (statusType === 'REQUESTED' && element.credit_limit_status !== 'Completed')) {
        if (dropDownType === 'status' || dropDownType === 'currencies') {
          this.validateIfExistsAndGetDropDownType('sellers', 'applicant', dropDownData, element);
          //this.validateIfExistsAndGetDropDownType('buyers', 'counter_party', dropDownData, element);
          this.validateIfExistsAndGetDropDownType('financial_institutions', 'finiancialInstitutions', dropDownData, element);
        } else if (dropDownType === 'buyers' && element.counter_party.id === id) {
          this.validateIfExistsAndGetDropDownType('sellers', 'applicant', dropDownData, element);
          this.validateIfExistsAndGetDropDownType('financial_institutions', 'finiancialInstitutions', dropDownData, element);
        } else if (dropDownType === 'sellers' && element.applicant.id === id) {
          //this.validateIfExistsAndGetDropDownType('buyers', 'counter_party', dropDownData, element);
          this.validateIfExistsAndGetDropDownType('financial_institutions', 'finiancialInstitutions', dropDownData, element);
        } else if (dropDownType === 'financial_institutions' && element.finiancialInstitutions.id === id) {
          this.validateIfExistsAndGetDropDownType('sellers', 'applicant', dropDownData, element);
          //this.validateIfExistsAndGetDropDownType('buyers', 'counter_party', dropDownData, element);
        }
      }
    }
  }

  validateIfExistsAndGetDropDownType(dropDownType: string, fieldType: string, dropDownData: any, currentElement: FinanceTypeDetails) {
    let isAlreadyPresent = false;
    dropDownData[dropDownType].forEach(element => {
      if (element.id === currentElement[fieldType].id && element.name === currentElement[fieldType].name) {
        isAlreadyPresent = true;
      }
    });
    if (currentElement[fieldType].id && currentElement[fieldType].name && !isAlreadyPresent) {
      dropDownData[dropDownType].push(currentElement[fieldType]);
    }
  }

  isCreditLimitExpired(element): boolean {
    const currDate = new Date();
    const expiryDateKey = element.extended_agreement_expiry_date ? 'extended_agreement_expiry_date' : 'agreement_expiry_date';
    const compareDate = getDateDiff(element[expiryDateKey], currDate);
    return (element[expiryDateKey] && compareDate < 0) ? true : false;
  }

  removeDuplicateValueInrray(requestData): Array<any> {
    const setObj = new Set(); // create key value pair from array of array
    return requestData.reduce((acc, item) => {
      if (!setObj.has(item.id + item.cfId)) {
        setObj.add(item.id + item.cfId)
        acc.push(item)
      }
      return acc;
    }, []);
  }

  attachWindowResizeEvent(): void {
    window.addEventListener('resize', (event) => {
      if (event && event.target) {
        this.isMobileDevice = window.screen.width <= 600;
      }
    }, true);
  }

  getNotificationNavDetails(notification: Notifications, user: User, calledFrom: string): { url: string, queryParams: {} } {
    const financeId = notification.owner_id;
    let navUrl = 'apply/def';
    let queryParams = {};
    if (this.isCFNotification(notification.notification_type)) {
      localStorage.setItem(environment.storage.creditLimitNotification, 'true');
      if ((calledFrom === 'pendingTasks' && notification.actionable) || (calledFrom !== 'pendingTasks' && notification.actionable && !notification.has_seen)) {
        if (user.organisation_type === 'BANK') {
          navUrl = user.is_lending_organisation
            ? 'credit-limit/module-lending/accept-facility'
            : 'credit-limit/module-arranging/accept-facility';
        } else if (user.organisation_type === 'ORG') {
          navUrl = 'credit-limit/module-arranging/facility-terms-acceptance';
        } else {
          navUrl = 'credit-limit/module-arranging/view-facility-other';
        }
      } else {
        navUrl = user.is_lending_organisation
          ? 'credit-limit/module-lending/view-facility'
          : (user.organisation_type === 'ORG'
            ? 'credit-limit/module-arranging/view-facility-client'
            : 'credit-limit/module-arranging/view-facility-other');
      }
      queryParams = {
        id: financeId
      };
    } else if (notification.notification_type.includes('EXPOSURE')) {
      navUrl = 'exposure-control/update'
      if (calledFrom == 'notifications' && notification.actionable) {
        queryParams = {
          id: financeId
        };
      }
    } else if (notification.notification_type.includes('PORTFOLIO_REVIEW')) {
      const params = {
        sessionId: financeId
      };
      if (notification.notification_info['activeUser'] !== user.user_department) {
        params['isView'] = true;
      }
      navUrl = 'portfolio-monitoring/review'
      if (calledFrom == 'notifications' && notification.actionable) {
        queryParams = params;
      }
    } else if (notification.notification_type.includes('CLIENT_CATEGORY')) {
      navUrl = 'account-categorisation/client-history'
      queryParams = {
        isViewPage: true,
        clientId: financeId
      };
      if (calledFrom === 'notifications' && notification.actionable) {
        queryParams['isViewPage'] = false
      };
    } else if (notification.notification_type.includes('SCF')) {
      navUrl = 'scf/program'
      if (calledFrom == 'notifications' && notification.actionable) {
        if (notification.notification_type === 'SCF_PROGRAM_UPDATE') {
          navUrl = 'scf/view-program';
          queryParams['id'] = financeId;
        } else if (notification.notification_type === 'SCF') {
          navUrl = 'scf/view-invoices';
          queryParams['finance_id'] = financeId;
        }
      }
    } else {
      const ownerType = notification.owner_type;
      const productCategory = this.prodConfig.getCategoryAbbreviationLC(notification.notification_type);
      const productType = this.prodConfig.conditionMapConfig.productType[ownerType]
        ? this.prodConfig.convertToCamelCase(ownerType)
        : '';
      queryParams = {
        finance: financeId
      };
      queryParams['productCategory'] = productCategory;
      queryParams['productType'] = productType;
    }
    if (notification.notification_info && Object.keys(notification.notification_info).length > 0) {
      for (const prop in notification.notification_info) {
        queryParams[prop] = notification.notification_info[prop];
      }
    }
    const notificationNavDetails = {
      url: navUrl,
      queryParams
    };
    return notificationNavDetails;
  }

  /**
   * Used to set default configs required for grouping and modify/group data by grouped cols
   * @param initialDataSet - Initial data coming from API call
   */
  groupDataBySpecificColumns(initialDataSet: any[], calledFrom: string, groupByCols: string[], user: User): any[] {
    const rootGroup = new RowGroup();
    // use expanded variable to show expand/collapse functionality if required.
    rootGroup.expanded = true;
    const groupedData = this.getSubLevelData(initialDataSet, 0, groupByCols, rootGroup, calledFrom, user);
    return groupedData;
  }

  /**
   * Used to prepare datasource, with Grouping done on 'groupingByCols'.
   * Method will first find the unique values for grouped columns and then recursively call itself to populate
   * the sub-level or sub-group data.
   * @param data - current level grouped data
   * @param level - current level number
   * @param groupByCols - columns against which grouping needs to be done ie., 'fi_org_name' or 'document_type'
   * @param parent - parent group info/data
   */
  getSubLevelData(data: any[], level: number, groupByCols: string[], parent: RowGroup, calledFrom: string, user: User): any {
    if (level >= groupByCols.length) {
      return data;
    }
    const groups = this.uniqueBy(
      data.map(row => {
        const result = new RowGroup();
        result.level = level + 1;
        result.parent = parent;
        for (let i = 0; i <= level; i++) {
          result[groupByCols[i]] = row[groupByCols[i]];
          if (calledFrom === 'credit-limit') {
            result['documents'] = row['documents'];
            result['documents_required'] = row['documents_required'];
            result['user'] = user;
            result['credit_limit_finance_type'] = row['credit_limit_finance_type'];
            result['finance_product_type'] = row['finance_product_type'];
            result['fi_id'] = row['fi_id'];
            result['interest_rate_range'] = row['interest_rate_range'];
            result['processing_fee_range'] = row['processing_fee_range'];
            result['product_deleted_at'] = row['product_deleted_at'];
            result['supported_currencies'] = row['supported_currencies'];
            const productTypeSnakeUC = this.prodConfig.getProductTypeAbbreviationSnakeUC(row['credit_limit_finance_type']);
            const countryControlIndex = this.prodConfig.productOnboardingConfig.incorporationCountryControls.findIndex((formControlObj) => formControlObj.productType === productTypeSnakeUC);
            const productApiKey = this.prodConfig.productOnboardingConfig.incorporationCountryControls[countryControlIndex].apiKey;
            result[productApiKey] = row[productApiKey];
          }
        }
        return result;
      }),
      JSON.stringify
    );
    const currentColumn = groupByCols[level];
    let subGroups = [];
    groups.forEach(group => {
      const rowsInGroup = data.filter(row => group[currentColumn] === row[currentColumn]);
      group.totalCounts = rowsInGroup.length;
      const subGroup = this.getSubLevelData(
        rowsInGroup,
        level + 1,
        groupByCols,
        group,
        calledFrom,
        user
      );
      subGroup.unshift(group);
      subGroups = subGroups.concat(subGroup);
    });
    return subGroups;
  }

  /**
   * Used to get unique grouped values
   * @param a - Array to iterate on to get unique group column values
   * @param key - JSON.stringify, to convert the name to string.
   */
  uniqueBy(a, key) {
    const seen = {};
    return a.filter(item => {
      const k = key(item);
      return seen.hasOwnProperty(k) ? false : (seen[k] = true);
    });
  }

  formAlphabeticalGroup(): any {
    const alphabetStr = 'abcdefghijklmnopqrstuvwxyz';
    const obj = {};
    for (let i = 0; i < alphabetStr.length; i++) {
      obj[alphabetStr[i]] = {
        active: false,
        names: []
      };
    }
    return obj;
  }

  parseInteger(value: any, digit): string {
    const strValue = String(value);
    return String(parseInt(strValue).toFixed(digit));
  }

  setFilteredLocationsData(type: string, data: any[]): void {
    if (type === 'city') {
      this.filteredCity = data;
    } else if (type === 'state') {
      this.filteredState = data;
    } else if (type === 'country') {
      this.filteredCountry = data;
    }
  }

  pushToGooleAnalyticsCustom(eventName: string, compReference: string, eventCategory: string, eventAction: string, companyRole: string): void {
    const gtmTag = {
      event: eventName,
      compreference: compReference,
      eventdetail: {
        'eventCategory': eventCategory,
        'eventAction': eventAction,
        'eventLabel': companyRole
      }
    };
    this.googleAnalytics.pushTag(gtmTag);
  }

  createBankFormData(formBuilder, sellerOrBuyerBankDetail, beneficiaryType?: string) {
    return formBuilder.group({
      sellerBankAccount: [sellerOrBuyerBankDetail.account_number ? sellerOrBuyerBankDetail.account_number : '', Validators.required],
      bankName: [sellerOrBuyerBankDetail.bank_name ? sellerOrBuyerBankDetail.bank_name : '', Validators.required],
      swiftcode: [sellerOrBuyerBankDetail.swift_code ? sellerOrBuyerBankDetail.swift_code : '', Validators.required],
      reenterAccountNumber: [sellerOrBuyerBankDetail.account_number ? sellerOrBuyerBankDetail.account_number : '', Validators.required],
      address: [sellerOrBuyerBankDetail.bank_branch_address ? sellerOrBuyerBankDetail.bank_branch_address : '', Validators.required],
      pincode: [sellerOrBuyerBankDetail.pin_code ? sellerOrBuyerBankDetail.pin_code : '', Validators.required],
      city: [sellerOrBuyerBankDetail.city ? sellerOrBuyerBankDetail.city : '', Validators.required],
      state: [sellerOrBuyerBankDetail.province ? sellerOrBuyerBankDetail.province : '', Validators.required],
      country: [sellerOrBuyerBankDetail.country ? sellerOrBuyerBankDetail.country : '', Validators.required],
      streetAddress: [sellerOrBuyerBankDetail.address ? sellerOrBuyerBankDetail.address : '', Validators.required],
      codeAccordingCountry: [sellerOrBuyerBankDetail.bank_branch_code ? sellerOrBuyerBankDetail.bank_branch_code : ''],
      organisationBankId: sellerOrBuyerBankDetail.id ? sellerOrBuyerBankDetail.id : '',
      counterPartyBankDetails: beneficiaryType === 'cp' ? true : false,
      sponsorBankDetails: beneficiaryType === 'sponsor' ? true : false,
      addressId: sellerOrBuyerBankDetail.addressId ? sellerOrBuyerBankDetail.addressId : '',
      email: sellerOrBuyerBankDetail.email ? sellerOrBuyerBankDetail.email : '',
      contact: sellerOrBuyerBankDetail.contact ? sellerOrBuyerBankDetail.contact : '',
      benificiaryName: sellerOrBuyerBankDetail.benificiaryName ? sellerOrBuyerBankDetail.benificiaryName : '',
      iban: sellerOrBuyerBankDetail.iban ? sellerOrBuyerBankDetail.iban : ''
    });
  }

  removeSpecialCharacters(value: string): string {
    if (value) {
      const modifiedValue = value.replace(/[^a-zA-Z0-9 ]/g, " ");
      return modifiedValue;
    }
    return value;
  }

  setOcrAnalysisRawData(ocrData: any): void {
    if (Array.isArray(ocrData)) {
      this.ocrAnalysisRawData.push(...ocrData);
    } else {
      this.ocrAnalysisRawData.push(ocrData);
    }
  }

  getOcrAnalysisRawData(): any {
    return this.ocrAnalysisRawData;
  }

  /**
   * finance-ocr-data accepts one or more `files` parts in a single request and returns one
   * combined response, so every caller builds its request the same way regardless of how
   * many files are in the batch.
   */
  buildOcrRequestFormData(files: File[], fileType: 'INVOICES' | 'BILL_OF_LADING' = 'INVOICES'): FormData {
    const formData = new FormData();
    formData.append('fileType', fileType);
    files.forEach((file) => formData.append('files', file, file.name));
    return formData;
  }

  findOcrResultForFile(ocrResponse: any, fileName: string): any {
    return ocrResponse?.results?.find((result) => result.filename === fileName);
  }

  /**
   * Normalizes finance-ocr-data's `predictions` object (`{ [label]: { value, score,
   * bounding_box } }`, keyed by field name - not an array) into a `{ [label]: { value, score } }`
   * map. Some stray non-field entries (fileName, fileType) come through as plain strings on the
   * same object rather than { value, score } - those are skipped.
   */
  mapOcrPredictionsToFieldMap(predictions: Record<string, any>): Record<string, { value: string; score: number }> {
    const fieldMap: Record<string, { value: string; score: number }> = {};
    Object.entries(predictions || {}).forEach(([label, prediction]) => {
      if (prediction && typeof prediction === 'object' && prediction.value !== undefined) {
        fieldMap[label] = { value: prediction.value, score: prediction.score };
      }
    });
    return fieldMap;
  }

  // Keep this in sync with OCR_PREFILLED_FIELD_LABELS above - it's the canonical list this
  // method's own fieldMap.<label> reads are meant to mirror.
  applyOcrFieldMapToInvoiceGroup(group: UntypedFormGroup, fieldMap: Record<string, { value: string; score: number }>, invoiceCurrencyControl?: AbstractControl | null): void {
    if (!group || !fieldMap) {
      return;
    }
    if (invoiceCurrencyControl && fieldMap.currency?.value) {
      invoiceCurrencyControl.setValue(fieldMap.currency.value);
    }
    if (fieldMap.invoice_number?.score > 0.7 && fieldMap.invoice_number?.value) {
      group.get('invoiceId')?.setValue(fieldMap.invoice_number.value);
    }
    if (fieldMap.payment_due_date?.score > 0.7 && fieldMap.payment_due_date?.value) {
      group.get('invoiceDueDate')?.setValue(this.stringToDate(fieldMap.payment_due_date.value));
    }
    if (fieldMap.invoice_date?.score > 0.7 && fieldMap.invoice_date?.value) {
      group.get('invoiceDate')?.setValue(this.stringToDate(fieldMap.invoice_date.value));
    }
    if (fieldMap.invoice_amount?.score > 0.7 && fieldMap.invoice_amount?.value) {
      group.get('invoiceAmount')?.setValue(parseFloat(fieldMap.invoice_amount.value.replace(',', '')));
    }
    if (fieldMap.net_d?.score > 0.7 && fieldMap.net_d?.value) {
      group.get('invoiceTerms')?.setValue(fieldMap.net_d.value);
    }
  }

  // Keep this in sync with OCR_BOL_PREFILLED_FIELD_LABELS above - same purpose as
  // applyOcrFieldMapToInvoiceGroup, for a finance-ocr-data call made with fileType:
  // 'BILL_OF_LADING' instead. hblNumber is only ever prefilled when the caller says the
  // product's TRACKING_INFORMATION/HBL_NO control is actually visible for this product -
  // mirrors the same prodConfig.validateProductControl check the pre-existing (removed)
  // prefillOcrBillInfo used to make inline.
  applyOcrFieldMapToBillOfLadingGroup(mblGroup: UntypedFormGroup, fieldMap: Record<string, { value: string; score: number }>, includeHblNumber = false): void {
    if (!mblGroup || !fieldMap) {
      return;
    }
    if (fieldMap.mbl_no?.score > 0.7 && fieldMap.mbl_no?.value) {
      mblGroup.get('mblNumber')?.setValue(fieldMap.mbl_no.value);
    }
    if (fieldMap.container_no?.score > 0.7 && fieldMap.container_no?.value) {
      const containerNumbers = mblGroup.get('containerNumbers') as UntypedFormArray;
      // finance-ocr-data returns every container as one comma-separated string; the form has one
      // control per container (see TrackingInfoComponent.addContainerFormGroup), so each one is
      // split into its own control - reusing the first (always-present) row and pushing a new
      // control for each container beyond it.
      if (containerNumbers) {
        const containerValues = fieldMap.container_no.value.split(',').map((value) => value.trim()).filter((value) => !!value);
        containerValues.forEach((value, index) => {
          if (index < containerNumbers.length) {
            containerNumbers.at(index).setValue(value);
          } else {
            containerNumbers.push(new UntypedFormControl(value));
          }
        });
      }
    }
    if (fieldMap.carrier?.score > 0.7 && fieldMap.carrier?.value) {
      mblGroup.get('carrierName')?.setValue(fieldMap.carrier.value);
    }
    if (fieldMap.booking_no?.score > 0.7 && fieldMap.booking_no?.value) {
      mblGroup.get('bookingNumber')?.setValue(fieldMap.booking_no.value);
    }
    if (fieldMap.shipment_reference?.score > 0.7 && fieldMap.shipment_reference?.value) {
      mblGroup.get('shipmentReference')?.setValue(fieldMap.shipment_reference.value);
    }
    if (includeHblNumber && fieldMap.hbl_number?.score > 0.7 && fieldMap.hbl_number?.value) {
      mblGroup.get('hblNumber')?.setValue(fieldMap.hbl_number.value);
    }
  }

  stringToDate(dateStr: string): Date {
    let splitChar = '-';
    if (dateStr.includes('/')) {
      splitChar = '/';
    }
    let [day, month, year] = dateStr.split(splitChar);
    if (year && year.length !== 4) {
      year = '20' + year.substr(-2);
    }
    return new Date(+year, +month - 1, +day);
  }

  setFiltersToStorage(filtersObj: any, filtersAppliedOn: string): void {
    localStorage.setItem(filtersAppliedOn, JSON.stringify(filtersObj));
  }

  getStoredFilters(filtersAppliedOn: string): any {
    const filtersObj = localStorage.getItem(filtersAppliedOn);
    return filtersObj ? JSON.parse(filtersObj) : '';
  }

  clearFiltersInStorage(filtersAppliedOn: string): void {
    localStorage.removeItem(filtersAppliedOn);
  }

  setFiAssessedState(isLendingOrg: boolean): void {
    this.fiAssessedState = isLendingOrg ? 'Loan Documents Issued' : 'Financial Institution Assessed';
  }

  validateActiveUserForState(state: string, activeUser: string, userDept: string): boolean {
    if (!state || !activeUser || !userDept) {
      return false;
    }
    return activeUser === userDept;
  }

  isCFNotification(notification_type: string): boolean {
    return notification_type?.includes('CREDIT_LIMIT') || notification_type?.includes('CREDIT_FACILITY');
  }

  downloadFile(apiResult, fileName: string): void {
    const csvDownloadAnchor: HTMLAnchorElement = document.createElement('a') as HTMLAnchorElement;
    const templateURL = URL.createObjectURL(apiResult);
    csvDownloadAnchor.href = templateURL;
    csvDownloadAnchor.download = fileName;
    csvDownloadAnchor.click();
    URL.revokeObjectURL(templateURL);
  }

  getCommifiedValue(value, inputCondition?: string, inputData?: any) {
    let decimalPoints = 2;
    if (inputData?.isDisabled && inputData?.inputType?.toString().split('_')?.length > 1) {

      decimalPoints = Number(inputData?.inputType.split('_')[1]);
    }
    const isNumber = Number(value) > 0;
    const isDecimalNumber = (isNumber && typeof (value) === 'string') ? value.includes('.') : false
    if (isDecimalNumber || inputCondition?.length) {
      const converToNum = inputData?.noDecimalChange ? value : Number(value);
      let fixingDigit = inputData?.noDecimalChange ? converToNum : (inputCondition === 'restrictDecimal' ? converToNum.toFixed(0) : converToNum.toFixed(decimalPoints));
      const parts = fixingDigit.toString().split('.');
      if (parts.length) {
        const formattedValue = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        parts[0] = formattedValue;
        fixingDigit = parts.join('.');
      }
      return fixingDigit;
    }
    return value;
  }

  handleLimitUpdateEventForCp(event, parentFormGroup: UntypedFormGroup, authService: AuthService, counterPartyData, creditLimitAcceptanceForm?: UntypedFormGroup): void {
    const combinedIds = '' + parentFormGroup.get('buyer_id')?.value + (parentFormGroup.get('sourceReferenceId')?.value ? parentFormGroup.get('sourceReferenceId').value : '');
    const counterPartyInfo = counterPartyData[combinedIds] ? counterPartyData[combinedIds] : counterPartyData;
    switch (event?.parentFormControlName ? event.parentFormControlName : event.inputField.formControlName) {
      case 'upgradeLimit': {
        switch (event.actionType) {
          case 'value': {
            return authService.latestValueSubject.next(parentFormGroup.get('upgradeLimit')?.value);
          }
          case 'keyup': {
            return this.setFieldValue(event, parentFormGroup);
          }
          case 'focusout': {
            parentFormGroup.get('limit')?.setValue(parentFormGroup.get('upgradeLimit').value);
            this.counterPartyValidator(creditLimitAcceptanceForm, parentFormGroup, 'limit');
            return parentFormGroup.get('upgradeLimit').markAllAsTouched();
          }
          case 'showError': {
            if (this.checkFieldError(event, parentFormGroup, authService)) {
              const errorMessages: { [key: string]: string } = {
                pattern: 'Only Numbers upto 2 decimals allowed.',
                required: 'Upgrade credit limit request amount is required.',
                siblingMax: 'Approved Limit at Counterparty level cannot be higher than Board Approved limit at Counterparty level.',
                parentMax: 'Approved Limit at Counterparty level cannot be higher than Approved limit at product level.',
                customMax: 'Approved Limit at Product/Counterparty level cannot be higher than Approved limit at master level.'
              };
              const errors = parentFormGroup.get('upgradeLimit')?.errors;
              if (errors) {
                this.setInputFieldValue(event?.indexes, 'errorMessage', errorMessages[Object.keys(errors)[0]], counterPartyInfo);
              }
            }
            return;
          }
        }
        break;
      }
      case 'usableLimit': {
        switch (event.actionType) {
          case 'value': {
            return authService.latestValueSubject.next(parentFormGroup.get('usableLimit')?.value ? parseInt(parentFormGroup.get('usableLimit')?.value.replace(/,+/g, '')) : '');
          }
          case 'keyup': {
            return this.setFieldValue(event, parentFormGroup);
          }
          case 'focusout': {
            this.counterPartyValidator(creditLimitAcceptanceForm, parentFormGroup, 'usableLimit');
            return parentFormGroup.get('usableLimit').markAllAsTouched();
          }
          case 'showError': {
            if (this.checkFieldError(event, parentFormGroup, authService)) {
              const errorMessages: { [key: string]: string } = {
                pattern: 'Only Numbers upto 2 decimals allowed.',
                required: 'Usable limit is required.',
                siblingMax: 'Usable limit cannot be higher than Approved Credit limit.',
                parentMax: 'Usable limit at Counterparty level cannot be higher than Usable limit at product level.',
                customMax: 'Usable limit at Product/Counterparty level cannot be higher than Usable limit at master level.'
              };
              const errors = parentFormGroup.get('usableLimit')?.errors;
              if (errors) {
                this.setInputFieldValue(event?.indexes, 'errorMessage', errorMessages[Object.keys(errors)[0]], counterPartyInfo);
              }
            }
            return;
          }
        }
        break;
      }
      case 'riskRating': {
        switch (event.actionType) {
          case 'value': {
            return authService.latestValueSubject.next(parentFormGroup.get('riskRating')?.value);
          }
          case 'selectionChange': {
            return this.setFieldValue(event, parentFormGroup);
          }
        }
        break;
      }
      case 'enforceGroupLimit': {
        switch (event.actionType) {
          case 'value': {
            return authService.latestValueSubject.next(parentFormGroup.get('enforceGroupLimit')?.value);
          }
          case 'click': {
            event.value = event?.event?.checked;
            return this.setFieldValue(event, parentFormGroup);
          }
        }
        break;
      }
      case 'commodities': {
        switch (event.actionType) {
          case 'value': {
            return authService.latestValueSubject.next(parentFormGroup.get('commodities')?.value);
          }
          case 'optionSelected': {
            const idx = parentFormGroup.get('commodities')?.value.length ? (parentFormGroup.get('commodities').value).findIndex(comm => comm.name === 'Other') : -1;
            if (idx !== -1) {
              if (!parentFormGroup.get('otherCommodities')) {
                this.commonUtilsService.addOtherCommodityControl(parentFormGroup);
                this.addOtherCommoditiesField(counterPartyInfo, event);
              }

            } else {
              if (parentFormGroup.get('otherCommodities')) {
                parentFormGroup.removeControl('otherCommodities');
                this.deleteOtherCommoditiesField(counterPartyInfo, event);
              }
            }
            return;
          }
          case 'focusout': {
            return parentFormGroup.get('commodities').markAllAsTouched();
          }
          case 'showError': {
            this.checkFieldError(event, parentFormGroup, authService);
            return;
          }
        }
        break;
      }
      case 'otherCommodities': {
        const commInd = Math.floor(event.indexes[event.indexes.length - 1] / 2);
        const control = parentFormGroup.get('otherCommodities')['controls'][commInd];
        switch (event.actionType) {
          case 'value': {
            return authService.latestValueSubject.next(control.value);
          }
          case 'change': {
            return control.setValue(event.value);
          }
          case 'focusout': {
            return control.markAllAsTouched();
          }
          case 'showError': {
            const val = control?.touched && (control?.status === 'INVALID');
            return authService.latestValueSubject.next(val);
          }
        }
        break;
      }
      case 'deleteBtn': {
        this.deleteOtherCommoditiesField(counterPartyInfo, event);
        const commInd = event.indexes[event.indexes.length - 1] / 2;
        this.commonUtilsService.onDeleteOtherCommodity(parentFormGroup, commInd);
        break;
      }
      case 'addBtn': {
        this.addOtherCommoditiesField(counterPartyInfo, event);
        this.commonUtilsService.otherCommoditySelected(true, parentFormGroup);
        break;
      }
      case 'riskParticipationValue':
      case 'riskParticipationType':
      case 'participationType':
      case 'riskParticipant':
      case 'riskOriginator': {
        this.handleAdvanceFacilityFieldsEmitEvent(event, parentFormGroup, authService);
        break;
      }
      default: {
      }
    }
  }

  getCommodityWithClass(eventValue): any {
    const approvedCommodities = this.getCommodityViewValue(eventValue, true, 'master');
    const requestedCommodities = this.getCommodityViewValue(eventValue, false);
    const value: any = [];
    for (let element of requestedCommodities) {
      if (!approvedCommodities.includes(element)) {
        value.push({ value: element, class: 'green-highlight' })
      } else {
        value.push({ value: element, class: '' })
      }
    }
    return value;
  }

  getCommodityViewValue(eventValue, approved: boolean, type?: string): any {
    const commArr = approved ? (type === 'master' ? eventValue?.masterApprovedCommodities : eventValue?.approvedCommodities) : eventValue?.requestedCommodities;
    const otherCommArr = approved ? (type === 'master' ? eventValue?.masterApprovedOtherCommodities : eventValue?.approvedOtherCommodities) : eventValue?.requestedOtherCommodities;
    const value: any = [];
    commArr?.forEach(element => {
      if (!element.includes('Other:') && (element !== 'Other')) value.push(element);
    });
    otherCommArr?.forEach(element => {
      value.push('Other: ' + element);
    });
    return value;
  }

  deleteOtherCommoditiesField(metaData, event): void {
    metaData = metaData[event.indexes[0]].content;
    const ind = event.indexes[event.indexes.length - 1];
    if (event.inputField.formControlName !== 'deleteBtn') {
      metaData.splice(ind + 1, 1);
    } else {
      metaData = metaData[event.indexes[1]].content[event.indexes[2]].content;
      metaData.splice(ind - 1, 2);
    }
  }

  addOtherCommoditiesField(metaData, event): void {
    const commMetaData = {
      fieldType: 'inputType',
      focusout: { methodCall: 'conditionCheck' },
      value: { methodCall: 'conditionCheck' },
      label: 'Other Commodities',
      change: { methodCall: 'conditionCheck' },
      formControlName: 'otherCommodities',
      required: true,
      showError: { methodCall: 'conditionCheck' },
      errorMessage: 'Please enter Other Commodities',
    }
    const addActionBtnData = {
      fieldType: 'simpleType',
      matIcon: 'add_circle_outline',
      label: '',
      click: { methodCall: 'conditionCheck' },
      class: 'outline-btn',
      width: 20,
      formControlName: 'addBtn',
    }

    const deleteActionBtnData = {
      fieldType: 'simpleType',
      matIcon: 'delete',
      label: '',
      click: { methodCall: 'conditionCheck' },
      class: 'outline-btn',
      width: 10,
      formControlName: 'deleteBtn',
    }
    metaData = metaData[event.indexes[0]]?.content ? metaData[event.indexes[0]].content : metaData;
    let ind;
    if (event.indexes.length > 2) {
      metaData = metaData[event.indexes[1]].content[event.indexes[2]].content;
      ind = event.indexes[3];
      metaData.splice(ind, 0, commMetaData);
      metaData.splice(ind + 1, 0, deleteActionBtnData);
    } else {
      ind = event.indexes[1] + 1;
      const content = {
        content: [
          {
            width: 25,
            content: [
              commMetaData, deleteActionBtnData, addActionBtnData
            ]
          }
        ]
      }

      metaData.splice(ind, 0, content);
    }
  }

  setFieldValue(event, parentFormGroup, control?: any): void {
    const form = control ? control : parentFormGroup;
    form.get(event.inputField.formControlName)?.setValue(event.value);
    form.get(event.inputField.formControlName)?.markAsTouched();
  }

  checkFieldError(event, parentFormGroup: UntypedFormGroup, authService: AuthService, control?: any): boolean {
    const form = control ? control : parentFormGroup;
    const val = form.get(event.inputField.formControlName)?.touched && (form.get(event.inputField.formControlName)?.status === 'INVALID');
    authService.latestValueSubject.next(val);
    return val;
  }

  setInputFieldValue(indexes: any[], actionType: string, value: any, counterPartyData): void {
    if (!indexes?.length) return;
    let tempObj: {} = counterPartyData;
    indexes.forEach(ind => {
      tempObj = tempObj[ind] ? tempObj[ind] : (tempObj['content'] ? tempObj['content'][ind] : '');
    });
    if (tempObj?.hasOwnProperty(actionType)) {
      tempObj[actionType] = value;
    }
  }

  processCPUpload(evt: any, callBackFunc: Function, uploadCategory: string, authService: any): any {
    const target: DataTransfer = <DataTransfer>(evt.target);
    if (target.files.length > 1) {
      this.ui.warningDialog(this.constants.cpBulkUploadErrorHandler.MULTIPLE_FILE_UPLOAD);
      authService.spinnerSubject.next(false);
      return;
    }
    if (!this.validateDocUploadByDocNames(target.files[0], environment.BULK_UPLOAD_FI_DATA_DOCUMENT_TYPES)) {
      authService.spinnerSubject.next(false);
      return;
    }
    const reader: FileReader = new FileReader();
    reader.onload = (e: any) => {

      const bstr: string = e.target.result;
      const wb: any = XLSX.read(bstr, { type: 'binary' });
      callBackFunc(target.files[0], wb, uploadCategory);
    };
    reader.readAsBinaryString(target.files[0]);
  }

  validateDocUploadByDocNames(inputFile: File, allowedList) {
    const BYTES_IN_MB = 1024 * 1024;
    const supportedFileExtensions = allowedList.ALLOWED_DOCUMENT_EXTENSIONS;
    const maxFileSizeAllowed = allowedList.ALLOWED_DOCUMENT_SIZE_MBS;

    const splitNames = inputFile.name.split('.');
    // not supporting multiple extensions like - "abc.pdf.docx"
    let file_extension_count = 0
    for (const splitName of splitNames) {
      if (supportedFileExtensions.includes(splitName)) {
        file_extension_count += 1;
      }
    }
    if (file_extension_count > 1) {
      this.ui.warningDialog(
        `Multiple file extensions are not allowed for document type - ${inputFile.name}`
      );
      return false;
    } else if (splitNames.length === 1) {
      this.ui.warningDialog(
        `Unknown file extensions are not allowed for document type - ${inputFile.name}`
      );
      return false;
    }

    const fileSizeInMBs = inputFile.size / BYTES_IN_MB;
    if (fileSizeInMBs > maxFileSizeAllowed) {
      this.ui.warningDialog(
        `Maximum file size allowed is: ${maxFileSizeAllowed} MBs` +
        ` for document type - ${inputFile.name}`
      );
      return false;
    }

    if (supportedFileExtensions.length) {
      const fileExtension = splitNames[splitNames.length - 1];
      if (!supportedFileExtensions.includes(fileExtension.toLowerCase())) {
        const noOfFileExtSpecificMsg = supportedFileExtensions.length === 1
          ? supportedFileExtensions.join()
          : `one of - [${supportedFileExtensions.join()}]`;
        this.ui.warningDialog(
          `Input file extension should be ${noOfFileExtSpecificMsg} for document type - ${inputFile.name}`
        );
        return false;
      }
    }
    return true;
  }

  encodeCell(row, col) {
    return XLSX.utils.encode_cell({ r: row, c: col });
  }

  deleteRow(ws, row_index: number): void {
    const variable = XLSX.utils.decode_range(ws["!ref"]);
    for (let R = row_index; R < variable.e.r; ++R) {
      for (let C = variable.s.c; C <= variable.e.c; ++C) {
        ws[this.encodeCell(R, C)] = ws[this.encodeCell(R + 1, C)];
      }
    }
    variable.e.r--
    ws['!ref'] = XLSX.utils.encode_range(variable.s, variable.e);
  }

  excelDateToJSDate(excelDate) {
    if (excelDate && (excelDate.toString().includes('-') || excelDate.toString().includes('/'))) {
      return excelDate;
    }
    const date = new Date(Math.round((excelDate - 25569) * 86400 * 1000));
    if (date) {
      return (date.getMonth() + 1) + '-' + date.getDate() + '-' + date.getFullYear();
    } else {
      return excelDate;
    }
  }

  async handleAndConvertBulkUploadCpCsvToJson(file, workBook: any, uploadCategory: string, countryList: any[], commoditiesList?: any[], applyCreditFacilityService?: any, countryCodes?: any[], calledFromApplyFacility?: boolean,
    allowAdvancePayment?: boolean, inputConditionDetails?: any
  ) {
    const errors: string[] = [];
    const productType = this.prodConfig.getProductTypeAbbreviationSnakeUC(uploadCategory);
    const { cpIdRfTpBulkUploadHeadersFormat, cpIfBulkUploadHeadersFormat } = this.buildBulkUploadHeadersFormat(
      productType,
      calledFromApplyFacility,
      allowAdvancePayment
    );
    let errorFileHeader;
    type AOA = any[][];
    const wsName: string = workBook.SheetNames[0];
    const workSheet: any = workBook.Sheets[wsName];
    const data = <AOA>(XLSX.utils.sheet_to_json(workSheet, { header: 1 }));
    if (data.length <= 1) {
      this.ui.warningDialog(this.constants.cpBulkUploadErrorHandler.EMPTY_FILE_UPLOADED);
      return;
    }
    const productCategory = this.prodConfig.getCategoryAbbreviationSnakeUC(uploadCategory);
    // const productType = this.prodConfig.getProductTypeAbbreviationSnakeUC(uploadCategory);
    const applicantToCp = this.prodConfig.isDisbursementToApplicantAndCollectedFromCp(productCategory, productType);
    if (applicantToCp) {
      if (JSON.stringify(data[0]) !== JSON.stringify(Object.keys(cpIfBulkUploadHeadersFormat))) {
        this.ui.warningDialog(this.constants.cpBulkUploadErrorHandler.WRONG_FORMAT_FILE_UPLOADED);
        return;
      }
      errorFileHeader = Object.keys(cpIfBulkUploadHeadersFormat).concat("Error");
    } else {
      if (JSON.stringify(data[0]) !== JSON.stringify(Object.keys(cpIdRfTpBulkUploadHeadersFormat))) {
        this.ui.warningDialog(this.constants.cpBulkUploadErrorHandler.WRONG_FORMAT_FILE_UPLOADED);
        return;
      }
      errorFileHeader = Object.keys(cpIdRfTpBulkUploadHeadersFormat).concat("Error");
    }
    const errorFileContent = [errorFileHeader];
    const cpDataArr = [];
    for (let rowCount = 1; rowCount < data.length; rowCount++) {
      const row = data[rowCount];
      //let errorCode;
      let cpData: any = {};
      if (applicantToCp) {
        const fields: any = Object.values(cpIfBulkUploadHeadersFormat);
        for (let idx = 0; idx < fields.length; idx++) {
          if (typeof row[idx] === 'string') {
            row[idx] = row[idx].trim();
          }
          cpData[fields[idx]] = row[idx];
        }
        const ifError = await this.validateCounterPartyForIF(cpData, countryList, commoditiesList, applyCreditFacilityService, countryCodes, inputConditionDetails);
        if (ifError) errors.push(ifError);
      } else {
        Object.values(cpIdRfTpBulkUploadHeadersFormat).forEach((rowKey: any, index) => {
          if (typeof row[index] === 'string') {
            row[index] = row[index].trim();
          }

          switch (rowKey) {
            case 'commodities': {
              const rawCommodityStr = row[index] ? String(row[index]).trim() : '';
              cpData['commodities'] = rawCommodityStr
                ? rawCommodityStr.split(',').map((c: string) => c.trim()).filter((c: string) => c.length > 0)
                : [];
              break;
            }

            case 'countries_operate_in': {
              cpData['countries_operate_in'] = row[index];
              if (cpData['countries_operate_in']) {
                const countriesOperateInValue = this.returnCountriesArray(cpData['countries_operate_in'], countryList, 'COUNTRIES_OPERATE_IN');
                if (typeof countriesOperateInValue !== 'string') {
                  cpData['countries_operate_in'] = countriesOperateInValue;
                } else {
                  errors.push(countriesOperateInValue);
                }
              }
              break;
            }

            case 'contact_number': {
              if (row[index]) {
                const contactNo = row[index] + '';
                if (contactNo) {
                  if (!this.constants.phoneReg.test(contactNo)) {
                    errors.push(this.constants.cpBulkUploadErrorHandler.CONTACT_NUMBER_INVALID);
                  } else {
                    cpData['contact_number'] = contactNo;
                  }
                }
              }
              break;
            }

            case 'allowAdvancePayment':
            case 'enforceGroupLimit': {
              const boolValue = typeof row[index] === 'string' ? row[index].toLowerCase() : row[index];
              cpData[rowKey] = boolValue === true || boolValue === 'true' || boolValue === 'yes';
              break;
            }

            case 'taxIdentificationNumber':
            case 'licenceNumber':
            case 'peppolId':
            case 'pin_code':
            case 'country_code':
            case 'percentageSalesPurchases': {
              if (row[index]) {
                cpData[rowKey] = row[index] + '';
              }
              break;
            }

            case 'supplierWebsite': {
              if (row[index]) {
                if (typeof row[index] === 'string' && row[index].toLowerCase() === 'available') {
                  cpData[rowKey] = row[index + 1] ? row[index + 1] : '';
                } else {
                  cpData[rowKey] = '';
                }
              }
              break;
            }

            default: {
              cpData[rowKey] = row[index];
              break;
            }
          }
        });
        const countryVal = typeof cpData['country'] === 'string' ? cpData['country'] : '';
        const countryIdx = countryList.findIndex((country) => country.country_name.toLowerCase() === countryVal.toLowerCase());
        if (countryIdx === -1 && countryVal !== '') {
          errors.push(this.constants.cpBulkUploadErrorHandler.COUNTRY_MISSING);
        }
        const invalidCommodity = (cpData['commodities'] ?? []).find(
          (name: string) => !commoditiesList?.some(
            (c: any) => c.name?.toLowerCase() === name.toLowerCase()
          )
        );
        if (invalidCommodity) {
          errors.push(this.constants.cpBulkUploadErrorHandler.COMMODITY_INVALID);
        }
        if (countryIdx !== -1) {
          cpData['country'] = countryList[countryIdx].country_code;
        }

        if (countryIdx !== -1) {
          cpData['country'] = countryList[countryIdx].country_code;
        }

        const stateCityError = await this.validateStateCityFields(cpData, applyCreditFacilityService, false);
        if (stateCityError) errors.push(stateCityError);

        const commonError = this.validateCommonFields(cpData, countryList, inputConditionDetails);
        if (commonError) errors.push(commonError);


        const isFullForm = this.prodConfig.getGivenKeyValueForCreditFacilityConfig(
          productType,
          'counterPartyComponentsConfig',
          'fullFormTypeAllowed'
        ) && allowAdvancePayment;

        if (isFullForm) {
          const fullFormError = await this.validateFullFormFields(cpData, countryList, countryCodes);
          if (fullFormError) errors.push(fullFormError);
        }

      }
      const errorCode = errors.length > 0 ? `Invalid or missing values in following columns: ${errors.join(', ')}` : '';
      const rowTextCombined = applicantToCp ? row.slice(0, 15).join('') : row.slice(0, 9).join('');
      rowTextCombined.replace(/(\r\n|\n|\r|\s)/gm, '')
      if (rowTextCombined === '') {
        this.deleteRow(workSheet, rowCount);
        continue;
      }
      if (errorCode) {
        for (let i = row.length; i < errorFileHeader.length - 1; i++) {
          row.push("");
        }
        row.push(errorCode);
        errorFileContent.push(row);
        this.deleteRow(workSheet, rowCount);
        this.stripErrorFields(cpData, errorCode);
      }
      cpDataArr.push(cpData);
    }
    if (errorFileContent.length > 1) {
      const errorWs: any = XLSX.utils.aoa_to_sheet(errorFileContent);
      const errorWb: any = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(errorWb, errorWs, 'Sheet1');
      XLSX.writeFile(errorWb, "Format_Error_File" + ".csv");
    }
    return cpDataArr;
  }

  private stripErrorFields(cpData: any, errorCode: string): void {
    const fields = this.errorFieldMap[errorCode] ?? [];
    fields.forEach(f => delete cpData[f]);
  }

  downloadCounterParyBulkUploadSample(uploadCategory: string, calledFromApplyFacility?: boolean, allowAdvancePayment?: boolean): void {
    const productCategory = this.prodConfig.getCategoryAbbreviationSnakeUC(uploadCategory);
    const productType = this.prodConfig.getProductTypeAbbreviationSnakeUC(uploadCategory);
    const { cpIdRfTpBulkUploadHeadersFormat, cpIfBulkUploadHeadersFormat } = this.buildBulkUploadHeadersFormat(
      productType,
      calledFromApplyFacility,
      allowAdvancePayment
    );
    const dataToExcel = [];
    if (this.prodConfig.isDisbursementToApplicantAndCollectedFromCp(productCategory, productType)) {
      dataToExcel.push(Object.keys(cpIfBulkUploadHeadersFormat));
    } else {
      dataToExcel.push(Object.keys(cpIdRfTpBulkUploadHeadersFormat));
    }
    dataToExcel.push([]);
    const counterPartyWs: any = XLSX.utils.aoa_to_sheet(dataToExcel);
    const counterPartyWb: any = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(counterPartyWb, counterPartyWs, 'Sheet1');
    XLSX.writeFile(counterPartyWb, "Counter_Party_Upload_Format" + ".csv");
  }

  returnCountriesArray(countriesVal: any, countryList: any, fieldName?: string): any {
    let errorStr = '';
    if (countriesVal.includes(',')) {
      const countryStr = countriesVal.replace(/[,]+/g, ',').trim();
      countriesVal = countryStr.split(',')?.map((item) => item.trim());
    } else {
      countriesVal = [countriesVal];
    }
    const countryCodesOperatesIn: string[] = [];
    for (let countryIdx = 0; countryIdx < countriesVal.length; countryIdx++) {
      const country = countriesVal[countryIdx];
      const foundCountry = countryList.find((item) => {
        return item.country_name.toLowerCase() === country?.trim().toLowerCase();
      });
      if (!foundCountry) {
        errorStr = this.constants.cpBulkUploadErrorHandler['COUNTRY_INVALID' + (fieldName ? '_' + fieldName.toUpperCase() : '')];
        return errorStr;
      } else {
        countryCodesOperatesIn.push(foundCountry.country_code);
      }
    }
    return countryCodesOperatesIn;
  }

  trimIfString = (value: unknown) => typeof value === 'string' ? value.trim() : value;

  async validateCounterPartyForIF(cpData, countryList: any[], commoditiesList: any[], applyCreditFacilityService: any, countryCodes: any[],
    inputConditionDetails: any
  ) {
    const errors: string[] = [];

    cpData['country'] = this.trimIfString(cpData['country']);
    const countryVal = typeof cpData['country'] === 'string' ? cpData['country'] : '';
    const countryIdx = countryList.findIndex((country) => country.country_name.toLowerCase() === countryVal.toLowerCase());
    if (countryIdx === -1 && countryVal !== '') {
      cpData['country'] = '';
      errors.push(this.constants.cpBulkUploadErrorHandler.COUNTRY_MISSING);
    } else if (countryIdx !== -1) {
      cpData['country'] = countryList[countryIdx].country_code;
    }

    const stateCityError = await this.validateStateCityFields(cpData, applyCreditFacilityService);
    if (stateCityError) errors.push(stateCityError);

    if (cpData['commodities']) {
      if (typeof cpData['commodities'] === 'string' && cpData['commodities']?.includes(',')) {
        const commodityStr = cpData['commodities'].replace(/[,]+/g, ',').trim();
        cpData['commodities'] = commodityStr.split(',')?.map((item) => item.trim());
      } else {
        cpData['commodities'] = [cpData['commodities']];
      }
      for (let commodityIdx = 0; commodityIdx < cpData['commodities'].length; commodityIdx++) {
        const findCommodityIndex = (commoditiesList ?? []).findIndex((item) => {
          const commodity = typeof cpData['commodities'][commodityIdx] === 'string' ? cpData['commodities'][commodityIdx].trim() : '';
          return item.name?.toLowerCase() === commodity?.toLowerCase();
        });
        if (findCommodityIndex === -1) {
          errors.push(this.constants.cpBulkUploadErrorHandler.COMMODITY_INVALID);
        }
      }
    }

    const commonError = this.validateCommonFields(cpData, countryList, inputConditionDetails);
    if (commonError) errors.push(commonError);

    const fullFormError = await this.validateFullFormFields(cpData, countryList, countryCodes);
    if (fullFormError) errors.push(fullFormError);

    // Process remaining keys (passportIssuingCountryCode, companyBusinessType, etc.)
    const keysToProcess = [
      'allowAdvancePayment',
      'enforceGroupLimit',
      'taxIdentificationNumber',
      'licenceNumber',
      'peppolId',
      'pin_code',
      'country_code',
      'percentageSalesPurchases'
    ];

    keysToProcess.forEach((key) => {
      if (cpData[key] === undefined || cpData[key] === null || cpData[key] === '') return;
      switch (key) {
        case 'allowAdvancePayment':
        case 'enforceGroupLimit': {
          cpData[key] = this.toBoolean(cpData[key]);
          break;
        }
        case 'taxIdentificationNumber':
        case 'licenceNumber':
        case 'peppolId':
        case 'pin_code':
        case 'country_code':
        case 'percentageSalesPurchases': {
          if (cpData[key]) {
            cpData[key] = cpData[key] + '';
          }
          break;
        }
      }
    });

    return errors.length > 0 ? errors.join(', ') : '';
  }

  async validateStateCityFields(cpData: any, applyCreditFacilityService: any, mandatory?: boolean): Promise<string> {
    let errorStr = '';

    if (cpData['country']) {
      const states = await this.getStatesOrCities(cpData['country'], applyCreditFacilityService, 'states', 'countries');
      if (states === 'error') {
        cpData['country'] = '';
        errorStr = this.constants.cpBulkUploadErrorHandler.COUNTRY_MISSING;
      } else {
        states.forEach((state) => {
          this.stateCodeList[state.name] = state.stateCode;
        });
        cpData['state'] = this.trimIfString(cpData['state']);
        cpData['stateArr'] = states;
        const stateVal = typeof cpData['state'] === 'string' ? cpData['state'] : '';
        const stateIdx = states.findIndex((item) => item.name.toLowerCase() === stateVal.toLowerCase());
        if (stateIdx === -1 && stateVal !== '') {
          cpData['state'] = '';
          if (mandatory) errorStr = this.constants.cpBulkUploadErrorHandler.STATE_MISSING;
        }
      }

      if (cpData['state']) {
        const cities = await this.getStatesOrCities(cpData['state'], applyCreditFacilityService, 'cities', 'states', cpData['country']);
        if (cities === 'error') {
          cpData['state'] = '';
          errorStr = this.constants.cpBulkUploadErrorHandler.STATE_INVALID;
        } else {
          cpData['city'] = this.trimIfString(cpData['city']);
          cpData['cityArr'] = cities;
          const cityVal = typeof cpData['city'] === 'string' ? cpData['city'] : '';
          const cityIdx = cities.findIndex((item) => item.name.toLowerCase() === cityVal.toLowerCase());
          if (cityIdx === -1 && cityVal !== '') {
            cpData['city'] = '';
            if (mandatory) errorStr = this.constants.cpBulkUploadErrorHandler.CITY_MISSING;
          }
        }
      } else {
        cpData['city'] = '';
      }
    } else {
      cpData['state'] = '';
      cpData['city'] = '';
    }

    return errorStr;
  }

  async validateFullFormFields(cpData: any, countryList: any[], countryCodes: any[]): Promise<string> {
    const errors: string[] = [];

    if (cpData['country_code']) {
      cpData['country_code'] = (cpData['country_code'] + '')?.trim();
      const countryCodeIdx = countryCodes.findIndex((codeInstance) => codeInstance.phone_code == cpData['country_code']);
      if (countryCodeIdx === -1) {
        errors.push(this.constants.cpBulkUploadErrorHandler.COUNTRY_CODE_INVALID);
      }
    }

    if (cpData['contact_number']) {
      const contactNo = cpData['contact_number'] + '';
      if (!this.constants.phoneReg.test(contactNo)) {
        errors.push(this.constants.cpBulkUploadErrorHandler.CONTACT_NUMBER_INVALID);
      } else {
        cpData['contact_number'] = contactNo;
      }
    }

    if (cpData['email']) {
      if (!this.constants.emailReg.test(cpData['email'])) {
        errors.push(this.constants.cpBulkUploadErrorHandler.EMAIL_INVALID);
      }
    }

    if (cpData['countries_operate_in']) {
      if (!Array.isArray(cpData['countries_operate_in'])) {
        if (typeof cpData['countries_operate_in'] === 'string' && cpData['countries_operate_in'].includes(',')) {
          const countryStr = cpData['countries_operate_in'].replace(/[,]+/g, ',').trim();
          cpData['countries_operate_in'] = countryStr.split(',')?.map((item) => item.trim());
        } else {
          cpData['countries_operate_in'] = [cpData['countries_operate_in']];
        }
        const countryCodesOperatesIn: string[] = [];
        for (let countryIdx = 0; countryIdx < cpData['countries_operate_in'].length; countryIdx++) {
          const foundCountry = countryList.find((item) => {
            const country = typeof cpData['countries_operate_in'][countryIdx] === 'string'
              ? cpData['countries_operate_in'][countryIdx].trim()
              : '';
            return item.country_name.toLowerCase() === country.toLowerCase();
          });
          if (!foundCountry) {
            errors.push(this.constants.cpBulkUploadErrorHandler.COUNTRY_INVALID_COUNTRIES_OPERATE_IN);
          } else {
            countryCodesOperatesIn.push(foundCountry.country_code);
          }
        }
        cpData['countries_operate_in'] = countryCodesOperatesIn;
      }
    }

    if (cpData['years_bco_dealing_with_commodity']) {
      const yearsBco = typeof cpData['years_bco_dealing_with_commodity'] === 'string' ? cpData['years_bco_dealing_with_commodity'] : '';
      const yrsBcoDealWithCommodityPresent = this.constants.importerDealingCommodity.findIndex(
        (item) => item.toLowerCase() === yearsBco.toLowerCase()
      );
      if (yrsBcoDealWithCommodityPresent === -1) {
        errors.push(this.constants.cpBulkUploadErrorHandler.INVALID_VALUE_YEARS_BCO_DEALING);
      }
    }

    if (cpData['years_of_relationship_with_bco']) {
      const yearsRel = typeof cpData['years_of_relationship_with_bco'] === 'string' ? cpData['years_of_relationship_with_bco'] : '';
      const yrsOfRelationWithBcoPresent = this.constants.importerDealingCommodity.findIndex(
        (item) => item.toLowerCase() === yearsRel.toLowerCase()
      );
      if (yrsOfRelationWithBcoPresent === -1) {
        errors.push(this.constants.cpBulkUploadErrorHandler.INVALID_VALUE_YEARS_OF_RELATIONSHIP);
      }
    }

    if (cpData['default_in_past_payments']) {
      const defaultPayments = typeof cpData['default_in_past_payments'] === 'string' ? cpData['default_in_past_payments'] : '';
      const isPaymentDefaultPresent = this.constants.delayOnPastPayments.findIndex(
        (item) => item.viewValue.toLowerCase() === defaultPayments.toLowerCase()
      );
      if (isPaymentDefaultPresent === -1) {
        errors.push(this.constants.cpBulkUploadErrorHandler.INVALID_VALUE_DEFAULT_IN_PAST_PAYMENTS);
      }
    }
    if (cpData['annual_turnover']) {
      const annualTurnover = typeof cpData['annual_turnover'] === 'string' ? cpData['annual_turnover'] : '';
      const isAnnualTurnoverPresent = this.constants.turnOver.findIndex(
        (item) => item.toLowerCase() === annualTurnover.toLowerCase()
      );
      if (isAnnualTurnoverPresent === -1) {
        errors.push(this.constants.cpBulkUploadErrorHandler.INVALID_VALUE_ANNUAL_TURNOVER);
      }
    }

    return errors.length > 0 ? errors.join(', ') : '';
  }

  validateCommonFields(cpData: any, countryList: any[], inputConditionDetails: any): string {
    const errors: string[] = [];

    if (cpData['companyBusinessType'] && typeof cpData['companyBusinessType'] === 'string') {
      const found = inputConditionDetails.businessTypeDropdownOptions
        .find((item: any) => item.value.toLowerCase() === cpData['companyBusinessType']?.trim().toLowerCase());
      if (!found) {
        errors.push(this.constants.cpBulkUploadErrorHandler.INVALID_VALUE_COMPANY_BUSINESS_TYPE);
      } else {
        cpData['companyBusinessType'] = { key: found.key, value: found.value };
      }
    }

    if (cpData['uaeFreeTradeZoneRegistered'] && typeof cpData['uaeFreeTradeZoneRegistered'] === 'string') {
      const found = inputConditionDetails.uaeFreeTradeZoneOptions
        .find((item: any) => item.value.toLowerCase() === cpData['uaeFreeTradeZoneRegistered']?.trim().toLowerCase());
      if (!found) {
        errors.push(this.constants.cpBulkUploadErrorHandler.INVALID_VALUE_UAE_FREE_TRADE_ZONE);
      } else {
        cpData['uaeFreeTradeZoneRegistered'] = this.toBoolean(found.key);
      }
    }

    if (cpData['passportIssuingCountryCode'] && typeof cpData['passportIssuingCountryCode'] === 'string') {
      const countriesOperateInValue = this.returnCountriesArray(cpData['passportIssuingCountryCode'], countryList, 'PASSPORT_ISSUING_COUNTRY_CODE');
      if (typeof countriesOperateInValue === 'string') {
        errors.push(countriesOperateInValue);
      } else {
        cpData['passportIssuingCountryCode'] = countriesOperateInValue[0];
      }
    }

    return errors.length > 0 ? errors.join(', ') : '';
  }

  private buildBulkUploadHeadersFormat(
    productType: string,
    calledFromApplyFacility: boolean,
    allowAdvancePayment: boolean
  ): { cpIdRfTpBulkUploadHeadersFormat: any; cpIfBulkUploadHeadersFormat: any } {

    const baseIdRfTp = this.constants.cpIdRfTpBulkUploadHeadersFormat;
    const baseIf = this.constants.cpIfBulkUploadHeadersFormat;

    const extraFields = calledFromApplyFacility
      ? {
        'Apply across group': 'applyAcrossGroup',
        'Trade License': 'tradeLicense',
        'Supplier Website': 'supplierWebsite',
        'Website Url': 'websiteUrl',
      }
      : {
        'Usable Limit': 'usableLimit',
        'Enforce Group Limit': 'enforceGroupLimit',
      };

    const advancePaymentField = allowAdvancePayment
      ? { 'Allow Advance Payment': 'allowAdvancePayment' }
      : {};

    const cpIfBulkUploadHeadersFormat = { ...baseIf, ...extraFields, ...advancePaymentField };

    const isFullForm = this.prodConfig.getGivenKeyValueForCreditFacilityConfig(
      productType,
      'counterPartyComponentsConfig',
      'fullFormTypeAllowed'
    ) && allowAdvancePayment;

    const cpIdRfTpBulkUploadHeadersFormat = isFullForm
      ? { ...cpIfBulkUploadHeadersFormat }
      : { ...baseIdRfTp, ...extraFields, ...advancePaymentField };

    return { cpIdRfTpBulkUploadHeadersFormat, cpIfBulkUploadHeadersFormat };
  }

  toBoolean = (val: any): boolean => {
    const value = typeof val === 'string' ? val.toLowerCase() : val;
    return value === true || value === 'true' || value === 'yes';
  };

  async getStatesOrCities(countryOrState: string, applyCreditFacilityService: any, type: string, parentType: string, countryCode?: string) {
    let statesOrCities = [];
    try {
      statesOrCities = await applyCreditFacilityService.getCountryCityAndStateDetail(type, countryOrState, parentType, this.stateCodeList, countryCode);
    } catch (e) {
      return 'error';
    }
    return statesOrCities;
  }

  getStringCommifiedValue(value) {
    if (typeof (value) === 'number') {
      value = value.toString() + '.00';
    }
    const converToNum = Number(value);
    if (converToNum === 0) {
      return 0;
    }
    if (converToNum) {
      const fixingDigit = converToNum.toFixed(2);
      return fixingDigit.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    }
    return value;
  }

  valueToDisplaySCF(value, field): string | number {
    if (!value && field.toLowerCase().includes('date')) return '--/--/--'
    if (value == 0 && field != 'tenure_above' && typeof (value) !== 'boolean') return value;
    if (!value && value != 0) return '--';
    if (field !== 'allowDisbursalDateChange' && (field.includes('date') || field.includes('Date') || field.toLowerCase().includes('from'))) {
      return this.datepipe.transform(getUTCDate(value), 'dd-MMM-yyyy');
    } else if (field === 'disbursal_terms') {
      return 'T + ' + value
    } else if (field === 'tenure_above') {
      const res = value === 0 ? 'Default' : 'Above ' + value + ' days'
      return res
    } else if (typeof (value) === 'number' && !field.includes('id') && !field.includes('number')) {
      return value.toLocaleString("en-US", { maximumFractionDigits: 2, minimumFractionDigits: 2 });
    } else if (typeof (value) === 'string' && !Number.isNaN(parseFloat(value)) && !field.includes('id') && !field.includes('number') && !field.includes('billToSiteCode')) {
      return parseFloat(value).toLocaleString("en-US", { maximumFractionDigits: 2, minimumFractionDigits: 2 });
    } else if (value === true) {
      return 'Yes'
    } else if (value === false) {
      return 'No'
    } else {
      return value;
    }
  }

  getCountrySuggestions(event, allCountryList): any[] {
    const filter = event;
    const filterValue = filter.toLowerCase();
    const filteredCountry = [];
    for (const location of allCountryList) {
      const lowerCaseCountry = location.toLowerCase();
      if (lowerCaseCountry.startsWith(filterValue) && filteredCountry.indexOf(lowerCaseCountry.startsWith(filterValue)) === -1) {
        filteredCountry.push(location);
      }
    }
    return filteredCountry;
  }

  handleClientNotificationNav(creditFacilityStatus: string, creditFacilityId: string, user: User, cfData: any, routeQueryParams: Params): { url: string, queryParams: any } {
    const queryParams = routeQueryParams ? Object.assign({}, routeQueryParams) : {};
    if (!queryParams['id']) {
      queryParams['id'] = creditFacilityId;
    }
    let navUrl = 'credit-limit/module-arranging/view-facility-client';
    if (user.organisation_type === 'ORG') {
      switch (creditFacilityStatus) {
        case CreditLimitStateType.ELIGIBILITY_REQUESTED:
          if (!queryParams['actionType']) {
            queryParams['actionType'] = 'CheckEligibility';
          }
          navUrl = 'credit-limit/module-arranging/view-facility-client';
          break;
        case CreditLimitStateType.TERM_SHEET_PUBLISHED:
          if (!queryParams['creditFacilityTab']) {
            queryParams['creditFacilityTab'] = 'Requested';
          }
          navUrl = 'credit-limit/module-arranging/term-sheet-acceptance';
          break;
        case CreditLimitStateType.FI_ASSESSED:
        case CreditLimitStateType.SIGNATURE_PENDING:
        case 'Renew Signature Pending':
        case 'Limit Update Signature Pending':
        case 'CounterParty Edit Signature Pending':
        case 'Product Update Signature Pending':
        case 'Agreement Change Signature Pending':
        case 'Facility Update Signature Pending':
        case 'Limit Update Financial Institution Assessed':
        case 'Product Update Financial Institution Assessed':
        case 'CounterParty Edit Financial Institution Assessed':
        case 'Agreement Change Terms Accepted':
        case 'Facility Update Financial Institution Assessed':
        case 'Agreement Change Financial Institution Assessed':
          if (!queryParams['creditFacilityTab']) {
            queryParams['creditFacilityTab'] = 'Requested';
          }
          navUrl = 'credit-limit/module-arranging/facility-terms-acceptance';
          break;
        case CreditLimitStateType.DETAILS_REQUIRED:
        case 'Limit Update Details Required':
        case 'Product Update Details Required':
        case 'CounterParty Edit Details Required':
        case 'CounterParty Edit Under Assessment':
        case 'Renew Details Required':
        case 'Sector Update Documents Required':
          if (!queryParams['creditFacilityTab']) {
            queryParams['creditFacilityTab'] = 'Requested';
          }
          navUrl = 'credit-limit/module-arranging/update-facility-by-client';
          break;
        case CreditLimitStateType.SAVE_AS_DRAFT:
        case CreditLimitStateType.ELIGIBILITY_REQUESTED_DRAFT:
          navUrl = 'credit-limit/module-arranging/apply-facility';
          if (!queryParams['status']) {
            queryParams['status'] = 'draft';
          }
          if (creditFacilityStatus === CreditLimitStateType.ELIGIBILITY_REQUESTED_DRAFT) {
            if (!queryParams['actionType']) {
              queryParams['actionType'] = 'CheckEligibility';
            }
          }
          break;
        case CreditLimitStateType.DRAFT:
          if (!queryParams['screenReference']) {
            queryParams['screenReference'] = 'requestCreditFacility';
          }
          navUrl = 'credit-limit/module-arranging/apply-facility';
          break;
        default:
          navUrl = 'credit-limit/module-arranging/view-facility-client';
      }
    }
    localStorage.setItem(environment.storage.FinancialInstitution, cfData.fi_name);
    return {
      url: navUrl,
      queryParams
    }
  }

  handleArrangingFIOrAdminNotificationNav(creditFacilityStatus: string, creditFacilityId: string, user: User, cfData: any, routeQueryParams: Params): { url: string, queryParams: any } {
    const queryParams = routeQueryParams ? Object.assign({}, routeQueryParams) : {};
    if (!queryParams['id']) {
      queryParams['id'] = creditFacilityId;
    }
    let navUrl = 'credit-limit/module-arranging/view-facility-other';
    if (user.organisation_type === 'BANK') {
      switch (creditFacilityStatus) {
        case CreditLimitStateType.ELIGIBILITY_REQUESTED:
        case CreditLimitStateType.UNDER_ASSESSMENT:
          if (!queryParams['creditFacilityTab']) {
            queryParams['creditFacilityTab'] = 'Requested';
          }
          navUrl = 'credit-limit/module-arranging/accept-facility';
          break;
        case 'Limit Update Under Assessment':
        case 'Product Update Under Assessment':
        case 'CounterParty Edit Under Assessment':
        case 'Sector Update Under Assessment':
          if (!queryParams['creditFacilityTab']) {
            queryParams['creditFacilityTab'] = 'Requested';
          }
          navUrl = 'credit-limit/module-arranging/update-facility';
          break;
        default:
          navUrl = 'credit-limit/module-arranging/view-facility-other';
      }
    }
    localStorage.setItem(environment.storage.FinancialInstitution, cfData.fi_name);
    return {
      url: navUrl,
      queryParams
    }
  }

  handleLendingNotificationNav(creditFacilityStatus: string, creditFacilityId: string, user: User, cfData: any, routeQueryParams: Params): { url: string, queryParams: any } {
    const queryParams = routeQueryParams ? Object.assign({}, routeQueryParams) : {};
    if (!queryParams['id']) {
      queryParams['id'] = creditFacilityId;
    }
    let navUrl = 'credit-limit/module-lending/view-facility';
    if (user.organisation_type === 'BANK') {
      let actionType = 'view';
      const makerCheckerStatuses = ['Terms Accepted', 'Completed', 'Rework Pending by Operations'];
      const creditUpdateTermsheetAssessment = ['Limit Update Terms Accepted', 'Product Update Terms Accepted', 'Agreement Change Terms Accepted', 'Limit Update Rework Pending by Operations', 'Product Update Rework Pending by Operations', 'Agreement Change Rework Pending by Operations'];
      // Ask backend to provide 'request_id' in individual credit facility 
      if (cfData.request_id) {
        actionType = 'view';
      } else if (cfData.internal_lending_info.active_user === 'SALES' && cfData.active_workflow === 'CREDIT_TERM_SHEET' && user.user_department === 'SALES') {
        if (creditFacilityStatus === 'Term Sheet Approved, KYC Pending') {
          actionType = 'view';
        } else {
          actionType = 'update';
        }
      } else if ((user.user_department === 'MAKER' || user.user_department === 'CHECKER') && (makerCheckerStatuses.includes(creditFacilityStatus)
        || (cfData.internal_lending_info.active_user === 'OPERATIONS' && cfData.internal_lending_info.active_workflow && creditUpdateTermsheetAssessment.includes(creditFacilityStatus)))) {
        actionType = 'update';
      } else if (["CM", "HOC"].includes(user.user_department) && creditFacilityStatus === "Reviewed By Credit Analyst") {
        actionType = 'update';
      } else if (user.user_department === cfData.internal_lending_info.active_user && cfData.internal_lending_info.active_workflow) {
        actionType = 'update';
      } else {
        actionType = 'view';
        navUrl = 'credit-limit/module-lending/view-facility';
      }
      if (actionType === 'update') {
        navUrl = 'credit-limit/module-lending/accept-facility';
      }
      if (cfData.request_id) { // for which all statuses request_id will not be null
        // queryParams['status'] = event.tableSubType; (check if needed while testing)
        if (!queryParams['requestId']) {
          queryParams['requestId'] = cfData.request_id;
        }
      }

      const counterPartyUpdateStatuses = [
        'CounterParty Edit Details Required',
        'CounterParty Edit Rework Pending by Credit Analyst',
        'CounterParty Edit Under Assessment',
        'CounterParty Edit Approved By Credit Team',
        'CounterParty Edit Financial Institution Assessed'
      ];
      if (creditFacilityStatus.includes('CounterParty Edit Reviewed by') || counterPartyUpdateStatuses.includes(creditFacilityStatus)) {// add all statuses which are covered under Pending Counter party
        if (!queryParams['requestType']) {
          queryParams['requestType'] = 'CounterParty';
        }
        if (!queryParams['activeUser'] && cfData.internal_lending_info.active_workflow) {
          queryParams['activeUser'] = cfData.internal_lending_info.active_user;
        }
      }
    }
    let breadCrumbToSet = cfData.fi_org_name
    if (!breadCrumbToSet) {
      breadCrumbToSet = cfData.applicant_name
    }
    localStorage.setItem(environment.storage.FinancialInstitution, breadCrumbToSet);
    return {
      url: navUrl,
      queryParams
    }
  }

  setActualFeesControls(formGroup: UntypedFormGroup, finance: FullFinance, prodCategorySnakeUC: string, productTypeSnackUC: string): void {
    const actualDiscountFeeAmount = this.isNeitherNullNorUndefined(finance.paymentTermsDetails[0].fees_actual.interest_rate_amount)
      ? finance.paymentTermsDetails[0].fees_actual.interest_rate_amount.toFixed(2)
      : '';
    formGroup.addControl('actualDiscountFeeAmount', new UntypedFormControl({ value: actualDiscountFeeAmount, disabled: true }));
    const actualProcessingFeeAmount = this.isNeitherNullNorUndefined(finance.paymentTermsDetails[0].fees_actual.transaction_fee_amount)
      ? finance.paymentTermsDetails[0].fees_actual.transaction_fee_amount.toFixed(2)
      : '';
    formGroup.addControl('actualProcessingFeeAmount', new UntypedFormControl({ value: actualProcessingFeeAmount, disabled: true }));
    const actualTaxAmount = this.isNeitherNullNorUndefined(finance.paymentTermsDetails[0].fees_actual.tax_amount)
      ? finance.paymentTermsDetails[0].fees_actual.tax_amount.toFixed(2)
      : '';
    formGroup.addControl('actualTaxAmount', new UntypedFormControl({ value: actualTaxAmount, disabled: true }));
    if (this.prodConfig.isfactoringFeeApplicable(prodCategorySnakeUC, productTypeSnackUC)) {
      const actualFactoringFeeAmt = this.isNeitherNullNorUndefined(finance.paymentTermsDetails[0].fees_actual.actualFactoringFeeAmt)
        ? finance.paymentTermsDetails[0].fees_actual.actualFactoringFeeAmt.toFixed(2)
        : '';
      formGroup.addControl('actualFactoringFeeAmt', new UntypedFormControl({ value: actualFactoringFeeAmt, disabled: true }));
      const actualFactoringFeeTaxAmt = this.isNeitherNullNorUndefined(finance.paymentTermsDetails[0].fees_actual.actualFactoringFeeTaxAmt)
        ? finance.paymentTermsDetails[0].fees_actual.actualFactoringFeeTaxAmt.toFixed(2)
        : '';
      formGroup.addControl('actualFactoringFeeTaxAmt', new UntypedFormControl({ value: actualFactoringFeeTaxAmt, disabled: true }));
    }
    const actualOverdueAmount = this.isNeitherNullNorUndefined(finance.paymentTermsDetails[0].fees_actual.overdue_interest_amount)
      ? finance.paymentTermsDetails[0].fees_actual.overdue_interest_amount.toFixed(2)
      : '';
    formGroup.addControl('actualOverdueInterestAmount', new UntypedFormControl({ value: actualOverdueAmount, disabled: true }));
    const actualOtherChargesAmount = this.isNeitherNullNorUndefined(finance.paymentTermsDetails[0].fees_actual.other_charges_amount)
      ? finance.paymentTermsDetails[0].fees_actual.other_charges_amount.toFixed(2)
      : '';
    if (formGroup.controls['otherChargesAmount']) {
      formGroup.get('otherChargesAmount').setValue(actualOtherChargesAmount);
    } else {
      formGroup.addControl('otherChargesAmount', new UntypedFormControl({ value: actualOtherChargesAmount, disabled: true }))
    }
  }

  isNeitherNullNorUndefined(value: any): boolean {
    return value !== null && value !== undefined && value !== '';
  }

  checkLoopParams(paramObj: any, paramName: string, paramMap: any): void {
    const currentLayer = Object.keys(paramMap);
    if (!currentLayer.includes('any')) {
      if (Array.isArray(paramObj)) {
        paramObj.forEach((obj) => {
          Object.keys(obj)?.forEach((param) => {
            if (!currentLayer.includes(param)) {
              delete obj[param];
            } else if (obj[param] && typeof (obj[param]) === 'object') {
              this.checkLoopParams(obj[param], param, paramMap[param]);
            }
          })
        })
      } else {
        Object.keys(paramObj)?.forEach((param) => {
          if (!currentLayer.includes(param)) {
            delete paramObj[param];
          } else if (paramObj[param] && typeof (paramObj[param]) === 'object') {
            this.checkLoopParams(paramObj[param], param, paramMap[param]);
          }
        });
      }
    }
  }

  formatCompRegUpdateAPIPayload(currentParams: any): any {
    const paramObj = JSON.parse(JSON.stringify(currentParams));
    const payloadStructure = this.constants.compRegUpdateAPIFormat;
    const currentLayer = Object.keys(payloadStructure);
    Object.keys(paramObj)?.forEach((param) => {
      if (!currentLayer.includes(param)) {
        delete paramObj[param];
      } else if (paramObj[param] && typeof (paramObj[param]) === 'object') {
        this.checkLoopParams(paramObj[param], param, payloadStructure[param]);
      }
    });
    return paramObj;
  }

  startAutoSaveTimer(callBackFunc: Function): void {
    this.autoSaveIdleTimer = setInterval(() => {
      callBackFunc();
    }, 1000 * 120); // 1000 * 60 = 60 seconds
  }

  clearAutoSaveTimer(): void {
    if (this.autoSaveIdleTimer) {
      clearInterval(this.autoSaveIdleTimer);
      this.autoSaveIdleTimer = null;
    }
  }

  checkDuplicateKYC(type: string, keyToCheckErrorOn: string, parentFormGroup: UntypedFormGroup, subChildrenKeyToCheckError?: string): void {
    const errorCountMap = {};
    parentFormGroup.get(type)['controls']?.forEach((singleControl) => {
      const singleValue = (singleControl?.get(keyToCheckErrorOn)?.value?.key || singleControl?.get(keyToCheckErrorOn)?.value?.trim()) + (subChildrenKeyToCheckError ? singleControl?.get(subChildrenKeyToCheckError)?.value : '');
      if (singleValue) {
        if (singleValue in errorCountMap && errorCountMap[singleValue] >= 1) {
          errorCountMap[singleValue] += 1
          singleControl.get(keyToCheckErrorOn).setErrors({ duplicate: true });
        } else if (singleControl.get(keyToCheckErrorOn)?.errors?.duplicate) {
          singleControl.get(keyToCheckErrorOn).setErrors(null);
        }
        singleControl.get(keyToCheckErrorOn).markAllAsTouched();
        errorCountMap[singleValue] = 1;
      }
    })
  }
  /*
    Description: This method creates an initial formgroup for exception object
    This is used in update page of CF for lending users
  */
  createNewCreditLimitExceptionFormGroup(formBuilder: UntypedFormBuilder, exceptionType: string, isConditionBasedSetupForMaxFinancingTenure: boolean): UntypedFormGroup {
    const formGroup = formBuilder.group({
      selectedProductAndFinanceType: [''],
      maxSanctionedAmountLimit: [''],
      maxFinanceTenure: [''],
      interestRateType: [], // For Interest Rate
      counterParties: [],
      transactionFeeChargeType: [],
      overdueRateType: [],
      marginDependentOnTenure: [''],
      processingFeeDependentOnTenure: [''],
      overdueFeeDependentOnTenure: [''],
      status: ['ACTIVE'],
      isNew: [true],
      boardSanctionedLimit: [''],
    });
    if (isConditionBasedSetupForMaxFinancingTenure) {
      formGroup.addControl('maxFinanceTenureFrom', new UntypedFormControl(''));
      formGroup.addControl('maxFinancingTenureFromOptions', new UntypedFormControl(this.maxFinancingTenureFromOptions));
      formGroup.addControl('daysFromBlDateForDisbursal', new UntypedFormControl(''));
    }
    if (exceptionType === 'counterParty') {
      formGroup.addControl('selectedCounterParty', new UntypedFormControl(''));
    } else {
      formGroup.addControl('creditLimit', new UntypedFormControl(''));
      formGroup.addControl('usableLimit', new UntypedFormControl('', [Validators.min(0), Validators.max(formGroup.get('creditLimit').value)]));
      formGroup.get('usableLimit').updateValueAndValidity();
      formGroup.get('usableLimit').markAllAsTouched();
    }
    return formGroup;
  }

  addComboValuesFormControl(exception: any, formGroup: any, formBuilder: any, rateType: string): void {
    const rateKey = rateType === 'marginDiscountRate' ? 'tenure_margin_rate' : 'tenureDiscountRate';
    const rateValue = exception[rateType] ? exception[rateType] : '';
    const dependentOnTenure = typeof (rateValue) === 'object';

    if (rateValue) {
      if (dependentOnTenure) {
        const marginRateCombo = [];
        rateValue?.forEach((rate) => {
          marginRateCombo.push(formBuilder.group({
            marginDiscountRate: [rate[rateKey] ? rate[rateKey] : ''],
            interestRateFinTenureUpToDays: [rate.days ? rate.days : '']
          }))
        })
        formGroup.addControl('marginRateCombo', formBuilder.array(marginRateCombo));
        formGroup.addControl('marginDiscountRate', new UntypedFormControl(''));
      } else {
        formGroup.addControl('marginRateCombo', formBuilder.array([formBuilder.group({
          marginDiscountRate: [''],
          interestRateFinTenureUpToDays: ['']
        })]));
        formGroup.addControl('marginDiscountRate', new UntypedFormControl(rateValue));
      }
    } else {
      formGroup.addControl('marginRateCombo', formBuilder.array([formBuilder.group({
        marginDiscountRate: [''],
        interestRateFinTenureUpToDays: ['']
      })]));
      formGroup.addControl('marginDiscountRate', new UntypedFormControl(''));
    }
  }

  /*
    Description: This method creates an formgroup with product level and counter party level exceptions using chargesException from api
    This is used in update page of CF for lending users
  */
  createNewCreditLimitExceptionFormArray(formBuilder: UntypedFormBuilder, creditLimitAcceptanceDetails): UntypedFormGroup {
    const chargesException = creditLimitAcceptanceDetails.chargesException;
    if (chargesException) {
      const productLevelChargesException = chargesException.productLevelChargesException;
      const counterPartyFormArray = formBuilder.array([]);
      const productFormArray = formBuilder.array([]);
      const counterPartyProductLevelChargesException = chargesException.counterPartyProductLevelChargesException;
      const isConditionBasedSetupForMaxFinancingTenure = creditLimitAcceptanceDetails?.credit_facility_details?.max_finance_tenure_from_enabled;
      const buyerInformation = creditLimitAcceptanceDetails?.buyer_information;
      if (counterPartyProductLevelChargesException?.length) {
        counterPartyProductLevelChargesException.forEach((exception) => {
          // For DRAFT exceptions all top-level fields are null; real data lives in exception_draft.
          // Spread order: exception first (base), then exception_draft overrides the null fields.
          // This ensures financeType/productType/productCategory from exception_draft win over nulls.
          // exceptionId/status/isExistingException stay correct as they only exist on exception.
          const src = exception.exception_draft
            ? { ...exception, ...exception.exception_draft, exception_draft: exception.exception_draft }
            : exception;
          const processingFeeDependentOnTenure = typeof (src.transactionFee) === 'object';
          const overdueFeeDependentOnTenure = typeof (src.overdueInterestRate) === 'object';
          const marginDependentOnTenure = typeof (src.marginDiscountRate) === 'object';
          const exceptionId = exception?.exceptionId || exception?.exception_id || src?.exceptionId || src?.exception_id;
          const formGroup = formBuilder.group({
            selectedProductAndFinanceType: [src?.productCategory + '_' + src?.productType + '_' + src?.financeType],
            maxSanctionedAmountLimit: [src.maxSanctionedAmountLimit ? src.maxSanctionedAmountLimit : ''],
            maxFinanceTenure: [src.maxFinanceTenure ? src.maxFinanceTenure : ''],
            interestRateType: [src.interestRateType ? src.interestRateType : ''], // For Interest Rate
            selectedCounterParty: [src.counterPartyId],
            transactionFeeChargeType: [src.transactionFeeChargeType ? src.transactionFeeChargeType : ''],
            overdueRateType: [src.overdueRateType ? src.overdueRateType : ''],
            marginDependentOnTenure: [marginDependentOnTenure],
            processingFeeDependentOnTenure: [processingFeeDependentOnTenure],
            overdueFeeDependentOnTenure: [overdueFeeDependentOnTenure],
            counterPartyName: [src.companyName],
            status: [exception.status || src.status || 'DRAFT'],
            isExistingException: [exception.isExistingException !== undefined ? exception.isExistingException : src.isExistingException],
            processingFeeInputType: [src.transactionFeeChargeType === 'VALUE' ? 'float_2' : 'percentage_5'],
            boardSanctionedLimit: [src.boardSanctionedLimit ? src.boardSanctionedLimit : ''],
          });
          // exceptionId always lives on the top-level exception object (not exception_draft)
          if (exceptionId) {
            formGroup.addControl('exceptionId', new UntypedFormControl(exceptionId));
          }
          if (buyerInformation) {
            const product = src?.productCategory + '_' + src?.productType;
            let selectProduct = buyerInformation[product];
            if (selectProduct?.length) {
              selectProduct = selectProduct.filter((prod) => { return prod.finance_type === src.financeType });
              if (selectProduct?.length) {
                const counterParties = [];
                selectProduct.forEach((prod) => {
                  counterParties.push({
                    key: prod.buyer_id,
                    label: prod.company_name
                  })
                });
                formGroup.addControl('counterParties', formBuilder.array(counterParties));
              }
            }
          }
          if (isConditionBasedSetupForMaxFinancingTenure) {
            const maxFinanceTenureFrom = src.maxFinanceTenureFrom ? src.maxFinanceTenureFrom : null;
            formGroup.addControl('maxFinanceTenureFrom', new UntypedFormControl(maxFinanceTenureFrom));
            if (!formGroup.controls['maxFinancingTenureFromOptions']) {
              const selectedProduct = formGroup.get('selectedProductAndFinanceType').value;
              this.addTenureDateOptions(formGroup, selectedProduct);
            }
            formGroup.addControl(
              'daysFromBlDateForDisbursal',
              new UntypedFormControl(src.daysFromBlDateForDisbursal ? src.daysFromBlDateForDisbursal : '')
            );
            if (maxFinanceTenureFrom) {
              formGroup.get('maxFinanceTenure').setValidators([Validators.required]);
              formGroup.get('maxFinanceTenure').markAllAsTouched();
            }
          }
          this.addFeeDetails(src, marginDependentOnTenure, formBuilder, formGroup, 'marginDiscountRate', 'tenure_margin_rate', 'interestRateFinTenureUpToDays', 'marginRateCombo', 'marginDiscountRate');
          this.addFeeDetails(src, processingFeeDependentOnTenure, formBuilder, formGroup, 'transactionFee', 'tenure_processing_fee', 'processingfinTenureUpToDays', 'processingFeeCombo', 'processingFee');
          this.addFeeDetails(src, overdueFeeDependentOnTenure, formBuilder, formGroup, 'overdueInterestRate', 'tenure_overdue_rate', 'overduefinTenureUpToDays', 'overdueFeeCombo', 'overdueInterestRate');
          if (src.factoringFee) {
            const factoringFeeDependentOnTenure = typeof (src.factoringFee) === 'object';
            formGroup.addControl('factoringFeeChargeType', new UntypedFormControl(src.factoringFeeChargeType ? src.factoringFeeChargeType : ''));
            if (factoringFeeDependentOnTenure) {
              const factoringFeeCombo = [];
              src.factoringFee?.forEach((fee) => {
                factoringFeeCombo.push(formBuilder.group({
                  factoringFee: [fee.tenure_processing_fee ? fee.tenure_processing_fee : ''],
                  factoringFeeTenureUpToDays: [fee.days ? fee.days : '']
                }));
              })
              formGroup.addControl('factoringFeeCombo', formBuilder.array(factoringFeeCombo));
              formGroup.addControl('factoringFeeDependentOnTenure', new UntypedFormControl(true));
            } else {
              formGroup.addControl('factoringFee', new UntypedFormControl(src.factoringFee, [Validators.max(100)]));
              formGroup.addControl('factoringFeeDependentOnTenure', new UntypedFormControl(true));
            }
          }

          counterPartyFormArray.push(formGroup);
        })
      }
      if (productLevelChargesException?.length) {
        productLevelChargesException.forEach((exception) => {
          // For DRAFT exceptions all top-level fields are null; real data lives in exception_draft.
          // Spread order: exception first (base), then exception_draft overrides the null fields.
          // This ensures financeType/productType/productCategory from exception_draft win over nulls.
          // exceptionId/status/isExistingException stay correct as they only exist on exception.
          const src = exception.exception_draft
            ? { ...exception, ...exception.exception_draft, exception_draft: exception.exception_draft }
            : exception;
          const processingFeeDependentOnTenure = typeof (src.transactionFee) === 'object';
          const overdueFeeDependentOnTenure = typeof (src.overdueInterestRate) === 'object';
          const marginDependentOnTenure = (src.fixedFloatingRateType === 'FIXED' && (src.discountRate && typeof (src.discountRate) === 'object')) || (src.marginDiscountRate && typeof (src.marginDiscountRate) === 'object'); // we are using same control for margin and fixed interest rate
          const exceptionId = exception?.exceptionId || exception?.exception_id || src?.exceptionId || src?.exception_id;
          const formGroup = formBuilder.group({
            selectedProductAndFinanceType: [src?.productCategory + '_' + src?.productType + '_' + src?.financeType],
            maxSanctionedAmountLimit: [src.maxSanctionedAmountLimit ? src.maxSanctionedAmountLimit : ''],
            maxFinanceTenure: [src.maxFinanceTenure ? src.maxFinanceTenure : ''],
            interestRateType: [src.interestRateType ? src.interestRateType : ''], // For Interest Rate
            creditLimit: [src.creditLimit ? Number(src.creditLimit).toFixed(2) : (src.creditLimitAmount ? Number(src.creditLimitAmount).toFixed(2) : '')],
            usableLimit: [src.usableCreditLimitAmount ? Number(src.usableCreditLimitAmount) : (src.usable_limit ? Number(src.usable_limit) : '')],
            transactionFeeChargeType: [src.transactionFeeChargeType ? src.transactionFeeChargeType : ''],
            overdueRateType: [src.overdueRateType ? src.overdueRateType : ''],
            processingFeeDependentOnTenure: [processingFeeDependentOnTenure],
            overdueFeeDependentOnTenure: [overdueFeeDependentOnTenure],
            marginDependentOnTenure: [marginDependentOnTenure],
            status: [exception.status || src.status || 'DRAFT'],
            isExistingException: [exception.isExistingException !== undefined ? exception.isExistingException : src.isExistingException],
            processingFeeInputType: [src.transactionFeeChargeType === 'VALUE' ? 'float_2' : 'percentage_5'],
            boardSanctionedLimit: [src.boardSanctionedLimit ? src.boardSanctionedLimit : ''],
          });
          // exceptionId always lives on the top-level exception object (not exception_draft)
          if (exceptionId) {
            formGroup.addControl('exceptionId', new UntypedFormControl(exceptionId));
          }
          if (isConditionBasedSetupForMaxFinancingTenure) {
            const maxFinanceTenureFrom = src.maxFinanceTenureFrom ? src.maxFinanceTenureFrom : null;
            formGroup.addControl('maxFinanceTenureFrom', new UntypedFormControl(maxFinanceTenureFrom));
            if (!formGroup.controls['maxFinancingTenureFromOptions']) {
              const selectedProduct = formGroup.get('selectedProductAndFinanceType').value;
              this.addTenureDateOptions(formGroup, selectedProduct);
            }
            formGroup.addControl(
              'daysFromBlDateForDisbursal',
              new UntypedFormControl(src.daysFromBlDateForDisbursal ? src.daysFromBlDateForDisbursal : '')
            );
            if (maxFinanceTenureFrom) {
              formGroup.get('maxFinanceTenure').setValidators([Validators.required]);
              formGroup.get('maxFinanceTenure').markAllAsTouched();
            }
          }

          this.checkCollectionFieldsConfigCondition(src?.productCategory + '_' + src?.productType, formGroup, src);

          this.checkInterestRateConfigCondition(src?.productCategory + '_' + src?.productType, formGroup);
          formGroup.get('fixedFloatingRateType')?.setValue(src.fixedFloatingRateType);

          // for Fixed interest rate we are keeping same control, have changed only label
          if (formGroup.get('fixedFloatingRateType')?.value !== 'FIXED') {
            this.addComboValuesFormControl(src, formGroup, formBuilder, 'marginDiscountRate');
          } else {
            this.addComboValuesFormControl(src, formGroup, formBuilder, 'discountRate');
          }
          this.addFeeDetails(src, processingFeeDependentOnTenure, formBuilder, formGroup, 'transactionFee', 'tenure_processing_fee', 'processingfinTenureUpToDays', 'processingFeeCombo', 'processingFee');
          this.addFeeDetails(src, overdueFeeDependentOnTenure, formBuilder, formGroup, 'overdueInterestRate', 'tenure_overdue_rate', 'overduefinTenureUpToDays', 'overdueFeeCombo', 'overdueInterestRate');
          if (src.factoringFee) {
            const factoringFeeDependentOnTenure = typeof (src.factoringFee) === 'object';
            formGroup.addControl('factoringFeeChargeType', new UntypedFormControl(src.factoringFeeChargeType ? src.factoringFeeChargeType : ''));
            if (factoringFeeDependentOnTenure) {
              const factoringFeeCombo = [];
              src.factoringFee?.forEach((fee) => {
                factoringFeeCombo.push(formBuilder.group({
                  factoringFee: [fee.tenure_processing_fee ? fee.tenure_processing_fee : ''],
                  factoringFeeTenureUpToDays: [fee.days ? fee.days : '']
                }));
              })
              formGroup.addControl('factoringFeeCombo', formBuilder.array(factoringFeeCombo));
              formGroup.addControl('factoringFeeDependentOnTenure', new UntypedFormControl(true));
            } else {
              formGroup.addControl('factoringFee', new UntypedFormControl(src.factoringFee, [Validators.max(100)]));
              formGroup.addControl('factoringFeeDependentOnTenure', new UntypedFormControl(true));
            }
          }

          productFormArray.push(formGroup);
        })
      }
      return formBuilder.group({
        productLevelChargesException: productFormArray,
        counterPartyProductLevelChargesException: counterPartyFormArray
      });
    }
    return formBuilder.group({});
  }


  createComboRateValuesMetaData(productArr: any[], exception: any, rateType: string): void {
    const rateKeyLabel = rateType === 'marginDiscountRate' ? 'Spread over Benchmark' : 'Interest Rate';
    const rateKey = rateType === 'marginDiscountRate' ? 'tenure_margin_rate' : 'tenureDiscountRate';
    const rateValue = exception[rateType];
    const dependentOnTenure = typeof (rateValue) === 'object';
    if (dependentOnTenure) {
      rateValue?.forEach((rate) => {
        const content = [];
        content.push(
          {
            isDisabled: true,
            label: rateKeyLabel,
            value: rate[rateKey] ? rate[rateKey] : '--',
          });
        content.push(
          {
            isDisabled: true,
            label: 'Financing Tenure',
            value: rate.days ? rate.days : '--',
          });
        productArr.push({ content: content });
      })
    } else {
      const content = [
        {
          isDisabled: true,
          label: rateKeyLabel,
          value: rateValue,
        }];
      productArr.push({ content: content });
    }
  }

  /*
   This method used to create readonly data for product level exceptions which will passed as input to input textbox selector component
   Used in both arranding and lending side
 */
  createProductLevelExceptions(creditLimitAcceptanceDetails, isLendingUser: boolean): any[] {
    const productLevelExceptionsArray = [];
    const productLevelChargesException = creditLimitAcceptanceDetails.chargesException?.productLevelChargesException;
    for (let index = 0; index < productLevelChargesException?.length; index++) {
      const exception = productLevelChargesException[index];
      const content: any[] = [
        {
          isDisabled: true,
          label: 'Product(s) + Finance(s)',
          value: this.prodConfig.getProductFinanceDesc(exception?.productCategory + '_' + exception?.productType + '_' + exception?.financeType),
        },
      ]

      if (exception.status === 'ACTIVE' && exception.isExistingException !== undefined && !exception.isExistingException) {
        content.push(
          {
            isDisabled: true,
            value: 'ADDED',
            class: 'add-tag',
            width: 11.5
          }
        )
      }
      if (exception.status === 'DELETED' && exception.isExistingException) {
        content.push(
          {
            isDisabled: true,
            value: 'REMOVED',
            class: 'remove-tag',
            width: 7
          }
        )
      }
      const productLevelExceptions = {
        heading: index === 0 ? 'PRODUCT LEVEL EXCEPTIONS' : '',
        content: content,
      };
      const productLevelExceptionsTerms = {
        content: [
          {
            isDisabled: true,
            label: 'Credit Limit',
            value: exception.creditLimitAmount ? this.getCommifiedValue(exception.creditLimitAmount, 'restrictDecimal') : '--',
          },
          {
            isDisabled: creditLimitAcceptanceDetails['isUsageLimitRestriction'] ? false : true,
            label: 'Usable Limit',
            value: exception.usableCreditLimitAmount ? (creditLimitAcceptanceDetails['isUsageLimitRestriction'] ? exception.usableCreditLimitAmount : this.getCommifiedValue(exception.usableCreditLimitAmount, 'restrictDecimal')) : '--',
            formControlName: 'usableLimit',
            focusout: { methodCall: 'conditionCheck' },
            keyup: { methodCall: 'conditionCheck' },
            fieldType: 'inputType',
            type: 'text',
            min: 0,
            showError: { methodCall: 'conditionCheck' },
            errorMessage: 'Usable limit is invalid.',
            placeholder: "Usable Limit",
            inputCondition: 'restrictDecimal',
            autocomplete: "off",
            formControlIndex: index
          },
          {
            isDisabled: true,
            label: 'Max Financing Tenure',
            value: exception.maxFinanceTenure ? exception.maxFinanceTenure : '--',
          },
          {
            isDisabled: true,
            label: 'Max Financing Tenure Calculation Date',
            value: exception.maxFinanceTenureFrom ? MaxFinancingTenureFromEnum[exception.maxFinanceTenureFrom] : '--',
          },
        ],
      };
      productLevelExceptionsArray.push(productLevelExceptions);
      if (exception.maxFinanceTenureFrom === 'DISBURSAL_DATE') {
        productLevelExceptionsTerms.content.push(
          {
            isDisabled: true,
            label: 'Allowed Days For Disbursal From BL Date',
            value: exception.daysFromBlDateForDisbursal ? exception.daysFromBlDateForDisbursal : '--',
          })
      }
      productLevelExceptionsArray.push(productLevelExceptionsTerms);
      const boardSanctionedLimitAndMaxAdvanceRate = {
        heading: '',
        content: [
          {
            isDisabled: true,
            label: 'Board Approved Limit',
            value: exception.boardSanctionedLimit ? this.getCommifiedValue(exception.boardSanctionedLimit, 'restrictDecimal') : '--'
          }
        ],
      };
      if (isLendingUser) productLevelExceptionsArray.push(boardSanctionedLimitAndMaxAdvanceRate);
      const maxSanctionedAmountLimit = {
        heading: '',
        content: [
          {
            isDisabled: true,
            label: 'Max Advance Rate',
            value: exception.maxSanctionedAmountLimit ? exception.maxSanctionedAmountLimit : '--'
          }
        ],
      };
      productLevelExceptionsArray.push(maxSanctionedAmountLimit);
      const creditLimitStatus = creditLimitAcceptanceDetails.status.current_status;

      if (!['Term Sheet Requested', 'Term Sheet Approved'].includes(creditLimitStatus)) {
        const productType = this.prodConfig.getProductTypeAbbreviationSnakeUC(exception?.productCategory + '_' + exception?.productType);
        const interestRateKeyName = isLendingUser ? 'discountRate' : 'interestRate';
        const interestRateFlexible = this.prodConfig.getGivenKeyValueForCreditFacilityConfig(productType, 'creditLimitTermsConfig', 'interestRateConfigurable') === InterestRateConfigurableType.FLEXIBLE;
        const collectionAtFlexible = this.prodConfig.getGivenKeyValueForCreditFacilityConfig(productType, 'creditLimitTermsConfig', 'feeAndChargesCondition', ['collectedAt']) === CollectedAt.FLEXIBLE ? true : false;
        const collectionFromFlexible = this.prodConfig.getGivenKeyValueForCreditFacilityConfig(productType, 'creditLimitTermsConfig', 'feeAndChargesCondition', ['collectedFrom']) === CollectedAt.FLEXIBLE ? true : false;
        const dependentOnTenure = exception.marginDiscountRate && typeof (exception.marginDiscountRate) === 'object';
        const interestRateDependentOnTenure = exception.fixedFloatingRateType === 'FIXED' && exception[interestRateKeyName] && typeof (exception[interestRateKeyName]) === 'object';
        const interestRateDetails = {
          heading: 'Interest Rate',
          content: [
            {
              isDisabled: true,
              label: 'Interest Rate Type',
              value: this.getInterestRateType(exception.interestRateType),
            },
            {
              isDisabled: true,
              label: 'Dependant on Tenure',
              value: (interestRateDependentOnTenure || dependentOnTenure) ? 'Yes' : 'No',
            }
          ],
        };

        if (interestRateFlexible) {
          interestRateDetails.content.splice(0, 0,
            {
              isDisabled: true,
              label: 'Fixed/Floating interest rate',
              value: exception.fixedFloatingRateType
            }
          );
        }

        productLevelExceptionsArray.push(interestRateDetails);

        if (exception.fixedFloatingRateType !== 'FIXED') {
          this.createComboRateValuesMetaData(productLevelExceptionsArray, exception, 'marginDiscountRate');
        }
        if (interestRateFlexible) {
          this.createComboRateValuesMetaData(productLevelExceptionsArray, exception, interestRateKeyName);
        }

        if (isLendingUser && (collectionAtFlexible || collectionFromFlexible)) this.addCollectionFieldsMetaData(productLevelExceptionsArray, exception.interestCollectionAt, exception.interestCollectionFrom, collectionAtFlexible, collectionFromFlexible);

        const transactionFee = exception?.transactionFee;
        const overdueInterestRate = exception?.overdueInterestRate;
        const processingFeeDependentOnTenure = typeof (transactionFee) === 'object';
        const overdueFeeDependentOnTenure = typeof (overdueInterestRate) === 'object';
        const transactionFeeDetails = {
          heading: 'Transaction Fees',
          content: [
            {
              isDisabled: true,
              label: 'Fee Charge Type',
              value: this.getTransactionFeeType(exception.transactionFeeChargeType),
            },
            {
              isDisabled: true,
              label: 'Dependant on Tenure',
              value: processingFeeDependentOnTenure ? 'Yes' : 'No',
            }
          ],
        };
        productLevelExceptionsArray.push(transactionFeeDetails);
        if (processingFeeDependentOnTenure) {
          transactionFee?.forEach((fee) => {
            const dependentransactionFeeDetails = {
              heading: '',
              content: [
                {
                  isDisabled: true,
                  label: 'Transaction Fee',
                  value: fee.tenure_processing_fee ? fee.tenure_processing_fee : '--',
                },
                {
                  isDisabled: true,
                  label: 'Financing Tenure',
                  value: fee.days ? fee.days : '--',
                }
              ],
            };
            productLevelExceptionsArray.push(dependentransactionFeeDetails);
          })
        } else {
          productLevelExceptionsArray.push({
            heading: '',
            content: [
              {
                isDisabled: true,
                label: 'Transaction Fee',
                value: transactionFee,
              }]
          });
        }
        this.createOverdueRateExp(exception, overdueFeeDependentOnTenure, productLevelExceptionsArray, overdueInterestRate);
        if (isLendingUser && (collectionAtFlexible || collectionFromFlexible)) this.addCollectionFieldsMetaData(productLevelExceptionsArray, exception.transactionFeeCollectionAt, exception.transactionFeeCollectionFrom, collectionAtFlexible, collectionFromFlexible);

        if (exception?.factoringFee) {
          const factoringFee = exception.factoringFee;
          const factoringFeeDependentOnTenure = typeof (factoringFee) === 'object';
          const factoringFeeDetails = {
            heading: 'Factoring Fees',
            content: [
              {
                isDisabled: true,
                label: 'Factoring Fee Charge Type',
                value: this.getTransactionFeeType(exception.factoringFeeChargeType),
              },
              {
                isDisabled: true,
                label: 'Dependant on Tenure',
                value: processingFeeDependentOnTenure ? 'Yes' : 'No',
              }
            ],
          };
          productLevelExceptionsArray.push(factoringFeeDetails);

          if (factoringFeeDependentOnTenure) {
            factoringFee?.forEach((fee) => {
              const dependentransactionFeeDetails = {
                heading: '',
                content: [
                  {
                    isDisabled: true,
                    label: 'Factoring Fee',
                    value: fee.tenure_processing_fee ? fee.tenure_processing_fee : '--',
                  },
                  {
                    isDisabled: true,
                    label: 'Financing Tenure',
                    value: fee.days ? fee.days : '--',
                  }
                ],
              };
              productLevelExceptionsArray.push(dependentransactionFeeDetails);
            });
          } else {
            productLevelExceptionsArray.push({
              heading: '',
              content: [
                {
                  isDisabled: true,
                  label: 'Factoring Fee',
                  value: factoringFee,
                }]
            });
          }
          if (isLendingUser && (collectionAtFlexible || collectionFromFlexible)) this.addCollectionFieldsMetaData(productLevelExceptionsArray, exception.factoringFeeCollectionAt, exception.factoringFeeCollectionFrom, collectionAtFlexible, collectionFromFlexible);
        }
      }
    }
    return productLevelExceptionsArray;
  }

  /*
    This method used to create readonly data for counter party level exceptions which will passed as input to input textbox selector component
    Used in both arranding and lending side
  */
  createCounterPartyLevelExceptions(creditLimitAcceptanceDetails, isLendingUser: boolean): any[] {
    const counterPartyLevelExceptionsArray = []
    const counterPartyLevelChargesException = creditLimitAcceptanceDetails?.chargesException?.counterPartyProductLevelChargesException;
    for (let index = 0; index < counterPartyLevelChargesException?.length; index++) {
      const exception = counterPartyLevelChargesException[index];
      const content: any[] = [
        {
          isDisabled: true,
          label: 'Product(s) + Finance(s)',
          value: this.prodConfig.getProductFinanceDesc(exception?.productCategory + '_' + exception?.productType + '_' + exception?.financeType),
        },
        {
          isDisabled: true,
          label: 'Counter Party',
          value: exception.companyName ? exception.companyName : '--',
        },
      ];
      if (exception.status === 'ACTIVE' && exception.isExistingException !== undefined && !exception.isExistingException) {
        content.push(
          {
            isDisabled: true,
            value: 'ADDED',
            class: 'add-tag',
            width: 11.5
          }
        )
      }
      if (exception.status === 'DELETED' && exception.isExistingException) {
        content.push(
          {
            isDisabled: true,
            value: 'REMOVED',
            class: 'remove-tag',
            width: 7
          }
        )
      }
      content.push(
        {
          isDisabled: true,
          label: 'Max Financing Tenure',
          value: exception.maxFinanceTenure ? exception.maxFinanceTenure : '--',
        },
        {
          isDisabled: true,
          label: 'Max Financing Tenure Calculation Date',
          value: exception.maxFinanceTenureFrom ? MaxFinancingTenureFromEnum[exception.maxFinanceTenureFrom] : '--',
        }
      )
      const counterPartyLevelExceptions = {
        heading: index === 0 ? 'COUNTER PARTY LEVEL EXCEPTIONS' : '',
        content: content
      };
      if (exception.maxFinanceTenureFrom === 'DISBURSAL_DATE') {
        counterPartyLevelExceptions.content.push(
          {
            isDisabled: true,
            label: 'Allowed Days For Disbursal From BL Date',
            value: exception.daysFromBlDateForDisbursal ? exception.daysFromBlDateForDisbursal : '--',
          })
      }
      counterPartyLevelExceptionsArray.push(counterPartyLevelExceptions);
      const boardSanctionedLimitAndMaxAdvanceRate = {
        heading: '',
        content: [
          {
            isDisabled: true,
            label: 'Board Approved Limit',
            value: exception.boardSanctionedLimit ? this.getCommifiedValue(exception.boardSanctionedLimit, 'restrictDecimal') : '--',
          }
        ],
      };
      if (isLendingUser) counterPartyLevelExceptionsArray.push(boardSanctionedLimitAndMaxAdvanceRate);
      const maxSanctionedAmountLimit = {
        heading: '',
        content: [
          {
            isDisabled: true,
            label: 'Max Advance Rate',
            value: exception.maxSanctionedAmountLimit ? exception.maxSanctionedAmountLimit : '--'
          }
        ],
      };
      counterPartyLevelExceptionsArray.push(maxSanctionedAmountLimit);
      const creditLimitStatus = creditLimitAcceptanceDetails.status.current_status;
      if (!['Term Sheet Requested', 'Term Sheet Approved'].includes(creditLimitStatus)) {
        const marginDiscountRate = exception?.marginDiscountRate;
        const dependentOnTenure = typeof (marginDiscountRate) === 'object';

        const interestRateDetails = {
          heading: 'Interest Rate',
          content: [
            {
              isDisabled: true,
              label: 'Interest Rate Type',
              value: this.getInterestRateType(exception.interestRateType),
            },
            {
              isDisabled: true,
              label: 'Dependant on Tenure',
              value: dependentOnTenure ? 'Yes' : 'No',
            }
          ],
        };
        if (dependentOnTenure) {
          marginDiscountRate?.forEach((margin) => {
            interestRateDetails.content.push(
              {
                isDisabled: true,
                label: 'Spread over Benchmark',
                value: margin.tenure_margin_rate ? margin.tenure_margin_rate : '--',
              })
            interestRateDetails.content.push(
              {
                isDisabled: true,
                label: 'Financing Tenure',
                value: margin.days ? margin.days : '--',
              })
          })
        } else {
          interestRateDetails.content.push(
            {
              isDisabled: true,
              label: 'Spread over Benchmark',
              value: marginDiscountRate,
            })
        }
        counterPartyLevelExceptionsArray.push(interestRateDetails);
        const transactionFee = exception?.transactionFee;
        const overdueInterestRate = exception.overdueInterestRate;
        const processingFeeDependentOnTenure = typeof (transactionFee) === 'object';
        const overdueFeeDependentOnTenure = typeof (overdueInterestRate) === 'object';
        const transactionFeeDetails = {
          heading: 'Transaction Fees',
          content: [
            {
              isDisabled: true,
              label: 'Fee Charge Type',
              value: this.getTransactionFeeType(exception.transactionFeeChargeType),
            },
            {
              isDisabled: true,
              label: 'Dependant on Tenure',
              value: processingFeeDependentOnTenure ? 'Yes' : 'No',
            }
          ],
        };
        counterPartyLevelExceptionsArray.push(transactionFeeDetails);
        if (processingFeeDependentOnTenure) {
          transactionFee?.forEach((fee) => {
            const dependentransactionFeeDetails = {
              heading: '',
              content: [
                {
                  isDisabled: true,
                  label: 'Transaction Fee',
                  value: fee.tenure_processing_fee ? fee.tenure_processing_fee : '--',
                },
                {
                  isDisabled: true,
                  label: 'Financing Tenure',
                  value: fee.days ? fee.days : '--',
                }
              ],
            };
            counterPartyLevelExceptionsArray.push(dependentransactionFeeDetails);
          })
        } else {
          counterPartyLevelExceptionsArray.push({
            heading: '',
            content: [
              {
                isDisabled: true,
                label: 'Transaction Fee',
                value: transactionFee,
              }]
          });
        }

        this.createOverdueRateExp(exception, overdueFeeDependentOnTenure, counterPartyLevelExceptionsArray, overdueInterestRate);


        if (exception?.factoringFee) {
          const factoringFee = exception.factoringFee;
          const factoringFeeDependentOnTenure = typeof (factoringFee) === 'object';
          const factoringFeeDetails = {
            heading: 'Factoring Fees',
            content: [
              {
                isDisabled: true,
                label: 'Factoring Fee Charge Type',
                value: this.getTransactionFeeType(exception.factoringFeeChargeType),
              },
              {
                isDisabled: true,
                label: 'Dependant on Tenure',
                value: processingFeeDependentOnTenure ? 'Yes' : 'No',
              }
            ],
          };
          counterPartyLevelExceptionsArray.push(factoringFeeDetails);

          if (factoringFeeDependentOnTenure) {
            factoringFee?.forEach((fee) => {
              const dependentransactionFeeDetails = {
                heading: '',
                content: [
                  {
                    isDisabled: true,
                    label: 'Factoring Fee',
                    value: fee.tenure_processing_fee ? fee.tenure_processing_fee : '--',
                  },
                  {
                    isDisabled: true,
                    label: 'Financing Tenure',
                    value: fee.days ? fee.days : '--',
                  }
                ],
              };
              counterPartyLevelExceptionsArray.push(dependentransactionFeeDetails);
            });
          } else {
            counterPartyLevelExceptionsArray.push({
              heading: '',
              content: [
                {
                  isDisabled: true,
                  label: 'Factoring Fee',
                  value: factoringFee,
                }]
            });
          }
        }
      }

    }
    return counterPartyLevelExceptionsArray
  }

  getInterestRateType(value): string {
    if (value) {
      return value === 'PER_MONTH' ? 'Per Month' : value === 'PER_ANNUM' ? 'Per Annum' : '--';
    }
    return '--';
  }

  getTransactionFeeType(value): string {
    if (value) {
      return value === 'PERCENT' ? 'Percentage (%)' : value === 'VALUE' ? 'Value' : '--';
    }
    return '--';
  }

  addTenureDateOptions(formGroup: UntypedFormGroup, selectedProduct: string): void {
    formGroup.addControl('maxFinancingTenureFromOptions', new UntypedFormControl(this.getMaxFinancingTenureOptions(selectedProduct)));
  }

  getMaxFinancingTenureOptions(selectedFinance: string): any[] {
    return selectedFinance?.includes('TRADE_FINANCE')
      ? this.maxFinancingTenureFromOptions
      : [
        {
          key: 'DISBURSAL_DATE',
          label: MaxFinancingTenureFromEnum.DISBURSAL_DATE
        },
        {
          key: 'INVOICE_DATE',
          label: MaxFinancingTenureFromEnum.INVOICE_DATE
        }
      ];
    //return dateOptions;
  }

  validateAndShowUpdateBtnOnApplications(user: User, finance: FullFinance): boolean {
    const operationRoles = ['MAKER', 'CHECKER'];
    if (user?.is_lending_organisation && finance.active_user) {
      if (finance.active_user === 'OPERATIONS') {
        if (!operationRoles.includes(user.user_department)) {
          return false;
        }
        if (["FINANCE_OVERDUE_WAIVEOFF", "FINANCE_SETTLEMENT"].includes(finance.active_workflow) && user?.user_department === 'MAKER') {
          return true;
        } else {
          return false;
        }
      } else {
        return user.user_department === finance.active_user;
      }
    }
    if (user?.user_department === "CA" && (finance?.state === FinanceStates.WAIVER_REQUEST || finance?.state === FinanceStates.DUE_DATE_UPDATE_REQUESTED)) {
      return true;
    }
    if (this.validateActiveUserForState(finance.state, finance.active_user, user.user_department)) {
      return true;
    }
    if (user.is_lending_organisation && !finance.active_user && !operationRoles.includes(user.user_department)) { return false }
    const prodCategorySnakeUC = this.prodConfig.getCategoryAbbreviationSnakeUC(finance.product_category);
    const productTypeSnakeUC = this.prodConfig.getProductTypeAbbreviationSnakeUC(finance.product_type);
    if (finance.state === FinanceStates.BANK_FUNDS_TRANSFERRED
      || finance.state === FinanceStates.BCO_TERMS_AGREED
      || finance.state === FinanceStates.DTF_FINANCE_ACCEPTED
      || finance.state === FinanceStates.APPROVED_BY_MAKER
      || finance.state === FinanceStates.REWORK_PENDING_BY_MAKER
      || finance.state === FinanceStates.OVERDUE_WAIVE_OFF_REQUESTED
      || finance.state === FinanceStates.PARTIALLY_SETTLED
      || finance.state === FinanceStates.WAIVER_REQUEST_REJECTED
      || finance.state === FinanceStates.CONVERSION_REQUEST_PENDING_MAKER_REVIEW
      || finance.state === FinanceStates.CONVERSION_REQUEST_PENDING_CHECKER_REVIEW
      || finance.state === FinanceStates.CONVERSION_REQUEST_PENDING_MAKER_RESUBMISSION
      || (finance.state === FinanceStates.ACCOUNT_SETTLED && this.prodConfig.isDisbursementToApplicantAndCollectedFromCp(prodCategorySnakeUC, productTypeSnakeUC))) {
      return true;
    }
    return false;
  }

  validateIndiaClient(user: User): boolean {
    return user && ((user.organisation_type === 'BANK' && environment.serviceProviderCode === user.tenant_code)
      || user.organisation_type === 'ORG' || user.organisation_type === 'DTF' || user.is_lending_organisation);
  }

  compareUsableAndCreditLimit(formGroup: UntypedFormGroup, creditLimitFormControlName: string, usableLimitFormControlName: string): void {
    if (formGroup?.get(usableLimitFormControlName)?.value) {
      let creditLimit: any = formGroup.get(creditLimitFormControlName).value;
      if (typeof (creditLimit) === 'string' && creditLimit.includes(',')) {
        creditLimit = creditLimit.replace(/,+/g, '');
      }
      let usableLimitValue = formGroup.get(usableLimitFormControlName).value;
      if (typeof (usableLimitValue) === 'string' && usableLimitValue?.includes(',')) {
        usableLimitValue = usableLimitValue.replace(/,+/g, '');
      }
      if (!formGroup.get(usableLimitFormControlName).errors || formGroup.get(usableLimitFormControlName).errors?.max) {
        if (Number(usableLimitValue) > Number(creditLimit)) {
          formGroup.get(usableLimitFormControlName).setErrors({
            max: {
              actual: usableLimitValue,
              max: creditLimit
            }
          });
        } else {
          formGroup.get(usableLimitFormControlName).setErrors(null);
        }
      }
    }
  }

  findAndOutputInvalidControls(form: FormGroup, fieldNameToDisplayName: any): boolean {
    let invalid = false;
    const controls = form.controls;
    let errorString = 'Please enter a value for : ';
    for (const name in controls) {
      if (controls[name].invalid) {
        invalid = true;
        // console.log('name: ', name);
        // console.log('fieldNameToDisplayName: ', fieldNameToDisplayName[name]);
        errorString += fieldNameToDisplayName[name] + ', ';
      }
    }
    if (errorString.endsWith(', ')) {
      errorString = errorString.slice(0, -2);
      errorString += '.';
    }
    errorString = errorString.replace(': ,', ':');
    this.ui.warningDialog(errorString);
    return invalid;
  }

  convertSnakeCaseToTitleCase(str: string): string {
    if (!str) return '';
    return str.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ');
  }

  mouseEnter(trigger): void {
    if (this.timedOutCloser) {
      clearTimeout(this.timedOutCloser);
    }
    if (this.prevMouseTrigger && trigger != this.prevMouseTrigger) {
      this.prevMouseTrigger.closeMenu();
    }
    trigger.openMenu();
    this.prevMouseTrigger = trigger;
  }

  mouseLeave(trigger): void {
    this.timedOutCloser = setTimeout(() => {
      trigger.closeMenu();
    }, 500);
  }

  getMinValueObject(array: any[], key: string): any | null {
    return array.reduce((minValueObject: any, current: any) => {
      const currentValue = current[key];
      const numericValue = Number(this.removeComma(currentValue));
      if (!isNaN(numericValue) && currentValue !== '' && currentValue !== null) {
        if (minValueObject === null || numericValue < Number(this.removeComma(minValueObject[key]))) {
          return current;
        }
      }
      return minValueObject;
    }, null);
  }

  createLimitEntry(formGroup: UntypedFormGroup | null, formField: string, siblingFormGroup: UntypedFormGroup | null, isParent: boolean, siblingFormField: string | null): any {
    return {
      id: this.removeComma(formGroup?.get(formField)?.value),
      formGroup,
      formField,
      siblingFormGroup,
      isParent,
      siblingFormField
    };
  }

  creditLimitMap(cpFormGroup: UntypedFormGroup, cpExcFormGroup: UntypedFormGroup | null, productLevelExcFormGroup: UntypedFormGroup | null, creditLimitAcceptanceForm: UntypedFormGroup | null): any {
    const limitMap = [
      this.createLimitEntry(cpExcFormGroup, 'boardSanctionedLimit', cpExcFormGroup, false, 'boardSanctionedLimit'),
      this.createLimitEntry(productLevelExcFormGroup, 'creditLimit', cpFormGroup, true, null),
      this.createLimitEntry(productLevelExcFormGroup, 'boardSanctionedLimit', cpFormGroup, true, null),
      this.createLimitEntry(creditLimitAcceptanceForm, this.masterCreditLimitControl, cpFormGroup, false, null),
    ];
    return this.getMinValueObject(limitMap, 'id');
  }

  counterPartyValidator(creditLimitAcceptanceForm: UntypedFormGroup, cpFormGroup: UntypedFormGroup, type: string): void {
    if (!creditLimitAcceptanceForm) return;
    const productType = cpFormGroup.get('product_type').value;
    const productLevelExcFormGroup = this.getExceptionsForm(productType, creditLimitAcceptanceForm, 'productLevelChargesException');
    const cpExcFormGroup = this.getExceptionsForm(productType, creditLimitAcceptanceForm, 'counterPartyProductLevelChargesException');
    const limitControlName = cpFormGroup.get('upgradeLimit') ? 'upgradeLimit' : 'limit';
    if (type === 'limit') {
      const minObjectVal = this.creditLimitMap(cpFormGroup, cpExcFormGroup, productLevelExcFormGroup, creditLimitAcceptanceForm);
      const parentMaxLimit = minObjectVal?.formGroup?.get(minObjectVal.formField).value;
      if (this.isNeitherNullNorUndefined(parentMaxLimit)) {
        this.setLimitValidator(cpFormGroup, parentMaxLimit, true, minObjectVal.isParent, minObjectVal.siblingFormField, minObjectVal.siblingFormGroup);
      }
    }
    const productUsableLimit = productLevelExcFormGroup?.get('usableLimit').value;
    const masterUsableLimit = creditLimitAcceptanceForm.get('usableLimit')?.value;
    if (this.isNeitherNullNorUndefined(productUsableLimit)) {
      this.addCustomValidator(cpFormGroup, productUsableLimit, 'usableLimit', true, true, limitControlName);
    } else if (this.isNeitherNullNorUndefined(masterUsableLimit)) {
      this.addCustomValidator(cpFormGroup, masterUsableLimit, 'usableLimit', true, false, limitControlName);
    }
  }

  creditLimitTermsValidator(counterPartyInfoProductForm: UntypedFormGroup, creditLimitAcceptanceForm: UntypedFormGroup, creditLimitAcceptanceDetails: any): void {
    if (!creditLimitAcceptanceForm) return;
    this.masterCreditLimit = creditLimitAcceptanceForm.get('creditLimit').value;
    this.masterCreditLimitControl = 'creditLimit';
    const temporaryEnhancementExpDate = creditLimitAcceptanceDetails['credit_limit_terms']['approved_terms']['temporaryEnhancementExpDate'];
    if (temporaryEnhancementExpDate && !this.hasExpDatePassed(temporaryEnhancementExpDate)) {
      if (Number(this.removeComma(this.masterCreditLimit)) > Number(this.removeComma(creditLimitAcceptanceForm.get('temporaryEnhancementLimit').value))) {
        this.masterCreditLimit = Number(this.removeComma(this.masterCreditLimit));
        this.masterCreditLimitControl = 'creditLimit';
      } else {
        this.masterCreditLimit = Number(this.removeComma(creditLimitAcceptanceForm.get('temporaryEnhancementLimit').value));
        this.masterCreditLimitControl = 'temporaryEnhancementLimit';
      }
    }
    const masterUsableLimit = creditLimitAcceptanceForm.get('usableLimit')?.value;
    const masterBoardSanctionedLimit = creditLimitAcceptanceForm.get('boardSanctionedCreditLimit')?.value;
    // if master board approved limit is present it should be greater than credit limit
    if (this.isNeitherNullNorUndefined(masterBoardSanctionedLimit)) {
      this.addCustomValidator(creditLimitAcceptanceForm, masterBoardSanctionedLimit, 'creditLimit', true, false, 'boardSanctionedCreditLimit');
    }
    // if master board approved limit is not present, credit limit should have no set max limit
    else {
      creditLimitAcceptanceForm.get('creditLimit').setValidators([Validators.required]);
      creditLimitAcceptanceForm.get('creditLimit').updateValueAndValidity();
      creditLimitAcceptanceForm.get('creditLimit').markAllAsTouched();
    }
    // master usable limit should be less than master credit limit
    this.addCustomValidator(creditLimitAcceptanceForm, this.masterCreditLimit, 'usableLimit', true, false, this.masterCreditLimitControl);
    // setting limits for all product level exceptions if they exist based on master level limits
    if (creditLimitAcceptanceForm.get('chargesException')?.get('productLevelChargesException').value.length) {
      const productExpControls = (creditLimitAcceptanceForm.get('chargesException').get('productLevelChargesException') as UntypedFormArray).controls;
      productExpControls.forEach((productLevelChargesException) => {
        //if master board sanctioned limit exists then add limit for product board sanctioned limit
        if (this.isNeitherNullNorUndefined(masterBoardSanctionedLimit)) {
          this.addCustomValidator(productLevelChargesException, masterBoardSanctionedLimit, 'boardSanctionedLimit', false, false);
        }
        // adding validator for product credit limit should be less than master credit limit & also less than product board limit if it exists
        if (productLevelChargesException.get('boardSanctionedLimit').value) {
          this.addCustomValidator(productLevelChargesException, this.masterCreditLimit, 'creditLimit', false, false, 'boardSanctionedLimit');
        }
        // adding validator for product credit limit should be less than master credit limit
        else {
          this.addCustomValidator(productLevelChargesException, this.masterCreditLimit, 'creditLimit', false, false);
        }
        // adding validator for product usable limit should be less than master usable limit & also less than product credit limit if it exists
        if (productLevelChargesException.get('creditLimit').value) {
          this.addCustomValidator(productLevelChargesException, masterUsableLimit, 'usableLimit', false, false, 'creditLimit');
        }
        else if (productLevelChargesException.get('boardSanctionedLimit').value) {
          this.addCustomValidator(productLevelChargesException, masterUsableLimit, 'usableLimit', false, false, 'boardSanctionedLimit');
        }
        // adding validator for product usable limit should be less than master usable limit
        else {
          this.addCustomValidator(productLevelChargesException, masterUsableLimit, 'usableLimit', false, false);
        }
      });
    }
    // setting limits for all counter party level exceptions if they exist based on master & product level limits
    if (creditLimitAcceptanceForm.get('chargesException')?.get('counterPartyProductLevelChargesException').value.length) {
      (creditLimitAcceptanceForm.get('chargesException').get('counterPartyProductLevelChargesException') as UntypedFormArray).controls.forEach((counterPartyProductLevelChargesException) => {
        //finding out exceptions at counter party level which has same product selected as product level 
        const productLevelExcFormGroup = this.getExceptionsForm(counterPartyProductLevelChargesException.get('selectedProductAndFinanceType').value, creditLimitAcceptanceForm, 'productLevelChargesException');
        // if board sanctioned limit exists at product level then add limit for counter party board sanctioned limit
        const productBAL = productLevelExcFormGroup?.get('boardSanctionedLimit').value;
        if (this.isNeitherNullNorUndefined(productBAL)) {
          counterPartyProductLevelChargesException.get('boardSanctionedLimit').setValidators([Validators.min(0), this.numericLimitValidator(Number(this.removeComma(productBAL)), 'parentMax')]);
        }
        // if board sanctioned limit exists at master level then add limit for counter party board sanctioned limit
        else if (this.isNeitherNullNorUndefined(masterBoardSanctionedLimit)) {
          counterPartyProductLevelChargesException.get('boardSanctionedLimit').setValidators([Validators.min(0), this.numericLimitValidator(Number(this.removeComma(masterBoardSanctionedLimit)), 'customMax')]);
        }
        counterPartyProductLevelChargesException.get('boardSanctionedLimit').updateValueAndValidity();
        counterPartyProductLevelChargesException.get('boardSanctionedLimit').markAllAsTouched();
      });
    }
    if (!counterPartyInfoProductForm) return;
    // setting limits for counter parties based on master & product exception limit values
    Object.keys(counterPartyInfoProductForm.controls).forEach((productTypeControlName) => {
      const cpFormControls = (counterPartyInfoProductForm.get(productTypeControlName) as UntypedFormArray)?.controls;
      if (cpFormControls.length) {
        // checking if there are any product level exceptions for counterparties of a particular product
        const productLevelExcFormGroup = this.getExceptionsForm(productTypeControlName, creditLimitAcceptanceForm, 'productLevelChargesException');
        cpFormControls.forEach((cpFormGroup: UntypedFormGroup) => {
          //if product credit limit exists, then counter party upgrade limit should be less than it
          const productCreditLimit = productLevelExcFormGroup?.get('creditLimit').value;
          const productBAL = productLevelExcFormGroup?.get('boardSanctionedLimit').value;
          const limitMap = [
            { parentMaxLimit: productCreditLimit, isRequired: true, isParent: true },
            { parentMaxLimit: productBAL, isRequired: true, isParent: true },
            { parentMaxLimit: this.masterCreditLimit, isRequired: true, isParent: false }
          ];
          const minObjectVal = this.getMinValueObject(limitMap, 'parentMaxLimit');
          if (this.isNeitherNullNorUndefined(minObjectVal?.parentMaxLimit)) {
            this.setLimitValidator(cpFormGroup, minObjectVal.parentMaxLimit, minObjectVal.isRequired, minObjectVal.isParent, null, null);
          }
          //if product usable limit exists, then counter party usable limit should be less than it
          const productUsableLimit = productLevelExcFormGroup?.get('usableLimit').value;
          if (this.isNeitherNullNorUndefined(productUsableLimit)) {
            this.addCustomValidator(cpFormGroup, productUsableLimit, 'usableLimit', true, true);
          }
          //otheriwse counter party usable limit should be less than master usable limit
          else {
            this.addCustomValidator(cpFormGroup, masterUsableLimit, 'usableLimit', true, false);
          }
        });
      }
    });

    // setting limits for counter parties based on master & counter party exception limit values
    Object.keys(counterPartyInfoProductForm.controls).forEach((productTypeControlName) => {
      const cpFormControls = (counterPartyInfoProductForm.get(productTypeControlName) as UntypedFormArray)?.controls;
      if (cpFormControls.length) {
        // checking if there are any counter party level exceptions for counterparties of a particular product
        const cpExcFormGroup = this.getExceptionsForm(productTypeControlName, creditLimitAcceptanceForm, 'counterPartyProductLevelChargesException');
        cpFormControls.forEach(cpFormGroup => {
          const cpBAL = cpExcFormGroup?.get('boardSanctionedLimit').value;
          if (this.isNeitherNullNorUndefined(cpBAL)) {
            //if board sanctioned limit exists at counter party level , then upgrade limit of selected product's counter party should be less than it 
            this.setLimitValidator(cpFormGroup, cpBAL, true, false, 'boardSanctionedLimit', cpExcFormGroup);
          }
        });
      }
    });
  }

  numericLimitValidator(limit: number, customError): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      let sanitizedValue;
      let numericValue = value;
      if (typeof value === 'string') {
        sanitizedValue = value.replace(/,/g, '');
        numericValue = Number(sanitizedValue);
      }
      if (!isNaN(numericValue)) {
        if (numericValue > limit && customError === 'customMax') {
          return { customMax: { requiredLimit: limit, actualValue: numericValue } };
        } else if (numericValue > limit && customError === 'parentMax') {
          return { parentMax: { requiredLimit: limit, actualValue: numericValue } };
        } else if (numericValue > limit && customError === 'siblingMax') {
          return { siblingMax: { requiredLimit: limit, actualValue: numericValue } };
        }
      }
      return null;
    };
  }

  getExceptionsForm(productTypeControlName: string, creditLimitAcceptanceForm: UntypedFormGroup, expType: string): any {
    const productLevelDetails = (creditLimitAcceptanceForm.get('chargesException')?.get(expType) as UntypedFormArray)?.controls;
    return productLevelDetails?.find(control =>
      control.get('selectedProductAndFinanceType')?.value === productTypeControlName
      && control.get('isDraft')?.value !== true
      && !['DELETED', 'INACTIVE'].includes(String(control.get('status')?.value || '').trim().toUpperCase())
    ) || null;
  }

  removeComma(fieldName): any {
    if (typeof (fieldName) === 'string' && fieldName.includes(',')) {
      fieldName = fieldName.replace(/,+/g, '');
    }
    return fieldName;
  }

  addCustomValidator(
    childFormGroup: any,
    parentLimit: number,
    childFormControlName: string,
    isRequired: boolean,
    isParent: boolean,
    siblingFormControlName?: string,
    siblingFormGroup?: any
  ): void {
    if (childFormGroup?.get(childFormControlName)) {
      const baseValidators = [Validators.min(0)];
      if (isRequired) {
        baseValidators.push(Validators.required);
      }
      if (siblingFormControlName) {
        if (siblingFormGroup) {
          baseValidators.push(this.numericLimitValidator(Number(this.removeComma(siblingFormGroup.get(siblingFormControlName)?.value)), 'siblingMax'));
        } else {
          baseValidators.push(this.numericLimitValidator(Number(this.removeComma(childFormGroup.get(siblingFormControlName)?.value)), 'siblingMax'));
        }
      }
      if (isParent) {
        baseValidators.push(this.numericLimitValidator(Number(this.removeComma(parentLimit)), 'parentMax'));
      } else {
        baseValidators.push(this.numericLimitValidator(Number(this.removeComma(parentLimit)), 'customMax'));
      }
      childFormGroup.get(childFormControlName).setValidators(baseValidators);
      childFormGroup.get(childFormControlName).updateValueAndValidity();
      childFormGroup.get(childFormControlName).markAllAsTouched();
    }
  }

  extractBankReferenceNumber(bankReferenceNumber: string, bankRefClientNameMap: any): string {
    const foundEntry = Object.entries(bankRefClientNameMap).find(
      ([key, value]) => bankReferenceNumber === (value as string).trim()
    );
    return foundEntry ? foundEntry[0] : bankReferenceNumber;
  }

  setLimitValidator(childFormGroup: any, parentLimit: number, isRequired: boolean, isParent: boolean, siblingFormControlName?: string, siblingFormGroup?: any) {
    this.addCustomValidator(childFormGroup, parentLimit, 'upgradeLimit', isRequired, isParent, siblingFormControlName, siblingFormGroup);
    this.addCustomValidator(childFormGroup, parentLimit, 'limit', isRequired, isParent, siblingFormControlName, siblingFormGroup);
  }

  checkAndAddAdvanceLevelField(key: string, form: any, productSnakeUC: string, storedValues?: any): void {
    const value = storedValues?.[key] !== undefined ? storedValues[key] : '';
    if (form.get(key)) {
      if (value) form.get(key).setValue(value);
      if (key === 'riskParticipationType') this.updateRiskParticipationValidators(form);
      return;
    }
    if (this.prodConfig.getGivenKeyValueForCreditFacilityConfig(productSnakeUC, 'creditLimitTermsConfig', 'advanceFacilityConditions', [key])) {
      form.addControl(key, new UntypedFormControl(value, Validators.required));
    }
  }

  checkAndAddAdvanceLevelControls(form: any, productSnakeUC: string, storedValues?: any): any {
    const advanceLevelFields = ['riskParticipationType', 'riskParticipationValue', 'riskParticipant', 'riskOriginator', 'participationType']
    advanceLevelFields.forEach(controlName => {
      this.checkAndAddAdvanceLevelField(controlName, form, productSnakeUC, storedValues);
    });
  }

  removeAdvanceLevelControls(form: any): any {
    form.removeControl('riskParticipationType');
    form.removeControl('riskParticipationValue');
    form.removeControl('riskParticipant');
    form.removeControl('riskOriginator');
    form.removeControl('participationType');
  }

  updateRiskParticipationValidators(form: any): void {
    const val = form.get('riskParticipationType').value;
    const validators = [Validators.required]
    if (val === 'VALUE') {
      validators.push(Validators.pattern(this.constants.inputPattern));
      validators.push(Validators.max(1000000000));
    } else validators.push(Validators.max(100));

    form.get('riskParticipationValue').setValidators(validators);
    form.get('riskParticipationValue').updateValueAndValidity();
  }

  handleAdvanceFacilityFieldsEmitEvent(event: any, parentFormGroup: any, authService: AuthService) {
    switch (event.inputField.formControlName) {

      case 'riskParticipationValue': {
        switch (event.actionType) {
          case 'isActive': {
            return authService.latestValueSubject.next(parentFormGroup.get(event.inputField.formControlName) ? true : false);
          }
          case 'value': {
            return authService.latestValueSubject.next(parentFormGroup.get(event.inputField.formControlName)?.value);
          }
          case 'type': {
            return authService.latestValueSubject.next(parentFormGroup.get('riskParticipationType').value === 'VALUE' ? 'number' : 'text');
          }
          case 'keyup': {
            return this.setFieldValue(event, parentFormGroup);
          }
          case 'max': {
            return authService.latestValueSubject.next(parentFormGroup.get('riskParticipationType')?.value === 'VALUE' ? 1000000000 : 0);
          }
          case 'focusout': {
            return parentFormGroup.get(event.inputField.formControlName).markAsTouched();
          }
          case 'inputType': {
            return authService.latestValueSubject.next(parentFormGroup.get('riskParticipationType').value === 'PERCENT' ? 'percentage_2' : 'number_9');
          }
          case 'showError': {
            if (this.checkFieldError(event, parentFormGroup, authService)) {
              const errorMessages: { [key: string]: string } = {
                max: 'Value cannot be greater than max allowed limit.',
                required: 'DPWFS share of risk participation is required.',
                pattern: 'Only Numerical values allowed.',
              };
              const errors = parentFormGroup.get(event.inputField.formControlName)?.errors;
              if (errors) {
                event.inputField['errorMessage'] = errorMessages[Object.keys(errors)[0]];
              }
            }
            return;
          }
        }
        break;
      }

      case 'riskParticipationType':
      case 'participationType':
      case 'riskParticipant':
      case 'riskOriginator': {
        switch (event.actionType) {
          case 'isActive': {
            return authService.latestValueSubject.next(parentFormGroup.get(event.inputField.formControlName) ? true : false);
          }
          case 'value': {
            return authService.latestValueSubject.next(parentFormGroup.get(event.inputField.formControlName)?.value);
          }
          case 'selectionChange': {
            parentFormGroup.get('riskParticipationValue').setValue('');
            this.setFieldValue(event, parentFormGroup);
            return this.updateRiskParticipationValidators(parentFormGroup);
          }
          case 'change': {
            return this.setFieldValue(event, parentFormGroup);
          }
          case 'showError': {
            return this.checkFieldError(event, parentFormGroup, authService);
          }
          case 'focusout': {
            parentFormGroup.get(event.inputField.formControlName).markAsTouched();
            break;
          }
        }
        break;
      }

    }
  }

  getMetaData(isDisabled?: boolean): any[] {
    const MetaDataArr = [];
    this.constants.advanceFacilityFields.forEach(metaDataKey => {
      const metaData = JSON.parse(JSON.stringify(this.constants[metaDataKey]));
      if (isDisabled) {
        metaData['isDisabled'] = true;
      } else {
        metaData['width'] = 23;
      }
      MetaDataArr.push(metaData);
    });
    return MetaDataArr;
  }

  checkInterestRateConfigCondition(selectedProductAndFinanceType: string, formGroup: any): void {
    const productType = this.prodConfig.getProductTypeAbbreviationSnakeUC(selectedProductAndFinanceType);
    if (this.prodConfig.getGivenKeyValueForCreditFacilityConfig(productType, 'creditLimitTermsConfig', 'interestRateConfigurable') === InterestRateConfigurableType.FLEXIBLE) {
      if (!formGroup.get('fixedFloatingRateType')) formGroup.addControl('fixedFloatingRateType', new UntypedFormControl(''));
    } else {
      formGroup.removeControl('fixedFloatingRateType');
    }
  }

  addCollectionFieldsMetaData(productLevelExceptionsArray: any[], collectionAtValue: any, collectionFromValue: any, collectionAtFlexible: boolean, collectionFromFlexible: boolean): void {
    const colletionFieldsKeyToLabelMapping = { UPFRONT: 'Upfront', ON_SETTLEMENT: 'On Settlement', APPLICANT: 'Applicant', SPONSOR: 'Sponsor' };
    const content = [];
    if (collectionAtFlexible) {
      content.push({
        label: 'Collection At',
        isDisabled: true,
        value: collectionAtValue ? colletionFieldsKeyToLabelMapping[collectionAtValue] : '--',
      });
    }
    if (collectionFromFlexible) {
      content.push({
        label: 'Collection From',
        isDisabled: true,
        value: collectionFromValue ? colletionFieldsKeyToLabelMapping[collectionFromValue] : '--',
      });
    }
    productLevelExceptionsArray.push({ content: content });
  }

  checkCollectionFieldsConfigCondition(productSnakeUC: string, formGroup: any, storedValues?: any): void {
    const productType = this.prodConfig.getProductTypeAbbreviationSnakeUC(productSnakeUC);
    if (this.prodConfig.getGivenKeyValueForCreditFacilityConfig(productType, 'creditLimitTermsConfig', 'feeAndChargesCondition', ['collectedAt']) === CollectedAt.FLEXIBLE) {
      this.addCollectionAtFieldsFormControl(formGroup, storedValues, false, productSnakeUC);
    } else {
      this.removeCollectionAtFieldsFormControl(formGroup)
    }

    if (this.prodConfig.getGivenKeyValueForCreditFacilityConfig(productType, 'creditLimitTermsConfig', 'feeAndChargesCondition', ['collectedFrom']) === CollectedAt.FLEXIBLE) {
      this.addCollectionFromFieldsFormControl(formGroup, storedValues, false, productSnakeUC);
    } else {
      this.removeCollectionFromFieldsFormControl(formGroup)
    }

  }

  removeCollectionAtFieldsFormControl(formGroup: any): void {
    formGroup.removeControl('factoringFeeCollectionAt');
    formGroup.removeControl('transactionFeeCollectionAt');
    formGroup.removeControl('interestCollectionAt');
  }

  removeCollectionFromFieldsFormControl(formGroup: any): void {
    formGroup.removeControl('factoringFeeCollectionFrom');
    formGroup.removeControl('transactionFeeCollectionFrom');
    formGroup.removeControl('interestCollectionFrom');
  }


  addCollectionFromFieldsFormControl(formGroup: any, storedValues: any, addValidator: boolean, productSnakeUC: string, addFactoringFee = true): void {
    const validators = addValidator ? [Validators.required] : [];
    if (!formGroup.get('interestCollectionFrom')) {
      formGroup.addControl('interestCollectionFrom', new UntypedFormControl(storedValues?.interestCollectionFrom ? storedValues.interestCollectionFrom : '', validators));

      formGroup.addControl('transactionFeeCollectionFrom', new UntypedFormControl(storedValues?.transactionFeeCollectionFrom ? storedValues.transactionFeeCollectionFrom : '', validators));

      if (addFactoringFee && this.prodConfig.getGivenKeyValueForCreditFacilityConfig(productSnakeUC, 'creditLimitTermsConfig', 'factoringFee')) {
        formGroup.addControl('factoringFeeCollectionFrom', new UntypedFormControl(storedValues?.factoringFeeCollectionFrom ? storedValues.factoringFeeCollectionFrom : '', validators));
      }
    }
  }

  addCollectionAtFieldsFormControl(formGroup: any, storedValues: any, addValidator: boolean, productSnakeUC: string, addFactoringFee = true, user?: User): void {
    // Change request 7 , needs confirmation
    // let feeValidators = addValidator ? [Validators.required] : [];
    // if (['MAKER', 'CHECKER', 'LEGAL'].includes(user?.user_department)) {
    //   feeValidators = addValidator ? [Validators.required] : [];
    // } else {
    //   feeValidators = [];
    // }
    const validators = addValidator ? [Validators.required] : [];
    if (!formGroup.get('interestCollectionAt')) {
      formGroup.addControl('interestCollectionAt', new UntypedFormControl(storedValues?.interestCollectionAt ? storedValues.interestCollectionAt : '', validators));

      formGroup.addControl('transactionFeeCollectionAt', new UntypedFormControl(storedValues?.transactionFeeCollectionAt ? storedValues.transactionFeeCollectionAt : '', validators));

      if (addFactoringFee && this.prodConfig.getGivenKeyValueForCreditFacilityConfig(productSnakeUC, 'creditLimitTermsConfig', 'factoringFee')) {
        formGroup.addControl('factoringFeeCollectionAt', new UntypedFormControl(storedValues?.factoringFeeCollectionAt ? storedValues.factoringFeeCollectionAt : '', validators));
      }
    }
  }

  sortByName(arr: Array<any>): Array<any> {
    return arr.sort((a, b) => {
      if (a.company_name.toLowerCase() < b.company_name.toLowerCase()) {
        return -1;
      }
      if (a.company_name.toLowerCase() > b.company_name.toLowerCase()) {
        return 1;
      }
      return 0;
    });
  }

  areArraysEqual(arr1: string[], arr2: string[]): boolean {
    if (arr1?.length !== arr2?.length) {
      return false;
    }
    for (let i = 0; i < arr1?.length; i++) {
      if (arr1[i] !== arr2[i]) {
        return false;
      }
    }
    return true;
  }

  moveObjectsToFront(arr: Array<any>): Array<any> {
    const frontObjects = [];
    const otherObjects = [];
    for (const obj of arr) {
      if (!this.areArraysEqual(obj.masterApprovedCommodities, obj.requestedCommodities) || !this.areArraysEqual(obj.masterApprovedOtherCommodities, obj.requestedOtherCommodities)) {
        frontObjects.push(obj);
      } else {
        otherObjects.push(obj);
      }
    }
    return [...frontObjects, ...otherObjects];
  }

  sortCp(value: any): any {
    const input = value;
    for (const key in input) {
      let sortedByNameArr = [];
      sortedByNameArr = this.sortByName(input[key]);
      let editedCps = [];
      editedCps = this.moveObjectsToFront(sortedByNameArr);
      input[key] = editedCps;
    }
    return input;
  }

  addFeeDetails(exception: any, dependentOnTenure: boolean, formBuilder: any, formGroup: any, exceptionName: string, feeType: string, daysControlName: string, comboName: string, feeControlName: string) {
    if (exception[exceptionName]) {
      if (dependentOnTenure) {
        const temp = exception[exceptionName];
        const combo = [];
        temp?.forEach((fee) => {
          combo.push(formBuilder.group({
            [feeControlName]: [fee[feeType] ? fee[feeType] : ''],
            [daysControlName]: [fee.days ? fee.days : '']
          }))
        })
        formGroup.addControl([comboName], formBuilder.array(combo));
        formGroup.addControl([feeControlName], new UntypedFormControl());
      } else {
        formGroup.addControl([comboName], formBuilder.array([formBuilder.group({
          [feeControlName]: [''],
          [daysControlName]: ['']
        })]));
        formGroup.addControl([feeControlName], new UntypedFormControl(exception[exceptionName] ? exception[exceptionName] : ''));
      }
    } else {
      formGroup.addControl([comboName], formBuilder.array([formBuilder.group({
        [feeControlName]: [''],
        [daysControlName]: ['']
      })]));
      formGroup.addControl([feeControlName], new UntypedFormControl(''));
    }
  }

  createOverdueRateExp(exception: any, overdueFeeDependentOnTenure: boolean, exceptionsArray: Array<any>, overdueInterestRate: any): void {
    const overdueRateDetails = {
      heading: 'Overdue Rate',
      content: [
        {
          isDisabled: true,
          label: 'Rate Type',
          value: exception.overdueRateType ? exception.overdueRateType : '--',
        },
        {
          isDisabled: true,
          label: 'Dependant on Delayed Tenure',
          value: overdueFeeDependentOnTenure ? 'Yes' : 'No',
        }
      ],
    };
    exceptionsArray.push(overdueRateDetails);
    if (overdueFeeDependentOnTenure) {
      overdueInterestRate?.forEach((fee) => {
        const dependentransactionFeeDetails = {
          heading: '',
          content: [
            {
              isDisabled: true,
              label: 'Overdue Interest',
              value: fee.tenure_overdue_rate ? fee.tenure_overdue_rate : '--',
            },
            {
              isDisabled: true,
              label: 'Financing Tenure',
              value: fee.days ? fee.days : '--',
            }
          ],
        };
        exceptionsArray.push(dependentransactionFeeDetails);
      })
    } else {
      exceptionsArray.push({
        heading: '',
        content: [
          {
            isDisabled: true,
            label: 'Overdue Interest',
            value: overdueInterestRate,
          }]
      });
    }
  }

  getCustomInterestRateDetailsPayload(form: UntypedFormGroup, individualWorkFlow?: boolean): any {
    const details = [];
    const disbursalDate = form.get('actualPaymentDate')?.value ? form.get('actualPaymentDate').value : form.get('actualDisbursalDate').value;
    form.get('customInterestRate')['controls'].forEach(control => {
      const startDate = control.get('startDate')?.value;
      const endDate = control.get('endDate')?.value;
      let numberOfDays = 0;
      if (new Date(endDate).getTime() < new Date(disbursalDate).getTime()) {
        numberOfDays = 0;
      } else if (this.isInBetween(disbursalDate, startDate, endDate)) {
        numberOfDays = getDateDiff(endDate, disbursalDate);
      } else if (new Date(startDate).getTime() > new Date(disbursalDate).getTime()) {
        numberOfDays = getDateDiff(endDate, startDate) + 1;
      }
      const detailsToBePushed = {
        benchmark_rate: control.get('benchmarkRate')?.value,
        margin: control.get('margin')?.value,
        start_date: startDate ? setUTCHours(startDate, 'start') : '',
        end_date: endDate ? setUTCHours(endDate, 'start') : '',
        number_of_days: numberOfDays,
      }
      if (individualWorkFlow) detailsToBePushed['editable'] = control.get('locked')?.value === false;
      details.push(detailsToBePushed);
    });
    return details;
  }

  isInBetween(date: any, startDate: any, endDate: any): boolean {
    const time = new Date(date).getTime();
    return time >= new Date(startDate).getTime() && time <= new Date(endDate).getTime();
  }

  getCreditMemoDetailsPayload(form: UntypedFormGroup): any {
    const details = [];
    form.get('creditNoteItems')['controls'].forEach(control => {
      if (!control.get('locked').value) {
        details.push({
          oracleArId: control.get('oracleArId')?.value,
          amountToBeWaived: control.get('amountToBeWaived')?.value,
          comment: control.get('comment')?.value,
        });
      }
    });
    return details;
  }

  setMaxFinTenureDate(finance: any, form: FormGroup): Date {
    let maxFinanceTenureDate: any = new Date();
    if (finance?.creditLimitDetails) {
      const creditLimitDetails = finance.creditLimitDetails[0];
      maxFinanceTenureDate = creditLimitDetails.max_financing_tenure + 1; // considering first date as exclusive
      if (creditLimitDetails.max_finance_tenure_from === 'BL_DATE') {
        const blDate = form.get("blDate")?.value;
        if (blDate) {
          maxFinanceTenureDate = new Date(new Date(blDate).setDate(maxFinanceTenureDate + new Date(blDate).getDate()))
        }
      } else if (creditLimitDetails.max_finance_tenure_from === 'INVOICE_DATE') {
        let formArray = form.get('invoiceDetails')?.value;
        if (!formArray) formArray = finance.invoicesDetails;
        const invoiceDate = formArray?.map((res) =>
          Date.parse(res.invoiceDate ? res.invoiceDate : res.invoice_date)
        );
        maxFinanceTenureDate = new Date(new Date(invoiceDate[0]).setDate(maxFinanceTenureDate + new Date(invoiceDate[0]).getDate()))
      } else if (creditLimitDetails.max_finance_tenure_from === 'DISBURSAL_DATE') {
        const disbursalDate = form.get('transactionEventDate')?.value ? form.get('transactionEventDate').value : (finance.paymentTermsDetails ? finance.paymentTermsDetails[0].actual_payment_date : '');
        if (disbursalDate) {
          maxFinanceTenureDate = new Date(new Date(disbursalDate).setDate(maxFinanceTenureDate + new Date(disbursalDate).getDate()))
        }
      }
    }
    return maxFinanceTenureDate;
  }

  removeInvalidAssessmentPeriodId(objects: any): any {
    return objects.map((obj) => {
      const normalizedPeriod = { ...obj };
      if (normalizedPeriod.startDate) {
        normalizedPeriod.startDate = setUTCHours(normalizedPeriod.startDate, 'start');
      }
      if (normalizedPeriod.endDate) {
        normalizedPeriod.endDate = setUTCHours(normalizedPeriod.endDate, 'start');
      }
      if (normalizedPeriod.start_date) {
        normalizedPeriod.start_date = setUTCHours(normalizedPeriod.start_date, 'start');
      }
      if (normalizedPeriod.end_date) {
        normalizedPeriod.end_date = setUTCHours(normalizedPeriod.end_date, 'start');
      }
      if (obj.assessment_period_id === null || obj.assessment_period_id === '') {
        const { assessment_period_id, ...rest } = normalizedPeriod;
        return rest;
      }
      return normalizedPeriod;
    });
  };

  disableCommodities(label: string, commoditiesValue: any, addClass: boolean, className: any, isUpdateCFFlow: boolean): any {
    return {
      isDisabled: true,
      label: label,
      value: commoditiesValue,
      class: !isUpdateCFFlow ? className : null,
      isArrOfObjs: addClass
    };
  }

  checkPeriodIsValid(form: UntypedFormGroup, endDateControlName, savedData: any): any {
    let prevEndDate;
    for (let i = 0; i < form.get('customInterestRate')['controls'].length; i++) {
      const control = form.get('customInterestRate')['controls'][i];
      if (prevEndDate) {
        const prevEndDatePlusOneDay = new Date(prevEndDate.setDate(prevEndDate.getDate() + 1));
        const compareDate = getDateDiff(prevEndDatePlusOneDay, control.get('startDate').value);
        if (compareDate) return { invalidPeriod: true };

      }
      prevEndDate = new Date(control.get('endDate').value);
    }
    const compareDate = getDateDiff(prevEndDate, form.get(endDateControlName)?.value);
    if (compareDate) return { invalidPeriod: true };

    let firstUnlockedPeriodSavedData;
    for (let i = 0; i < form.get('customInterestRate')['controls'].length; i++) {
      const control = form.get('customInterestRate')['controls'][i];
      if (!control.get('locked').value) {
        if (!firstUnlockedPeriodSavedData) firstUnlockedPeriodSavedData = savedData[i];
        const endDateValue = new Date(control.get('endDate').value);
        const compareDate = getDateDiff(endDateValue, new Date());
        if (compareDate < 0 && ((parseFloat(firstUnlockedPeriodSavedData?.benchmark_rate) !== parseFloat(control.get('benchmarkRate').value)) || (parseFloat(firstUnlockedPeriodSavedData?.margin) !== parseFloat(control.get('margin').value)))) return { lockedPeriod: true };
      }
    }
    return {};
  }

  getMonthDifference(start: Date, end: Date): number {
    if (!(start instanceof Date) || !(end instanceof Date)) {
      return 0;
    }

    // Calculate difference in milliseconds
    const timeDiff = end.getTime() - start.getTime();

    // Convert to days
    const daysDiff = timeDiff / (1000 * 60 * 60 * 24);

    // Divide by 30.5 and round to nearest value
    const months = Math.round(daysDiff / 30.5);

    return months;
  }

  hasExpDatePassed(dateString: string): boolean {
    const targetDate = new Date(dateString);
    targetDate.setUTCHours(0, 0, 0, 0);

    const today = new Date();
    today.setUTCHours(0, 0, 0, 0);

    return today > targetDate;
  }

  getPreShipmentCarriedFeeValue(preshipmentDetails: any): number {
    preshipmentDetails.carriedTransactionFee = preshipmentDetails.carriedTransactionFee ? parseFloat(preshipmentDetails.carriedTransactionFee) : 0;
    preshipmentDetails.carriedOverdueFee = preshipmentDetails.carriedOverdueFee ? parseFloat(preshipmentDetails.carriedOverdueFee) : 0;
    return (preshipmentDetails.carriedTransactionFee + preshipmentDetails.carriedOverdueFee).toFixed(2);
  }

  isString(value: any): boolean {
    return typeof value === 'string';
  }

  // ─── Checklist field scroll / highlight helpers ───────────────────────────

  readonly FIELD_HIGHLIGHT_DURATION_MS = 1500;

  /**
   * Scrolls the field identified by `formControlName` into view and briefly highlights it.
   *
   * Strategy 1 — metadata-driven fields (rendered via input-textbox-selector / add-input-textbox):
   *   Finds the field object in `originalMetaData` and sets `_highlight = true` for
   *   FIELD_HIGHLIGHT_DURATION_MS. The template's [ngClass] picks this up and plays the animation.
   *
   * Strategy 2 — directly-rendered reactive form controls:
   *   Falls back to document.querySelector('[formcontrolname]') for scroll + CSS class toggle.
   *
   * Call this after the target tab/sub-tab is already active (post-render tick).
   */
  triggerScrollToField(formControlName: string, originalMetaData: any[]): void {
    let foundInMeta = false;

    if (originalMetaData) {
      for (const section of originalMetaData) {
        const field = this.findFieldInSection(section, formControlName);
        if (field) {
          field._highlight = true;
          foundInMeta = true;
          setTimeout(() => { field._highlight = false; }, this.FIELD_HIGHLIGHT_DURATION_MS);
          break;
        }
      }
    }

    if (!foundInMeta) {
      const el = document.querySelector(`[formcontrolname="${formControlName}"]`) as HTMLElement;
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('field-highlight-direct');
        setTimeout(() => { el.classList.remove('field-highlight-direct'); }, this.FIELD_HIGHLIGHT_DURATION_MS);
      }
    }
  }

  /** Recursively finds a field object by `formControlName` within a metadata section tree. */
  findFieldInSection(section: any, formControlName: string): any {
    if (!section) return null;
    if (section.formControlName === formControlName) return section;
    if (section.content && Array.isArray(section.content)) {
      for (const item of section.content) {
        const found = this.findFieldInSection(item, formControlName);
        if (found) return found;
      }
    }
    return null;
  }

  onKeyFilterValues(event: any, inputConditionDetails: any): void {
    const value = event.value.toLowerCase();
    const inputField = event.inputField;
    if (!inputField['filteredOptions']?.variableName || !inputField['dropDownValues']?.variableName) return;
    let filteredOptions = [];
    if (value === '') {
      filteredOptions = inputConditionDetails[inputField['dropDownValues'].variableName];
    } else {
      inputConditionDetails[inputField['dropDownValues'].variableName]?.forEach(option => {
        if (option.value.startsWith(value) || option.value?.toLowerCase()?.startsWith(value)) {
          filteredOptions.push(option);
        }
      });
    }
    if (!filteredOptions) filteredOptions = [];
    inputConditionDetails[inputField['filteredOptions'].variableName] = [...filteredOptions];
  }
  validateFiles(files: FileList): string | null {
    const maxLengthVoilation: string[] = [];
    const invalidCharacterFiles: string[] = [];

    Array.from(files).forEach((file: File) => {
      if (file.name.length > 100) {
        maxLengthVoilation.push(file.name);
      }
      const lastIndexOfExtensionSeparator = file.name?.lastIndexOf('.');
      if (lastIndexOfExtensionSeparator !== -1) {
        const onlyName = file.name.substring(0, lastIndexOfExtensionSeparator);
        if (this.constants.fileNameReg.test(onlyName)) {
          invalidCharacterFiles.push(onlyName);
        }
      }
    });

    if (maxLengthVoilation.length > 0) {
      return `Uploaded file name should not be more than 100 character`;
    }

    if (invalidCharacterFiles.length > 0) {
      return 'Invalid Characters in file name, following charaters not allowed: ! @ # $ ^ & ( ) \\ - ` + , . / " ';
    }

    return null;
  }

  setOrClearControlError(control: AbstractControl | null | undefined, errorKey: string, hasError: boolean): void {
    if (!control) return;
    const currentErrors = { ...(control.errors || {}) };
    if (hasError) {
      currentErrors[errorKey] = true;
      control.setErrors(currentErrors);
    } else if (currentErrors[errorKey]) {
      delete currentErrors[errorKey];
      control.setErrors(Object.keys(currentErrors).length ? currentErrors : null);
    }
  }

  /**
   * Returns completion state for every visible section. A section is complete
   * only when every visible field that maps to a form control is valid.
   *
   * `fieldAliases` keeps UI/config names separate from form-control names.
   * Fields which are only layout/config containers are ignored.
   */
  getVisibleSectionCompletion(
    form: UntypedFormGroup,
    sectionData: Record<string, any>,
    fieldAliases: Record<string, string> = {}
  ): Record<string, boolean> {
    if (!form || !sectionData) return {};

    return Object.keys(sectionData).reduce((completion, sectionKey) => {
      const section = sectionData[sectionKey];
      if (!section?.isVisible) {
        completion[sectionKey] = false;
        return completion;
      }

      const controls = this.getVisibleSectionControls(form, section.fieldData, fieldAliases);
      completion[sectionKey] = controls.length > 0 && controls.every(control => this.isControlComplete(control));
      return completion;
    }, {} as Record<string, boolean>);
  }

  private isControlComplete(control: AbstractControl): boolean {
    if (control.disabled) {
      if (control instanceof UntypedFormGroup || control instanceof UntypedFormArray) {
        const children = Object.values(control.controls);
        return children.length > 0 && children.every(child => this.isControlComplete(child));
      }
      const value = control.value;
      return value !== null && value !== undefined && value !== '';
    }
    return control.valid;
  }

  private getVisibleSectionControls(
    form: UntypedFormGroup,
    fieldData: Record<string, any>,
    fieldAliases: Record<string, string>
  ): AbstractControl[] {
    if (!fieldData) return [];

    const controls: AbstractControl[] = [];
    Object.keys(fieldData).forEach(fieldKey => {
      const field = fieldData[fieldKey];
      if (!field?.isVisible) return;

      const control = form.get(fieldAliases[fieldKey] || fieldKey);
      if (control) controls.push(control);
      if (field.fieldData) {
        controls.push(...this.getVisibleSectionControls(form, field.fieldData, fieldAliases));
      }
    });
    return Array.from(new Set(controls));
  }
}
